import { __decorate } from 'tslib';
import * as i0 from '@angular/core';
import { Injectable, InjectionToken, Inject, NgModule, Directive, HostBinding, Input, ContentChildren, HostListener, Pipe, EventEmitter, Output, ErrorHandler, PLATFORM_ID, LOCALE_ID, APP_INITIALIZER, inject } from '@angular/core';
import { EntityStore, StoreConfig, QueryEntity, guid, QueryConfig, Order, Store, Query, Paginator } from '@datorama/akita';
import * as i1 from '@angular/common/http';
import { HttpHeaders, HttpEventType, HttpResponse, HTTP_INTERCEPTORS, HttpParams } from '@angular/common/http';
import { Subject, firstValueFrom, of, ReplaySubject, Observable, from, throwError, BehaviorSubject, lastValueFrom, iif, concat, combineLatest, merge, forkJoin } from 'rxjs';
import { tap, take, map, debounceTime, catchError, distinctUntilChanged, switchMap, skip, first, concatMap, connect, shareReplay, filter, takeUntil, mergeMap, finalize, toArray, share } from 'rxjs/operators';
import { saveAs } from 'file-saver';
import * as i2 from '@angular/common';
import { CommonModule, isPlatformBrowser, DOCUMENT, formatDate } from '@angular/common';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import * as i1$1 from '@ngneat/transloco';
import { TranslocoModule, TRANSLOCO_SCOPE } from '@ngneat/transloco';
import { FocusKeyManager } from '@angular/cdk/a11y';
import * as i2$1 from '@angular/router';
import { NavigationEnd } from '@angular/router';
import * as i1$2 from '@angular/cdk/layout';
import { isEqual, groupBy, isEqualWith } from 'lodash';
import * as Sentry from '@sentry/browser';
import * as localForage from 'localforage';
import * as i2$2 from '@ngneat/transloco-locale';
import { TranslocoLocaleModule } from '@ngneat/transloco-locale';
import { AkitaFiltersPlugin } from 'akita-filters-plugin';
import { NgEntityService } from '@datorama/akita-ng-entity-service';
import { GroupByPipe } from 'angular-pipes';
import * as i1$3 from '@angular/platform-browser';
import { getExtension } from 'mime/lite';

let ArchiveDocumentStore = class ArchiveDocumentStore extends EntityStore {
    constructor() {
        super();
    }
};
ArchiveDocumentStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArchiveDocumentStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ArchiveDocumentStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArchiveDocumentStore, providedIn: 'root' });
ArchiveDocumentStore = __decorate([
    StoreConfig({ name: 'archive-documents', idKey: 'sDocumentID' }) // TODO ist sDocumentID the correct idKey?
], ArchiveDocumentStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArchiveDocumentStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class ArchiveDocumentQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
    selectByTicket(ticketId) {
        return this.selectAll({
            filterBy: (entity) => entity.lngReferenceID === ticketId && entity.sProgram === 'Call',
        });
    }
}
ArchiveDocumentQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArchiveDocumentQuery, deps: [{ token: ArchiveDocumentStore }], target: i0.ɵɵFactoryTarget.Injectable });
ArchiveDocumentQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArchiveDocumentQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArchiveDocumentQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: ArchiveDocumentStore }]; } });

function createArchiveDocument({ id = guid(), lngReferenceID = 0, sName = '', lngSize = 0, dtEntryDate = new Date(), sExtensionType = '', sProgram = '', }) {
    return {
        id,
        lngReferenceID,
        sName,
        lngSize,
        dtEntryDate,
        sExtensionType,
        sProgram,
    };
}

const ARCHIVE_DOCUMENT_CONFIG = new InjectionToken('ArchiveDocumentConfig');

class ArchiveDocumentService {
    constructor(store, archiveDocumentQuery, http, config) {
        this.store = store;
        this.archiveDocumentQuery = archiveDocumentQuery;
        this.http = http;
        this.config = config;
    }
    /**
     * Gets all files which belong to a given process.
     */
    get(sProgram, lngReferenceID) {
        const encodedProgram = encodeURIComponent(sProgram);
        return this.http.get(`${this.config.apiUrl}/archive-documents?sProgram=${encodedProgram}&lngReferenceID=${lngReferenceID}`).pipe(tap((entities) => {
            this.store.set(entities);
        }));
    }
    /**
     * Opens a file save dialog, when a file is downloaded
     */
    downloadFile(file) {
        const acceptHeaders = new HttpHeaders();
        acceptHeaders.append('Accept', '*/*');
        acceptHeaders.append('Accept-Encoding', 'gzip, deflate, br');
        acceptHeaders.append('Connection', 'keep-alive');
        const encodedProgram = encodeURIComponent(file.sProgram);
        const encodedDocumentID = encodeURIComponent(file.sDocumentID);
        return this.http
            .get(`${this.config.apiUrl}/archive-documents?sProgram=${encodedProgram}&lngReferenceID=${file.lngReferenceID}&sDocumentID=${encodedDocumentID}`, {
            headers: acceptHeaders,
            responseType: 'blob',
            reportProgress: true,
            observe: 'response',
            withCredentials: true,
        })
            .pipe(take(1))
            .subscribe((res) => {
            this.saveFileAs(res.body, file.sName, file.sExtensionType);
        });
    }
    /**
     * Uploads a given file to the file server.
     */
    uploadFile(formData) {
        const progress = new Subject();
        this.http
            .post(`${this.config.apiUrl}/archive-documents`, formData, {
            observe: 'events',
            reportProgress: true,
        })
            .subscribe((event) => {
            if (event.type === HttpEventType.UploadProgress) {
                // calculate the progress percentage
                const percentDone = Math.round((100 * event.loaded) / event.total);
                // pass the percentage into the progress-stream
                progress.next(percentDone);
            }
            else if (event instanceof HttpResponse) {
                // Close the progress-stream if we get an answer form the API
                // The upload is complete
                const newFile = createArchiveDocument(event.body);
                this.store.add(newFile);
                progress.complete();
            }
        }, (entity) => {
            this.store.add(entity);
        });
        return progress.asObservable();
    }
    saveFileAs(fileBlob, fileName, fileExtension) {
        const fileType = 'application/' + fileExtension;
        const file = new Blob([fileBlob], { type: fileType });
        saveAs(file, fileName);
    }
}
ArchiveDocumentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArchiveDocumentService, deps: [{ token: ArchiveDocumentStore }, { token: ArchiveDocumentQuery }, { token: i1.HttpClient }, { token: ARCHIVE_DOCUMENT_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
ArchiveDocumentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArchiveDocumentService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArchiveDocumentService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: ArchiveDocumentStore }, { type: ArchiveDocumentQuery }, { type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [ARCHIVE_DOCUMENT_CONFIG]
                }] }]; } });

class ArchiveDocumentModule {
    static forRoot(config) {
        return {
            ngModule: ArchiveDocumentModule,
            providers: [
                {
                    provide: ARCHIVE_DOCUMENT_CONFIG,
                    useValue: config,
                },
            ],
        };
    }
}
ArchiveDocumentModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArchiveDocumentModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ArchiveDocumentModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: ArchiveDocumentModule, imports: [CommonModule] });
ArchiveDocumentModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArchiveDocumentModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArchiveDocumentModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }] });

const AUTH_CONFIG = new InjectionToken('AuthConfig');

class HttpEndpointsConstants {
}
// Auth
HttpEndpointsConstants.login = 'auth/login';
HttpEndpointsConstants.refresh = 'auth/refresh-token';
HttpEndpointsConstants.logout = 'auth/logout';
HttpEndpointsConstants.register = 'register';
HttpEndpointsConstants.requestPassword = '/auth/request-pass';
HttpEndpointsConstants.resetPassword = '/auth/reset-pass';
HttpEndpointsConstants.changePassword = '/auth/new-pass';

const defaultCoreConfig = {
    baseUrl: 'http://localhost:4200',
    branchKey: '',
    webshopId: 1,
    mobileBreakpoint: '600px',
    tabletBreakpoint: '1024px',
    useConsent: false,
};
const CORE_CONFIG = new InjectionToken('CoreConfig');

/**
 * An interceptor which adds webshopId and branchKey in header of all requests.
 */
class BaseInterceptor {
    constructor(coreConfig, translocoService) {
        this.coreConfig = coreConfig;
        this.translocoService = translocoService;
    }
    /**
     * Interceptor to add webshopId, branchKey and language in header (all requests)
     */
    intercept(request, next) {
        request = request.clone({
            headers: request.headers
                .set('webshopId', this.coreConfig.webshopId.toString())
                .set('businessUnit', this.coreConfig.branchKey)
                .set('language', this.translocoService.getActiveLang()),
        });
        return next.handle(request);
    }
}
BaseInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BaseInterceptor, deps: [{ token: CORE_CONFIG }, { token: i1$1.TranslocoService }], target: i0.ɵɵFactoryTarget.Injectable });
BaseInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BaseInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BaseInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [CORE_CONFIG]
                }] }, { type: i1$1.TranslocoService }]; } });

class CoreModule {
    static forRoot(config) {
        return {
            ngModule: CoreModule,
            providers: [
                {
                    provide: CORE_CONFIG,
                    useValue: { ...defaultCoreConfig, ...config },
                },
            ],
        };
    }
}
CoreModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CoreModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CoreModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: CoreModule, imports: [CommonModule] });
CoreModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CoreModule, providers: [{ provide: HTTP_INTERCEPTORS, useClass: BaseInterceptor, multi: true }], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CoreModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    providers: [{ provide: HTTP_INTERCEPTORS, useClass: BaseInterceptor, multi: true }],
                }]
        }] });

class FocusableDirective {
    constructor(element) {
        this.element = element;
        this.tabindex = -1;
        this.role = 'list-item';
        this.disabled = false;
    }
    getLabel() {
        return this.element.nativeElement.innerText;
    }
    focus() {
        this.element.nativeElement.focus();
    }
}
FocusableDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FocusableDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
FocusableDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.12", type: FocusableDirective, selector: "[focusable]", host: { properties: { "tabindex": "this.tabindex", "role": "this.role" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FocusableDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[focusable]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { tabindex: [{
                type: HostBinding,
                args: ['tabindex']
            }], role: [{
                type: HostBinding,
                args: ['role']
            }] } });

/**
 * Example:
 *
 * ```
 * <ul [focusList]={focusFirst: true, withTypeAhead: true}>
 *   <li><a focusable href="#">1. Link</a></li>
 *   <li><a focusable href="#">2. Link</a></li>
 *   <li><a focusable href="#">3. Link</a></li>
 *   <li><a focusable href="#">4. Link</a></li>
 *   <li><a focusable href="#">5. Link</a></li>
 * </ul>
 * ```
 *
 * @see https://indepth.dev/posts/1147/doing-a11y-easily-with-angular-cdk-keyboard-navigable-lists
 */
class FocusListDirective {
    constructor() {
        this.options = {};
        this.tabindex = 0;
        this.role = 'list';
    }
    set focusList(options) {
        this.options = { ...this.options, ...options };
    }
    onKeydown(event) {
        if (!this.keyManager) {
            return;
        }
        this.keyManager.onKeydown(event);
    }
    ngAfterViewInit() {
        if (!this.items) {
            return;
        }
        this.keyManager = new FocusKeyManager(this.items).withWrap();
        if (this.options.withTypeAhead) {
            this.keyManager.withTypeAhead();
        }
        if (this.options.focusFirst) {
            this.keyManager.setFirstItemActive();
        }
    }
}
FocusListDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FocusListDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FocusListDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.12", type: FocusListDirective, selector: "[focusList]", inputs: { focusList: "focusList" }, host: { listeners: { "keydown": "onKeydown($event)" }, properties: { "tabindex": "this.tabindex", "role": "this.role" } }, queries: [{ propertyName: "items", predicate: FocusableDirective, descendants: true }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FocusListDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: '[focusList]',
                }]
        }], propDecorators: { focusList: [{
                type: Input
            }], items: [{
                type: ContentChildren,
                args: [FocusableDirective, { descendants: true }]
            }], tabindex: [{
                type: HostBinding,
                args: ['tabindex']
            }], role: [{
                type: HostBinding,
                args: ['role']
            }], onKeydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

class A11yModule {
}
A11yModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: A11yModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
A11yModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: A11yModule, declarations: [FocusListDirective, FocusableDirective], exports: [FocusListDirective, FocusableDirective] });
A11yModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: A11yModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: A11yModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [FocusListDirective, FocusableDirective],
                    exports: [FocusListDirective, FocusableDirective],
                }]
        }] });

/**
 * A service for helping with common string operation.
 * Should be used statically
 */
class StringHelper {
    /**
     * Check if given value is of type "string"
     */
    static isString(value) {
        return typeof value === 'string' || value instanceof String;
    }
    /**
     * Trims characters of a given string on the right
     */
    static rtrim(input, characters = ' \f\n\r\t') {
        let k = input.length - 1;
        while (k >= 0) {
            if (characters.indexOf(input.charAt(k)) === -1) {
                return input.substring(0, k + 1);
            }
            k--;
        }
        return input;
    }
    /**
     * Trims characters of a given string on the left
     */
    static ltrim(input, characters = ' \f\n\r\t') {
        let k = 0;
        while (k < input.length) {
            if (characters.indexOf(input.charAt(k)) === -1) {
                return input.substring(k, input.length);
            }
            k++;
        }
        return input;
    }
    /**
     * Trims characters of a given string on both sides
     */
    static trim(input, characters = ' \f\n\r\t') {
        return StringHelper.rtrim(StringHelper.ltrim(input, characters), characters);
    }
}

/**
 * Takes care of DI for non Injectables
 */
class InjectorClass {
    /**
     * Manual injection with correct type
     */
    static inject(token, context) {
        if (!InjectorClass.injector) {
            // tslint:disable-next-line:no-console
            console.warn(`
// Inject before being ready. Add InjectorClass to the first possible place. E.g.
export class CoreModule {
  constructor(injector: Injector) {
    InjectorClass.set(injector);
  }
}`, token, context);
            return undefined;
        }
        return InjectorClass.injector.get(token);
    }
    /**
     * Manual injection with correct type
     */
    static async waitAndInject(token) {
        await firstValueFrom(InjectorClass.ready);
        return InjectorClass.injector.get(token);
    }
    /**
     * Set the injector and mark it ready
     */
    static set(injector) {
        InjectorClass.injector = injector;
        InjectorClass.ready.next(undefined);
        InjectorClass.ready.complete();
    }
}
/**
 * Provides injector for non DI components
 */
InjectorClass.injector = undefined;
/**
 * Ready listener which resolves, as soon as
 * the Injector is available
 */
InjectorClass.ready = new Subject();

/**
 * A service for helping with common url operation.
 */
class UrlHelperService {
    constructor(config, location, router, translocoService) {
        this.config = config;
        this.location = location;
        this.router = router;
        this.translocoService = translocoService;
    }
    localizeUrl(url) {
        const normalizedUrl = `/${StringHelper.trim(url, '/')}`;
        if (this.config.ignoreLanguageForUrls) {
            return normalizedUrl;
        }
        return `/${this.translocoService.getActiveLang()}${normalizedUrl}`;
    }
    localizeUrlAsync(url) {
        const normalizedUrl = `/${StringHelper.trim(url, '/')}`;
        if (this.config.ignoreLanguageForUrls) {
            return of(normalizedUrl);
        }
        return this.translocoService.langChanges$.pipe(map((lang) => `/${lang}${normalizedUrl}`));
    }
    normalize(path) {
        path = path.toLowerCase();
        path = path.replace(/ä/g, 'ae');
        path = path.replace(/ö/g, 'oe');
        path = path.replace(/ü/g, 'ue');
        path = path.replace(/ß/g, 'ss');
        path = path.replace(/é/g, 'e');
        path = path.replace(/è/g, 'e');
        path = path.replace(/ê/g, 'e');
        path = path.replace(/â/g, 'a');
        path = path.replace(/à/g, 'a');
        path = path.replace(/[^a-z0-9-/]/gi, '-');
        path = path.replace(/-+/g, '-');
        return path;
    }
    enforceAbsoluteUrl(url) {
        url = url ?? this.location.path();
        return url.match('^http') ? url : `${this.config.baseUrl}${url}`;
    }
    /**
     * Get current url path
     */
    getPath() {
        return this.getPathWithoutParams();
    }
    /**
     * Get url query params as object
     */
    getQueryParams() {
        return this.router.parseUrl(this.location.path()).queryParams || {};
    }
    /**
     * Convert an object to query params
     */
    objectToQueryParams(object = {}) {
        return Object.entries(object)
            .map((p) => `${encodeURIComponent(p[0])}=${encodeURIComponent(Array.isArray(p[1]) ? p[1].join(';') : p[1])}`)
            .join('&');
    }
    /**
     * Get the url the Angular way and then strip of any params
     */
    getPathWithoutParams() {
        const url = this.location.path();
        if (!url.match(/\?/)) {
            return url;
        }
        return url.substring(0, url.lastIndexOf('?'));
    }
}
UrlHelperService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UrlHelperService, deps: [{ token: CORE_CONFIG }, { token: i2.Location }, { token: i2$1.Router }, { token: i1$1.TranslocoService }], target: i0.ɵɵFactoryTarget.Injectable });
UrlHelperService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UrlHelperService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UrlHelperService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [CORE_CONFIG]
                }] }, { type: i2.Location }, { type: i2$1.Router }, { type: i1$1.TranslocoService }]; } });

class DateHelperService {
    /**
     * @alexander_widmer
     * Perfect case for writing an Unit-Test
     */
    static getDateInWorkingDaysFromNow(daysFromNow = 1) {
        const today = new Date();
        let daysSinceToday = 0;
        let workingDaysSinceToday = 0;
        while (workingDaysSinceToday < daysFromNow + 1) {
            const dateToCheck = new Date(today.getFullYear(), today.getMonth(), today.getDate() + daysSinceToday);
            if (DateHelperService.workingDays.includes(dateToCheck.getDay())) {
                workingDaysSinceToday++;
            }
            daysSinceToday++;
        }
        return new Date(today.getFullYear(), today.getMonth(), today.getDate() + daysSinceToday).getTime();
    }
    static getISODate(timestamp) {
        const date = new Date(timestamp);
        return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
    }
    static getUTCDate(dateString) {
        const newDate = new Date(dateString);
        return newDate.getTime() - newDate.getTimezoneOffset() * 60000;
    }
}
/**
 * JavaScript working days
 * 1 - Monday
 * 2 - Tuesday
 * 3 - Wednesday
 * 4 - Thursday
 * 5 - Friday
 */
DateHelperService.workingDays = [1, 2, 3, 4, 5];
DateHelperService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DateHelperService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
DateHelperService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DateHelperService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DateHelperService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

function queryPaginated(http, baseUrl, urlOrFilter) {
    let params = new HttpParams();
    let url = baseUrl;
    if (typeof urlOrFilter === 'string') {
        // we were given a page URL, use it
        url = urlOrFilter;
    }
    else if (typeof urlOrFilter === 'object') {
        // we were given filtering criteria, build the query string
        Object.keys(urlOrFilter)
            .sort()
            .forEach((key) => {
            const value = urlOrFilter[key];
            if (value !== null) {
                params = params.set(key, value.toString());
            }
        });
    }
    return http.get(url, {
        params: params,
    });
}

/**
 * A service for building the url path
 * Should be used statically
 */
class UrlPathHelperService {
    static getUrlPath(sUrlPath = '', defaultLangCode) {
        let path = '/' + defaultLangCode;
        path += sUrlPath.replace(/^\/[\w'-]*/, '');
        return StringHelper.rtrim(path, '/');
    }
}
UrlPathHelperService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UrlPathHelperService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
UrlPathHelperService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UrlPathHelperService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UrlPathHelperService, decorators: [{
            type: Injectable
        }] });

class LocalizeUrlPathAsyncPipe {
    constructor(urlHelper) {
        this.urlHelper = urlHelper;
    }
    transform(path) {
        return this.urlHelper.localizeUrlAsync(path);
    }
}
LocalizeUrlPathAsyncPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LocalizeUrlPathAsyncPipe, deps: [{ token: UrlHelperService }], target: i0.ɵɵFactoryTarget.Pipe });
LocalizeUrlPathAsyncPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: LocalizeUrlPathAsyncPipe, name: "localizeUrlPathAsync" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LocalizeUrlPathAsyncPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'localizeUrlPathAsync',
                }]
        }], ctorParameters: function () { return [{ type: UrlHelperService }]; } });

class LocalizeUrlPathPipe {
    constructor(urlHelper) {
        this.urlHelper = urlHelper;
    }
    transform(path) {
        return this.urlHelper.localizeUrl(path);
    }
}
LocalizeUrlPathPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LocalizeUrlPathPipe, deps: [{ token: UrlHelperService }], target: i0.ɵɵFactoryTarget.Pipe });
LocalizeUrlPathPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: LocalizeUrlPathPipe, name: "localizeUrlPath" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LocalizeUrlPathPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'localizeUrlPath',
                }]
        }], ctorParameters: function () { return [{ type: UrlHelperService }]; } });

class LocalizeUrlPathModule {
}
LocalizeUrlPathModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LocalizeUrlPathModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
LocalizeUrlPathModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: LocalizeUrlPathModule, declarations: [LocalizeUrlPathPipe, LocalizeUrlPathAsyncPipe], imports: [CommonModule], exports: [LocalizeUrlPathPipe, LocalizeUrlPathAsyncPipe] });
LocalizeUrlPathModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LocalizeUrlPathModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LocalizeUrlPathModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [LocalizeUrlPathPipe, LocalizeUrlPathAsyncPipe],
                    exports: [LocalizeUrlPathPipe, LocalizeUrlPathAsyncPipe],
                }]
        }] });

class UrlEncodingPipe {
    constructor(urlHelperService) {
        this.urlHelperService = urlHelperService;
    }
    transform(path) {
        return this.urlHelperService.normalize(path);
    }
}
UrlEncodingPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UrlEncodingPipe, deps: [{ token: UrlHelperService }], target: i0.ɵɵFactoryTarget.Pipe });
UrlEncodingPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: UrlEncodingPipe, name: "urlEncoding" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UrlEncodingPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'urlEncoding' }]
        }], ctorParameters: function () { return [{ type: UrlHelperService }]; } });

class UrlEncodingModule {
}
UrlEncodingModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UrlEncodingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
UrlEncodingModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: UrlEncodingModule, declarations: [UrlEncodingPipe], imports: [CommonModule], exports: [UrlEncodingPipe] });
UrlEncodingModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UrlEncodingModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UrlEncodingModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [UrlEncodingPipe],
                    exports: [UrlEncodingPipe],
                }]
        }] });

class DebounceClickDirective {
    constructor() {
        this.debounceTime = 500;
        this.debounceClick = new EventEmitter();
        this.clicks = new Subject();
    }
    ngOnInit() {
        this.subscription = this.clicks.pipe(debounceTime(this.debounceTime)).subscribe((e) => this.debounceClick.emit(e));
    }
    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }
    clickEvent(event) {
        event.preventDefault();
        event.stopPropagation();
        this.clicks.next(event);
    }
}
DebounceClickDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DebounceClickDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
DebounceClickDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.12", type: DebounceClickDirective, selector: "[debounceClick]", inputs: { debounceTime: "debounceTime" }, outputs: { debounceClick: "debounceClick" }, host: { listeners: { "click": "clickEvent($event)" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DebounceClickDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[debounceClick]',
                }]
        }], propDecorators: { debounceTime: [{
                type: Input
            }], debounceClick: [{
                type: Output
            }], clickEvent: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

class DebounceClickModule {
}
DebounceClickModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DebounceClickModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DebounceClickModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: DebounceClickModule, declarations: [DebounceClickDirective], imports: [CommonModule], exports: [DebounceClickDirective] });
DebounceClickModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DebounceClickModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DebounceClickModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [DebounceClickDirective],
                    exports: [DebounceClickDirective],
                }]
        }] });

/**
 * Offers common breakpoints to reuse within the application.
 * Alls breakpoints are constructed with "max-width". That means,
 * they say if a screen is smaller than that.
 *
 * Examples
 * `mobile` means, that the screen is of mobile size or smaller
 * `tablet` means, that the screen is of tablet size or smaller
 */
let BreakpointService = class BreakpointService {
    constructor(config, breakpointObserver) {
        this.breakpointObserver = breakpointObserver;
        this.mobile = this.breakpointObserver.observe([`(max-width: ${config.mobileBreakpoint})`]).pipe(untilDestroyed(this), map((result) => result.matches));
        this.tablet = this.breakpointObserver.observe([`(max-width: ${config.tabletBreakpoint})`]).pipe(untilDestroyed(this), map((result) => result.matches));
    }
};
BreakpointService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BreakpointService, deps: [{ token: CORE_CONFIG }, { token: i1$2.BreakpointObserver }], target: i0.ɵɵFactoryTarget.Injectable });
BreakpointService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BreakpointService, providedIn: 'root' });
BreakpointService = __decorate([
    UntilDestroy()
], BreakpointService);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BreakpointService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [CORE_CONFIG]
                }] }, { type: i1$2.BreakpointObserver }]; } });

class CustomHttpParameterCodec {
    decodeKey(key) {
        return decodeURIComponent(key);
    }
    decodeValue(value) {
        return decodeURIComponent(value);
    }
    encodeKey(key) {
        return encodeURIComponent(key);
    }
    encodeValue(value) {
        return encodeURIComponent(value);
    }
}

const defaultErrorConfig = {
    showOutput: false,
};
const ERROR_CONFIG = new InjectionToken('ErrorConfig');

class ErrorService {
    constructor() {
        this.errors = new ReplaySubject(1);
    }
    static mapError(error, options, params) {
        return {
            ...error,
            error,
            label: options.label,
            display: options.display || false,
            translate: options.translate || false,
            translateScope: options.translateScope || undefined,
            ignore: options.ignore || false,
            params,
        };
    }
    add(error) {
        this.errors.next(error);
        return error;
    }
    listen() {
        return this.errors.asObservable();
    }
}
ErrorService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ErrorService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ErrorService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ErrorService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ErrorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class ErrorHandlerService extends ErrorHandler {
    constructor(errorService) {
        super();
        this.errorService = errorService;
    }
    unhandledRejection(event) {
        this.handleError(event, true);
    }
    handleError(errorEvent, unhandled = false) {
        this.errorService.add({
            label: `ErrorHandlerService.handleError(${unhandled ? 'unhandled' : ''})`,
            error: errorEvent,
        });
    }
}
ErrorHandlerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ErrorHandlerService, deps: [{ token: ErrorService }], target: i0.ɵɵFactoryTarget.Injectable });
ErrorHandlerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ErrorHandlerService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ErrorHandlerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: ErrorService }]; }, propDecorators: { unhandledRejection: [{
                type: HostListener,
                args: ['window:unhandledrejection', []]
            }] } });

class ErrorModule {
    static forRoot(config) {
        return {
            ngModule: ErrorModule,
            providers: [
                config?.disableErrorHandler ? [] : { provide: ErrorHandler, useClass: ErrorHandlerService },
                {
                    provide: ERROR_CONFIG,
                    useValue: { ...defaultErrorConfig, ...config },
                },
            ],
        };
    }
}
ErrorModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ErrorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ErrorModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: ErrorModule, imports: [CommonModule] });
ErrorModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ErrorModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ErrorModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }] });

class LogService {
    constructor(config) {
        this.config = config;
        this.showOutput = this.config.showOutput;
    }
    error(message, params) {
        if (!this.showOutput) {
            return;
        }
        console.error(message, params);
    }
    log(message, params) {
        if (!this.showOutput) {
            return;
        }
        console.log(message, params);
    }
    warn(message, params) {
        if (!this.showOutput) {
            return;
        }
        console.warn(message, params);
    }
}
LogService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LogService, deps: [{ token: ERROR_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
LogService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LogService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LogService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [ERROR_CONFIG]
                }] }]; } });

function Catch(options) {
    return (_target, _propertyKey, descriptor) => {
        const originalMethod = descriptor.value;
        descriptor.value = function (...args) {
            let params;
            if (args.length) {
                if (options.trackParam === true) {
                    params = JSON.stringify(args[0]);
                }
                else if (options.trackParam) {
                    params = JSON.stringify(args[0][options.trackParam]);
                }
            }
            const errorService = InjectorClass.inject(ErrorService);
            if (!errorService) {
                return;
            }
            try {
                const methodReturn = originalMethod.apply(this, args);
                if (methodReturn instanceof Promise) {
                    return methodReturn.catch((error) => errorService.add(ErrorService.mapError(error, options, params)));
                }
                if (methodReturn instanceof Observable) {
                    return methodReturn.pipe(catchError((error) => of(errorService.add(ErrorService.mapError(error, options, params)))));
                }
                return methodReturn;
            }
            catch (exception) {
                errorService.add(ErrorService.mapError(exception, options, params));
            }
        };
        return descriptor;
    };
}

function caughtError(options, params) {
    return catchError((err) => {
        const errorService = InjectorClass.inject(ErrorService);
        const error = ErrorService.mapError(err, options, params);
        if (errorService) {
            errorService.add(error);
        }
        return of(error);
    });
}

var HookPriority;
(function (HookPriority) {
    HookPriority[HookPriority["HIGHEST"] = 100] = "HIGHEST";
    HookPriority[HookPriority["HIGH"] = 200] = "HIGH";
    HookPriority[HookPriority["DEFAULT"] = 500] = "DEFAULT";
    HookPriority[HookPriority["LOW"] = 800] = "LOW";
    HookPriority[HookPriority["LOWEST"] = 900] = "LOWEST";
})(HookPriority || (HookPriority = {}));

var HookReturnType;
(function (HookReturnType) {
    HookReturnType["PRIMITIVE"] = "PRIMITIVE";
    HookReturnType["PROMISE"] = "PROMISE";
    HookReturnType["OBSERVABLE"] = "OBSERVABLE";
})(HookReturnType || (HookReturnType = {}));

class HookHandlerFactoryService {
    constructor(logService) {
        this.logService = logService;
        this.handlers = [];
    }
    register(handler) {
        this.handlers = [...this.handlers, handler];
        this.handlers.sort((a, b) => a.priority() - b.priority());
    }
    /**
     * Passes the hook event to all handlers.
     * If one handler throws an exception, the complete execution is stopped.
     */
    handle(event) {
        const originalEvent = { ...event };
        try {
            for (const handler of this.filterHandlersByEvent(event)) {
                event.callable = handler.handle({ ...event }, originalEvent);
            }
        }
        catch (error) {
            this.logService.warn('@Hook: Execution canceled!', error);
            event.callable = () => undefined;
        }
        return event.callable.apply(event.host, event.params);
    }
    filterHandlersByEvent(event) {
        return this.handlers.filter((h) => h.ids().find((id) => isEqual(id, event.id)));
    }
}
HookHandlerFactoryService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: HookHandlerFactoryService, deps: [{ token: LogService }], target: i0.ɵɵFactoryTarget.Injectable });
HookHandlerFactoryService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: HookHandlerFactoryService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: HookHandlerFactoryService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: LogService }]; } });

/**
 * This decorator can be used to hook onto any method.
 *
 * Be aware, that hooking into synced methods should only be handled by synced hooks,
 * since the caller of the method might not expect an async result.
 * Use `returnType` to get warnings about mismatched sync/async
 */
function Hook(options) {
    return (_target, _propertyKey, descriptor) => {
        const originalMethod = descriptor.value;
        descriptor.value = function (...args) {
            const hookHandler = InjectorClass.inject(HookHandlerFactoryService);
            const logService = InjectorClass.inject(LogService);
            if (!hookHandler || !logService) {
                return;
            }
            const returnValue = hookHandler.handle({
                id: options.id,
                callable: originalMethod,
                host: this,
                params: args.filter(Boolean),
            });
            if (!options.returnType) {
                return returnValue;
            }
            const isPromise = returnValue instanceof Promise;
            const isObservable = returnValue instanceof Observable;
            if (options.returnType === HookReturnType.PRIMITIVE && (isPromise || isObservable)) {
                logService.warn('@Hook: Primitive return type expected, but got async type!', returnValue);
            }
            if (options.returnType === HookReturnType.PROMISE && !isPromise) {
                logService.warn('@Hook: Promise return type expected, but got something else!', returnValue);
            }
            if (options.returnType === HookReturnType.OBSERVABLE && !isObservable) {
                logService.warn('@Hook: Observable return type expected, but got something else!', returnValue);
            }
            return returnValue;
        };
        return descriptor;
    };
}

class HookHandlerAbstract {
    /**
     * @see HookHandlerInterface.priority()
     */
    priority() {
        return HookPriority.DEFAULT;
    }
    /**
     * @see HookHandlerInterface.handle()
     */
    handle(event, originalEvent) {
        return () => {
            const callable = this.before(event, originalEvent);
            return this.after(event, callable.apply(event.host, event.params));
        };
    }
    /**
     * If only a simple "before" hook is needed, you can just overwrite this method.
     */
    before(event, originalEvent) {
        return event.callable || originalEvent.callable;
    }
    /**
     * If only a simple "after" hook is needed, you can just overwrite this method.
     */
    after(_, result) {
        return result;
    }
}
HookHandlerAbstract.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: HookHandlerAbstract, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
HookHandlerAbstract.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: HookHandlerAbstract, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: HookHandlerAbstract, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

let AuthHookTriggerService = class AuthHookTriggerService {
    constructor(auth) {
        this.auth = auth;
        this.auth.authUser$
            .pipe(distinctUntilChanged((a, b) => isEqual(a, b)), switchMap((user) => this.triggerAuthChange(user)), untilDestroyed(this))
            .subscribe();
    }
    triggerAuthChange(user) {
        return of(user);
    }
};
AuthHookTriggerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthHookTriggerService, deps: [{ token: AuthService }], target: i0.ɵɵFactoryTarget.Injectable });
AuthHookTriggerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthHookTriggerService, providedIn: 'root' });
__decorate([
    Hook({
        id: { type: 'AuthChangedHook' },
        returnType: HookReturnType.OBSERVABLE,
    })
], AuthHookTriggerService.prototype, "triggerAuthChange", null);
AuthHookTriggerService = __decorate([
    UntilDestroy()
], AuthHookTriggerService);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthHookTriggerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: AuthService }]; }, propDecorators: { triggerAuthChange: [] } });

class AuthHookModule {
    constructor(injector) {
        InjectorClass.set(injector);
        injector.get(AuthHookTriggerService);
    }
    static forRoot() {
        return {
            ngModule: AuthHookModule,
            providers: [],
        };
    }
}
AuthHookModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthHookModule, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.NgModule });
AuthHookModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: AuthHookModule, imports: [CommonModule] });
AuthHookModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthHookModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthHookModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }], ctorParameters: function () { return [{ type: i0.Injector }]; } });

let ErrorHookTriggerService = class ErrorHookTriggerService {
    constructor(errorService) {
        this.errorService = errorService;
        this.errorService
            .listen()
            .pipe(distinctUntilChanged(), switchMap((error) => this.triggerError(error)), untilDestroyed(this))
            .subscribe();
    }
    triggerError(error) {
        return of(error);
    }
};
ErrorHookTriggerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ErrorHookTriggerService, deps: [{ token: ErrorService }], target: i0.ɵɵFactoryTarget.Injectable });
ErrorHookTriggerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ErrorHookTriggerService, providedIn: 'root' });
__decorate([
    Hook({
        id: { type: 'ErrorHook' },
        returnType: HookReturnType.OBSERVABLE,
    })
], ErrorHookTriggerService.prototype, "triggerError", null);
ErrorHookTriggerService = __decorate([
    UntilDestroy()
], ErrorHookTriggerService);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ErrorHookTriggerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: ErrorService }]; }, propDecorators: { triggerError: [] } });

class ErrorHookModule {
    constructor(injector) {
        InjectorClass.set(injector);
        injector.get(ErrorHookTriggerService);
    }
    static forRoot() {
        return {
            ngModule: ErrorHookModule,
            providers: [],
        };
    }
}
ErrorHookModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ErrorHookModule, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.NgModule });
ErrorHookModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: ErrorHookModule, imports: [CommonModule] });
ErrorHookModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ErrorHookModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ErrorHookModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }], ctorParameters: function () { return [{ type: i0.Injector }]; } });

let LanguageHookTriggerService = class LanguageHookTriggerService {
    constructor(transloco) {
        this.transloco = transloco;
        this.transloco.langChanges$
            .pipe(skip(1), // Transloco first triggers the defaultLang, before we set any language
        distinctUntilChanged(), switchMap((lang) => this.triggerLanguageChange(lang)), untilDestroyed(this))
            .subscribe();
    }
    triggerLanguageChange(lang) {
        return of(lang);
    }
};
LanguageHookTriggerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LanguageHookTriggerService, deps: [{ token: i1$1.TranslocoService }], target: i0.ɵɵFactoryTarget.Injectable });
LanguageHookTriggerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LanguageHookTriggerService, providedIn: 'root' });
__decorate([
    Hook({
        id: { type: 'LanguageChangedHook' },
        returnType: HookReturnType.OBSERVABLE,
    })
], LanguageHookTriggerService.prototype, "triggerLanguageChange", null);
LanguageHookTriggerService = __decorate([
    UntilDestroy()
], LanguageHookTriggerService);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LanguageHookTriggerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.TranslocoService }]; }, propDecorators: { triggerLanguageChange: [] } });

class LanguageHookModule {
    constructor(injector) {
        InjectorClass.set(injector);
        injector.get(LanguageHookTriggerService);
    }
    static forRoot() {
        return {
            ngModule: LanguageHookModule,
            providers: [],
        };
    }
}
LanguageHookModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LanguageHookModule, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.NgModule });
LanguageHookModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: LanguageHookModule, imports: [CommonModule] });
LanguageHookModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LanguageHookModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LanguageHookModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    providers: [],
                }]
        }], ctorParameters: function () { return [{ type: i0.Injector }]; } });

/**
 * Triggers the launched hook, as soon as Angular is ready.
 */
let LaunchedHookTriggerService = class LaunchedHookTriggerService {
    constructor() {
        of(true)
            .pipe(first(), switchMap((launched) => this.triggerLaunched(launched)), untilDestroyed(this))
            .subscribe();
    }
    triggerLaunched(launched) {
        return of(launched);
    }
};
LaunchedHookTriggerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LaunchedHookTriggerService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
LaunchedHookTriggerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LaunchedHookTriggerService, providedIn: 'root' });
__decorate([
    Hook({
        id: { type: 'LaunchedHook' },
        returnType: HookReturnType.OBSERVABLE,
    })
], LaunchedHookTriggerService.prototype, "triggerLaunched", null);
LaunchedHookTriggerService = __decorate([
    UntilDestroy()
], LaunchedHookTriggerService);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LaunchedHookTriggerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { triggerLaunched: [] } });

class AppHookModule {
    constructor(injector) {
        InjectorClass.set(injector);
        injector.get(LaunchedHookTriggerService);
    }
    static forRoot() {
        return {
            ngModule: AppHookModule,
            providers: [],
        };
    }
}
AppHookModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AppHookModule, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.NgModule });
AppHookModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: AppHookModule, imports: [CommonModule] });
AppHookModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AppHookModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AppHookModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }], ctorParameters: function () { return [{ type: i0.Injector }]; } });

const SENTRY_CONFIG = new InjectionToken('SentryConfig');

class SentryService {
    constructor(config) {
        this.config = config;
    }
    setup() {
        if (!this.config.active) {
            return;
        }
        Sentry.init({
            release: this.config.release,
            attachStacktrace: true,
            autoSessionTracking: false,
            debug: this.config.debug,
            environment: this.config.environment,
            dsn: this.config.dsn,
        });
    }
    updateUser(user) {
        if (!this.config.active) {
            return;
        }
        Sentry.setUser({ email: user ? user.sEmail || user.sUserName : undefined });
    }
    captureException(error) {
        if (!this.config.active) {
            return;
        }
        Sentry.captureException(error.error?.orginalError || error.error, {
            user: { ip_address: undefined },
            extra: error,
            tags: { label: error.label },
        });
    }
}
SentryService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SentryService, deps: [{ token: SENTRY_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
SentryService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SentryService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SentryService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [SENTRY_CONFIG]
                }] }]; } });

class SentryModule {
    constructor(sentryService) {
        this.sentryService = sentryService;
        this.sentryService.setup();
    }
    static forRoot(config) {
        return {
            ngModule: SentryModule,
            providers: [
                {
                    provide: SENTRY_CONFIG,
                    useValue: config,
                },
            ],
        };
    }
}
SentryModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SentryModule, deps: [{ token: SentryService }], target: i0.ɵɵFactoryTarget.NgModule });
SentryModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: SentryModule, imports: [CommonModule] });
SentryModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SentryModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SentryModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }], ctorParameters: function () { return [{ type: SentryService }]; } });

function tapOnce(tapFn, tapIndex = 0) {
    return (source$) => source$.pipe(concatMap((value, index) => {
        if (index === tapIndex) {
            tapFn(value);
        }
        return of(value);
    }));
}

function switchTap(next) {
    return (source$) => source$.pipe(connect((published) => published.pipe(switchMap((value) => from(next(value)).pipe(map(() => value))))));
}

const defaultElasticsearchConfig = {
    defaultLanguage: 'de',
    availableLanguages: ['de', 'en'],
};
const ELASTICSEARCH_CONFIG = new InjectionToken('ElasticsearchConfig');

class ElasticsearchModule {
    static forRoot(config) {
        return {
            ngModule: ElasticsearchModule,
            providers: [
                {
                    provide: ELASTICSEARCH_CONFIG,
                    useValue: { ...defaultElasticsearchConfig, ...config },
                },
            ],
        };
    }
}
ElasticsearchModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ElasticsearchModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ElasticsearchModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: ElasticsearchModule, imports: [CommonModule] });
ElasticsearchModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ElasticsearchModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ElasticsearchModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }] });

class ElasticSearchService {
    constructor(config, logService, http) {
        this.config = config;
        this.logService = logService;
        this.http = http;
        this.indexLanguage = this.config.defaultLanguage;
        this.availableLanguages = config.availableLanguages;
        this.setIndexLanguage(config.defaultLanguage);
    }
    setIndexLanguage(lang) {
        const shortenedLanguage = lang.substring(0, 2);
        if (!this.availableLanguages.includes(shortenedLanguage)) {
            return this.logService.warn('Trying to set a non-configured index language', lang);
        }
        this.indexLanguage = shortenedLanguage;
    }
    execute(url, query) {
        return this.http.post(url, query);
    }
    localizedIndex(name) {
        return `${name}_${this.indexLanguage}`;
    }
}
ElasticSearchService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ElasticSearchService, deps: [{ token: ELASTICSEARCH_CONFIG }, { token: LogService }, { token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
ElasticSearchService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ElasticSearchService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ElasticSearchService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [ELASTICSEARCH_CONFIG]
                }] }, { type: LogService }, { type: i1.HttpClient }]; } });

// TODO replace with akita persist by locastorage
class LocalstorageService {
    async set(key, value) {
        return localStorage.setItem(key, JSON.stringify(value));
    }
    async get(key) {
        return JSON.parse(localStorage.getItem(key));
    }
    async remove(key) {
        return localStorage.removeItem(key);
    }
}
LocalstorageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LocalstorageService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
LocalstorageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LocalstorageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LocalstorageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class SimpleStorageService {
    constructor(storage) {
        this.storage = storage;
    }
    async set(value) {
        return this.storage.set(this.key(), value);
    }
    async get() {
        return this.storage.get(this.key());
    }
    async remove() {
        return this.storage.remove(this.key());
    }
}
SimpleStorageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SimpleStorageService, deps: [{ token: LocalstorageService }], target: i0.ɵɵFactoryTarget.Injectable });
SimpleStorageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SimpleStorageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SimpleStorageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: LocalstorageService }]; } });

// TODO replace with akita persist by locastorage
class StorageService {
    constructor() {
        this.localForage = localForage;
        this.ready = false;
    }
    async set(key, value) {
        await this.init();
        return this.localForage.setItem(key, value);
    }
    async get(key) {
        await this.init();
        return this.localForage.getItem(key);
    }
    async remove(key) {
        await this.init();
        return this.localForage.removeItem(key);
    }
    async init() {
        if (this.ready) {
            return;
        }
        this.ready = true;
        // eslint-disable-next-line angular/module-getter
        this.localForage.config({
            name: StorageService.STORAGE_NAME,
            storeName: StorageService.STORAGE_NAME,
        });
    }
}
StorageService.STORAGE_NAME = 'lobos-webapp';
StorageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: StorageService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
StorageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: StorageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: StorageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class MemoryStorageService extends StorageService {
    constructor() {
        super(...arguments);
        this.storage = {};
    }
    async set(key, value) {
        return (this.storage[key] = value);
    }
    async get(key) {
        return this.storage[key];
    }
    async remove(key) {
        const { [key]: _, ...rest } = this.storage;
        this.storage = rest;
    }
}
MemoryStorageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MemoryStorageService, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
MemoryStorageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MemoryStorageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MemoryStorageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class RedirectLanguageGuard {
    constructor(router) {
        this.router = router;
    }
    canActivate() {
        this.router.navigateByUrl('de');
        return true;
    }
}
RedirectLanguageGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RedirectLanguageGuard, deps: [{ token: i2$1.Router }], target: i0.ɵɵFactoryTarget.Injectable });
RedirectLanguageGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RedirectLanguageGuard, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RedirectLanguageGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i2$1.Router }]; } });

let RequestDebounceStore = class RequestDebounceStore extends EntityStore {
    constructor() {
        super();
    }
};
RequestDebounceStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RequestDebounceStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
RequestDebounceStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RequestDebounceStore, providedIn: 'root' });
RequestDebounceStore = __decorate([
    StoreConfig({
        name: 'request-debounce',
        idKey: 'id',
        resettable: true,
    })
], RequestDebounceStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RequestDebounceStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class RequestDebounceQuery extends QueryEntity {
    constructor(store) {
        super(store);
    }
}
RequestDebounceQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RequestDebounceQuery, deps: [{ token: RequestDebounceStore }], target: i0.ɵɵFactoryTarget.Injectable });
RequestDebounceQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RequestDebounceQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RequestDebounceQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: RequestDebounceStore }]; } });

class RequestDebounceService {
    constructor(store, query) {
        this.store = store;
        this.query = query;
    }
    debounce(id, store, query, observable) {
        if (query.hasEntity(id)) {
            return query.selectEntity(id);
        }
        return this._debounce(id, store, observable);
    }
    debounceAll(store, observable) {
        return this._debounce('all', store, observable, true);
    }
    _debounce(id, store, observable, multi = false) {
        const debounceId = `${store.storeName}_${id.toString()}`;
        if (this.query.hasEntity(debounceId)) {
            const cache = this.query.getEntity(debounceId);
            if (['done', 'loading'].includes(cache.state)) {
                return cache.observable;
            }
        }
        const state = {
            id: debounceId,
            entityId: id.toString(),
            store: store.storeName,
            idKey: store.idKey,
            state: 'loading',
            observable: observable.pipe(tap((result) => {
                if (multi) {
                    store.upsertMany(result);
                }
                else {
                    // @ts-ignore
                    store.upsert(id, result);
                }
                this.store.update(debounceId, { state: 'done' });
            }), catchError((error) => {
                this.store.update(debounceId, { state: 'error' });
                return throwError(() => error);
            }), shareReplay(1)),
        };
        this.store.upsert(debounceId, state);
        return state.observable;
    }
}
RequestDebounceService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RequestDebounceService, deps: [{ token: RequestDebounceStore }, { token: RequestDebounceQuery }], target: i0.ɵɵFactoryTarget.Injectable });
RequestDebounceService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RequestDebounceService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RequestDebounceService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: RequestDebounceStore }, { type: RequestDebounceQuery }]; } });

/*
 * Public API Surface of core
 */

const GOOGLE_TAG_MANAGER_CONFIG = new InjectionToken('GoogleTagManagerConfig');

class ConsentService {
    constructor(coreConfig) {
        this.coreConfig = coreConfig;
        this.consent$ = new BehaviorSubject(!this.coreConfig.useConsent);
        this.consentAccepted$ = this.consent$.asObservable();
    }
    updateConsent(value) {
        this.consent$.next(value);
    }
}
ConsentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ConsentService, deps: [{ token: CORE_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
ConsentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ConsentService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ConsentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [CORE_CONFIG]
                }] }]; } });

let TrackingFactory = class TrackingFactory {
    constructor(errorService, consentService) {
        this.errorService = errorService;
        this.consentService = consentService;
        this.adapters = [];
        this.eventCache = [];
        this.pageCache = [];
        this.consentAccepted = false;
        consentService.consentAccepted$
            .pipe(tap((accepted) => {
            if (accepted) {
                this.consentAccepted = accepted;
                this.pushEventCache();
                this.pushPageCache();
            }
        }), untilDestroyed(this))
            .subscribe();
    }
    register(adapter) {
        this.adapters.push(adapter);
    }
    async trackEvent(event) {
        if (!this.consentAccepted) {
            this.eventCache.push(event);
            return;
        }
        for (const adapter of this.adapters) {
            try {
                await adapter.trackEvent(event);
            }
            catch (error) {
                this.errorService.add({ label: 'TrackingFactory.trackEvent()', ...error, error });
            }
        }
    }
    async trackPage(page) {
        if (!this.consentAccepted) {
            this.pageCache.push(page);
            return;
        }
        for (const adapter of this.adapters) {
            try {
                await adapter.trackPage(page);
            }
            catch (error) {
                this.errorService.add({ label: 'TrackingFactory.trackPage()', ...error, error });
            }
        }
    }
    pushEventCache() {
        this.eventCache.forEach((item) => {
            this.trackEvent(item);
        });
        this.eventCache = [];
    }
    pushPageCache() {
        this.pageCache.forEach((item) => {
            this.trackPage(item);
        });
        this.pageCache = [];
    }
};
TrackingFactory.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TrackingFactory, deps: [{ token: ErrorService }, { token: ConsentService }], target: i0.ɵɵFactoryTarget.Injectable });
TrackingFactory.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TrackingFactory, providedIn: 'root' });
TrackingFactory = __decorate([
    UntilDestroy()
], TrackingFactory);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TrackingFactory, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: ErrorService }, { type: ConsentService }]; } });

class GoogleTagManagerLoaderService {
    constructor(config, platformId, document, errorService) {
        this.config = config;
        this.platformId = platformId;
        this.document = document;
        this.errorService = errorService;
        this.loaded$ = new BehaviorSubject(false);
        this.isBrowser = isPlatformBrowser(this.platformId);
    }
    pushTag(item) {
        return this.addGtmToDom().pipe(filter((loaded) => loaded), map(() => this.pushOnDataLayer(item)), first());
    }
    getDataLayer() {
        if (!this.isBrowser) {
            return [];
        }
        return (window.dataLayer = window.dataLayer || []);
    }
    pushOnDataLayer(obj) {
        const dataLayer = this.getDataLayer();
        dataLayer.push(obj);
    }
    addGtmToDom() {
        if (this.loaded$.value) {
            return this.loaded$;
        }
        this.pushOnDataLayer({
            'gtm.start': new Date().getTime(),
            event: 'gtm.js',
        });
        const gtmScript = this.document.createElement('script');
        gtmScript.id = 'GTMscript';
        gtmScript.async = true;
        gtmScript.src = this.applyGtmQueryParams('https://www.googletagmanager.com/gtm.js');
        gtmScript.addEventListener('load', () => this.loaded$.next(true));
        gtmScript.addEventListener('error', (error) => this.errorService.add({ label: 'GtmLoaderService.addGtmToDom()', ...error, error }));
        this.document.head.insertBefore(gtmScript, this.document.head.firstChild);
        // stop here, if no SSR
        if (this.isBrowser) {
            return this.loaded$;
        }
        const iframe = this.document.createElement('iframe');
        iframe.setAttribute('src', this.applyGtmQueryParams('https://www.googletagmanager.com/ns.html'));
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.display = 'none';
        iframe.style.visibility = 'hidden';
        const noscript = this.document.createElement('noscript');
        noscript.id = 'GTMiframe';
        noscript.appendChild(iframe);
        this.document.body.insertBefore(noscript, this.document.body.firstChild);
        return this.loaded$;
    }
    applyGtmQueryParams(url) {
        if (url.indexOf('?') === -1) {
            url += '?';
        }
        return (url +
            Object.keys(this.config)
                .filter((k) => this.config[k])
                .map((k) => `${k}=${this.config[k]}`)
                .join('&'));
    }
}
GoogleTagManagerLoaderService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GoogleTagManagerLoaderService, deps: [{ token: GOOGLE_TAG_MANAGER_CONFIG }, { token: PLATFORM_ID }, { token: DOCUMENT }, { token: ErrorService }], target: i0.ɵɵFactoryTarget.Injectable });
GoogleTagManagerLoaderService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GoogleTagManagerLoaderService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GoogleTagManagerLoaderService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [GOOGLE_TAG_MANAGER_CONFIG]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: ErrorService }]; } });

class GoogleTagManagerAdapter {
    constructor(gtmLoaderService, logService) {
        this.gtmLoaderService = gtmLoaderService;
        this.logService = logService;
    }
    /**
     * Track events to GTM
     *
     * @see https://developers.google.com/tag-manager/ecommerce-ga4 for ecommerce events
     * @see https://support.google.com/analytics/answer/9267735?hl=en for recommended events
     */
    async trackEvent(event) {
        this.logService.log('GTM trackEvent', event);
        await this.pushTag({
            event: event['name'],
            ...event['payload'],
        });
    }
    /**
     * GTM page tracking in the backend is very unreliable. Events are fired multiple times
     * and pages are tracked with different page titles (through timing issues)
     *
     * @since 26.10.2021 the virtualPageview is the way to go
     * @see https://www.analyticsmania.com/post/single-page-web-app-with-google-tag-manager/ for other options
     */
    async trackPage(page) {
        this.logService.log('GTM trackPage', page);
        await this.pushTag({
            event: 'virtualPageview',
            pageUrl: page.url,
            pageTitle: page.title,
        });
    }
    async pushTag(tag) {
        await lastValueFrom((tag['ecommerce'] ? this.gtmLoaderService.pushTag({ ecommerce: null }) : of(undefined)).pipe(switchMap(() => this.gtmLoaderService.pushTag(tag))));
    }
}
GoogleTagManagerAdapter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GoogleTagManagerAdapter, deps: [{ token: GoogleTagManagerLoaderService }, { token: LogService }], target: i0.ɵɵFactoryTarget.Injectable });
GoogleTagManagerAdapter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GoogleTagManagerAdapter, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GoogleTagManagerAdapter, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: GoogleTagManagerLoaderService }, { type: LogService }]; } });

class GoogleTagManagerAdapterModule {
    constructor(trackingFactory, gtmAdapter) {
        this.trackingFactory = trackingFactory;
        this.gtmAdapter = gtmAdapter;
        trackingFactory.register(gtmAdapter);
    }
    static forRoot(config) {
        return {
            ngModule: GoogleTagManagerAdapterModule,
            providers: [
                {
                    provide: GOOGLE_TAG_MANAGER_CONFIG,
                    useValue: { ...config },
                },
            ],
        };
    }
}
GoogleTagManagerAdapterModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GoogleTagManagerAdapterModule, deps: [{ token: TrackingFactory }, { token: GoogleTagManagerAdapter }], target: i0.ɵɵFactoryTarget.NgModule });
GoogleTagManagerAdapterModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: GoogleTagManagerAdapterModule, imports: [CommonModule] });
GoogleTagManagerAdapterModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GoogleTagManagerAdapterModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GoogleTagManagerAdapterModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }], ctorParameters: function () { return [{ type: TrackingFactory }, { type: GoogleTagManagerAdapter }]; } });

class GoogleTagManagerHelper {
    static hierarchicalCategoriesToItemCategories(hierCat = []) {
        const itemPartial = {};
        return hierCat
            .map((cat, index) => {
            if (index >= GoogleTagManagerHelper.ITEM_MAX_CATEGORIES) {
                return {};
            }
            return { [`item_category${index === 0 ? '' : index + 1}`]: cat.sValue };
        })
            .reduce((obj, item) => ({
            ...obj,
            ...item,
        }), itemPartial);
    }
    static articleToItem(article) {
        return {
            item_id: article.sArticleID.toString(),
            item_name: article.sName,
            currency: article.oPriceInfo?.sCurrencyCode,
            price: GoogleTagManagerHelper.articlePrice(article),
            discount: article.oPriceInfo?.decSpecialPriceNet ? article.oPriceInfo?.decBasePriceNet - article.oPriceInfo?.decSpecialPriceNet : 0,
            ...GoogleTagManagerHelper.hierarchicalCategoriesToItemCategories(article.oProductInfo?.length ? article.oProductInfo[0].listHierarchicalCategories : []),
        };
    }
    static articlePrice(article) {
        return article.oPriceInfo?.decSpecialPriceNet || article.oPriceInfo?.decBasePriceNet;
    }
}
GoogleTagManagerHelper.ITEM_MAX_CATEGORIES = 5;
GoogleTagManagerHelper.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GoogleTagManagerHelper, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
GoogleTagManagerHelper.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GoogleTagManagerHelper, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: GoogleTagManagerHelper, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

const doTracking = (options, args, instance) => {
    const tracking = InjectorClass.inject(TrackingFactory);
    const errorService = InjectorClass.inject(ErrorService);
    if (!tracking || !errorService) {
        return;
    }
    let event = (options.event || {});
    if (options.provide) {
        event = { ...event, ...options.provide(args, instance) };
    }
    tracking.trackEvent(event).catch((error) => errorService.add({ label: 'TrackMethod.doTracking()', ...error, error }));
};
/**
 * Method tracking for trigger based events.
 * If the decorated method is returning an error, tracking is skipped.
 */
function TrackMethod(options) {
    if (!options.event && !options.provide) {
        console.warn('When using @TrackMethod, provide at least one option to determine the event data.');
    }
    return (_target, _propertyKey, descriptor) => {
        const originalMethod = descriptor.value;
        descriptor.value = function (...args) {
            try {
                const methodReturn = originalMethod.apply(this, args);
                if (methodReturn instanceof Promise) {
                    return methodReturn.then(() => doTracking(options, args, this));
                }
                if (methodReturn instanceof Observable) {
                    return methodReturn.pipe(tap(() => doTracking(options, args, this)));
                }
                doTracking(options, args, this);
                return methodReturn;
            }
            catch (exception) { }
        };
        return descriptor;
    };
}

/**
 * Class decorator for tracking pages
 */
function TrackPageView(options) {
    return (constructor) => {
        /**
         * Subject to manage unsubscription
         */
        let destroyed = new Subject();
        const originalPageEnterEvent = constructor.prototype.ngOnInit;
        const originalPageExitEvent = constructor.prototype.ngOnDestroy;
        constructor.prototype.ngOnInit = function (...args) {
            destroyed = new Subject();
            const tracking = InjectorClass.inject(TrackingFactory);
            const errorService = InjectorClass.inject(ErrorService);
            const urlHelper = InjectorClass.inject(UrlHelperService);
            if (!tracking || !errorService || !urlHelper) {
                return;
            }
            options
                .provide(this)
                .pipe(takeUntil(destroyed), catchError((error) => throwError(() => errorService.add({ label: 'TrackPage.ngOnInit()', ...error, error: error }))))
                .subscribe((page) => tracking.trackPage({
                ...page,
                url: page.url || urlHelper.enforceAbsoluteUrl(),
            }));
            originalPageEnterEvent && typeof originalPageEnterEvent === 'function' && originalPageEnterEvent.apply(this, args);
        };
        constructor.prototype.ngOnDestroy = function (...args) {
            destroyed.next();
            destroyed.complete();
            originalPageExitEvent && typeof originalPageExitEvent === 'function' && originalPageExitEvent.apply(this, args);
        };
    };
}

/**
 * Event tracking for page based events.
 */
function TrackClass(options) {
    return (constructor) => {
        /**
         * Subject to manage unsubscription
         */
        let destroyed = new Subject();
        const initializer = options.initializer || 'ngOnInit';
        const originalPageEnterEvent = constructor.prototype[initializer];
        const originalPageExitEvent = constructor.prototype.ngOnDestroy;
        constructor.prototype[initializer] = function (...args) {
            destroyed = new Subject();
            const tracking = InjectorClass.inject(TrackingFactory);
            const errorService = InjectorClass.inject(ErrorService);
            if (!tracking || !errorService) {
                return;
            }
            options
                .provide(this)
                .pipe(takeUntil(destroyed), catchError((error) => throwError(() => errorService.add({
                label: 'TrackClass.ngOnInit()',
                ...error,
                error: error,
            }))))
                .subscribe((event) => tracking.trackEvent(event));
            originalPageEnterEvent && typeof originalPageEnterEvent === 'function' && originalPageEnterEvent.apply(this, args);
        };
        constructor.prototype.ngOnDestroy = function (...args) {
            destroyed.next();
            destroyed.complete();
            originalPageExitEvent && typeof originalPageExitEvent === 'function' && originalPageExitEvent.apply(this, args);
        };
    };
}

var AuthStateEnum;
(function (AuthStateEnum) {
    AuthStateEnum["UNKNOWN"] = "UNKNOWN";
    AuthStateEnum["LOADING"] = "LOADING";
    AuthStateEnum["ANONYMOUS"] = "ANONYMOUS";
    AuthStateEnum["AUTHORIZED"] = "AUTHORIZED";
})(AuthStateEnum || (AuthStateEnum = {}));

class AuthHttpService {
    constructor(http, config) {
        this.http = http;
        this.config = config;
    }
    login(request) {
        return this.http.post(`${this.config.apiUrl}/${HttpEndpointsConstants.login}`, request);
    }
    refresh() {
        return this.http.post(`${this.config.apiUrl}/${HttpEndpointsConstants.refresh}`, {});
    }
    logout() {
        return this.http.post(`${this.config.apiUrl}/${HttpEndpointsConstants.logout}`, {});
    }
    register(registrationData) {
        return this.http.post(`${this.config.apiUrl}/${HttpEndpointsConstants.register}`, registrationData);
    }
    checkVatRegNoExists(vatRegNo) {
        return this.http.get(`${this.config.apiUrl}/${HttpEndpointsConstants.register}/vat-reg-no-check/${vatRegNo}`);
    }
    requestPassword(username) {
        return this.http.post(`${this.config.apiUrl}/${HttpEndpointsConstants.requestPassword}`, { sEmail: username });
    }
    resetPassword(resetPasswordData) {
        return this.http.post(`${this.config.apiUrl}/${HttpEndpointsConstants.resetPassword}`, resetPasswordData);
    }
    changePassword(newPassword) {
        return this.http.post(`${this.config.apiUrl}/${HttpEndpointsConstants.changePassword}`, {
            sNewPassword: newPassword,
        });
    }
}
AuthHttpService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthHttpService, deps: [{ token: i1.HttpClient }, { token: AUTH_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
AuthHttpService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthHttpService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthHttpService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [AUTH_CONFIG]
                }] }]; } });

class AuthStorageService extends SimpleStorageService {
    key() {
        return 'auth_storage';
    }
}
AuthStorageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthStorageService, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
AuthStorageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthStorageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthStorageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

let AuthService = class AuthService {
    constructor(errorService, authHttpService, authStorageService) {
        this.errorService = errorService;
        this.authHttpService = authHttpService;
        this.authStorageService = authStorageService;
        this.token = new BehaviorSubject(undefined);
        this.status = new BehaviorSubject(AuthStateEnum.UNKNOWN);
        this.isLoggedIn$ = this.listen().pipe(map((status) => status === AuthStateEnum.AUTHORIZED), shareReplay());
        this.authUser$ = this.token.pipe(map((authResponse) => authResponse?.oUser));
        this.userAuthorization$ = this.token.pipe(map((authResponse) => authResponse?.oAuthorizations));
    }
    getToken() {
        if (this.isUnknown()) {
            this.restoreState();
        }
        return this.token.asObservable();
    }
    // TODO refactor -> use async listen instead!
    getAuthUser() {
        return this.token.getValue()?.oUser;
    }
    isAuthorized() {
        return this.status.getValue() === AuthStateEnum.AUTHORIZED;
    }
    isUnknown() {
        return this.status.getValue() === AuthStateEnum.UNKNOWN;
    }
    isLoadingState() {
        return this.status.getValue() === AuthStateEnum.UNKNOWN || this.status.getValue() === AuthStateEnum.LOADING;
    }
    isAnonymous() {
        return this.status.getValue() === AuthStateEnum.ANONYMOUS;
    }
    listen() {
        if (this.isUnknown()) {
            this.restoreState();
        }
        return this.status.asObservable();
    }
    async login(username, password) {
        this.status.next(AuthStateEnum.LOADING);
        return firstValueFrom(this.authHttpService.login({ username, password }).pipe(first(), tap((authResponse) => this.token.next(authResponse)), tap((authResponse) => this.authStorageService.set(authResponse)), tap(() => this.status.next(AuthStateEnum.AUTHORIZED)), first(), map(() => AuthStateEnum.AUTHORIZED), catchError((error) => {
            this.errorService.add({ label: 'AuthService.login()', ...error, error });
            this.status.next(AuthStateEnum.ANONYMOUS);
            return throwError(() => error);
        }), untilDestroyed(this)));
    }
    async refresh() {
        this.status.next(AuthStateEnum.LOADING);
        return firstValueFrom(this.authHttpService.refresh().pipe(first(), tap((authResponse) => this.token.next(authResponse)), tap((authResponse) => this.authStorageService.set(authResponse)), tap(() => this.status.next(AuthStateEnum.AUTHORIZED)), map(() => AuthStateEnum.AUTHORIZED), catchError((error) => {
            this.errorService.add({ label: 'AuthService.refresh()', ...error, error });
            this.status.next(AuthStateEnum.ANONYMOUS);
            return throwError(() => error);
        }), untilDestroyed(this)));
    }
    async logout(onlyLocally = false) {
        this.status.next(AuthStateEnum.LOADING);
        return firstValueFrom((onlyLocally ? of(undefined) : this.authHttpService.logout()).pipe(first(), 
        // if an error occurs while logging out remotely, proceed anyway with local token destruction
        catchError((error) => of(this.errorService.add({ label: 'AuthService.logout()', ...error, error }))), tap(() => this.token.next(undefined)), tap(() => this.authStorageService.remove()), tap(() => this.status.next(AuthStateEnum.ANONYMOUS)), first(), map(() => AuthStateEnum.ANONYMOUS), untilDestroyed(this)));
    }
    restoreState() {
        this.status.next(AuthStateEnum.LOADING);
        from(this.authStorageService.get())
            .pipe(first(), tap((authResponse) => this.token.next(authResponse || undefined)), tap((authResponse) => this.status.next(authResponse ? AuthStateEnum.AUTHORIZED : AuthStateEnum.ANONYMOUS)), map(() => this.status.getValue()), untilDestroyed(this))
            .subscribe();
    }
    updateAuthUser(user) {
        return this.authUser$.pipe(filter((user) => !!user), map((authUser) => ({ ...authUser, ...user })), map((user) => ({ ...this.token.getValue(), oUser: user })), switchMap((token) => from(this.authStorageService.set(token))), map((token) => token?.oUser));
    }
    changeAuthUser(user) {
        this.token.next({ ...this.token.getValue(), oUser: user });
    }
    async register(registrationData) {
        return lastValueFrom(this.authHttpService.register(registrationData));
    }
    async checkVatRegNoExists(vatRegNo) {
        return lastValueFrom(this.authHttpService.checkVatRegNoExists(vatRegNo));
    }
    async requestPassword(username) {
        return lastValueFrom(this.authHttpService.requestPassword(username));
    }
    async resetPassword(resetPasswordData) {
        return lastValueFrom(this.authHttpService.resetPassword(resetPasswordData));
    }
    async changePassword(newPassword) {
        return lastValueFrom(this.authHttpService.changePassword(newPassword));
    }
};
AuthService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthService, deps: [{ token: ErrorService }, { token: AuthHttpService }, { token: AuthStorageService }], target: i0.ɵɵFactoryTarget.Injectable });
AuthService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthService, providedIn: 'root' });
__decorate([
    Hook({ id: { type: 'AuthLoginHook' }, returnType: HookReturnType.PROMISE }),
    TrackMethod({
        event: {
            name: 'login',
            payload: { method: 'email' },
        },
    })
], AuthService.prototype, "login", null);
__decorate([
    Hook({ id: { type: 'AuthLogoutHook' }, returnType: HookReturnType.PROMISE })
], AuthService.prototype, "logout", null);
AuthService = __decorate([
    UntilDestroy()
], AuthService);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: ErrorService }, { type: AuthHttpService }, { type: AuthStorageService }]; }, propDecorators: { login: [], logout: [] } });

class BearerTokenAuthInterceptor {
    constructor(config, authService, errorService, router, log, urlHelper) {
        this.config = config;
        this.authService = authService;
        this.errorService = errorService;
        this.router = router;
        this.log = log;
        this.urlHelper = urlHelper;
        // TODO refactor use environment for blacklist
        this.endpointBlacklist = [
            HttpEndpointsConstants.login,
            '/assets/',
            '/es/',
            '/translation/',
            '/cms/',
        ];
        this.endpointRefreshBlacklist = [HttpEndpointsConstants.refresh, HttpEndpointsConstants.logout];
    }
    intercept(request, next) {
        const isNotBlacklisted = request.url.match(this.endpointBlacklist.join('|')) === null;
        if (!isNotBlacklisted || request.method.toLowerCase() === 'options') {
            return next.handle(request);
        }
        const isNotRefreshBlacklisted = request.url.match(this.endpointRefreshBlacklist.join('|')) === null;
        return this.addAuth(request).pipe(first(), mergeMap((authRequest) => next.handle(authRequest)), catchError((errorResponse) => {
            this.log.warn('BearerTokenAuthInterceptor.intercept()', errorResponse);
            if (errorResponse.status === 401 && isNotRefreshBlacklisted) {
                return this.refreshTokenRequest(request, next);
            }
            throw errorResponse;
        }));
    }
    addAuth(request) {
        const headers = request.headers;
        return this.authService.getToken().pipe(
        // tap((token: AuthResponseInterface | undefined) => this.log.warn('addAuth', token)),
        first(), map((token) => request.clone(token?.token ? { headers: headers.set('Authorization', `Bearer ${token.token}`) } : { headers })));
    }
    refreshTokenRequest(request, next) {
        return this.refreshToken(request).pipe(first(), mergeMap((authRequest) => next.handle(authRequest)), catchError((refreshErrorResponse) => {
            // logout when error occurs while refreshing
            this.authService
                .logout(true)
                .then(() => this.router.navigate([this.urlHelper.localizeUrl(this.config.unauthorizedRoute)]))
                .catch((error) => this.errorService.add({
                label: 'BearerTokenAuthInterceptor.refreshTokenRequest()',
                ...error,
                error,
            }));
            throw { ...refreshErrorResponse, status: BearerTokenAuthInterceptor.RETRY_FAILED_STATUS_CODE };
        }));
    }
    refreshToken(request) {
        this.log.warn('refresh', request);
        return from(this.authService.refresh()).pipe(mergeMap(() => this.addAuth(request)));
    }
}
BearerTokenAuthInterceptor.RETRY_FAILED_STATUS_CODE = 429;
BearerTokenAuthInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BearerTokenAuthInterceptor, deps: [{ token: AUTH_CONFIG }, { token: AuthService }, { token: ErrorService }, { token: i2$1.Router }, { token: LogService }, { token: UrlHelperService }], target: i0.ɵɵFactoryTarget.Injectable });
BearerTokenAuthInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BearerTokenAuthInterceptor, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BearerTokenAuthInterceptor, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [AUTH_CONFIG]
                }] }, { type: AuthService }, { type: ErrorService }, { type: i2$1.Router }, { type: LogService }, { type: UrlHelperService }]; } });

var ACCESS_ID;
(function (ACCESS_ID) {
    ACCESS_ID["SHOW_HISTORICAL_DATA"] = "show-historical-data";
    ACCESS_ID["SHOW_USER_MANAGEMENT"] = "show-user-management";
    ACCESS_ID["SHOW_OFFERS"] = "sales-offers";
    ACCESS_ID["SHOW_ORDERS"] = "sales-orders";
    ACCESS_ID["SHOW_CREDIT_NOTES"] = "sales-credit-notes";
    ACCESS_ID["SHOW_BACKORDER"] = "backlog";
})(ACCESS_ID || (ACCESS_ID = {}));
var CRUD_METHOD;
(function (CRUD_METHOD) {
    CRUD_METHOD["CREATE"] = "create";
    CRUD_METHOD["DELETE"] = "delete";
    CRUD_METHOD["UPDATE"] = "update";
    CRUD_METHOD["READ"] = "read";
})(CRUD_METHOD || (CRUD_METHOD = {}));
let AccessService = class AccessService {
    constructor(authService) {
        this.authService = authService;
    }
    hasAccess(accessId) {
        if (accessId === undefined) {
            return of(true);
        }
        if (!StringHelper.isString(accessId)) {
            return of(!!accessId);
        }
        return this.authService.authUser$.pipe(first(), switchMap((user) => iif(() => !user, 
        // true
        of(false), 
        // false
        of(this.isUserAuthorized(accessId, user)))));
    }
    isUserAuthorized(accessId, user, method = CRUD_METHOD.READ) {
        if (user !== undefined) {
            if (accessId === ACCESS_ID.SHOW_HISTORICAL_DATA) {
                return user.oP48APIAuthorizationGroup?.shtShowOpenItems === 1;
            }
            else if (accessId === ACCESS_ID.SHOW_USER_MANAGEMENT) {
                if (user.oP48APIAuthorizationGroup?.lngAPIAuthorizationGroupID !== null) {
                    return user.oP48APIAuthorizationGroup?.shtP48ShowUserManagement === 1;
                }
                else {
                    return user.shtP48ShowUserManagement === 1;
                }
            }
            else {
                const endpoint = user.oP48APIAuthorizationGroup?.oP48APIAuthorizationGroupAssignList.find((agl) => agl.sAPIAuthorizationID === accessId);
                if (endpoint) {
                    switch (method) {
                        case CRUD_METHOD.CREATE:
                            return endpoint.bCRUDCreate;
                        case CRUD_METHOD.UPDATE:
                            return endpoint.bCRUDUpdate;
                        case CRUD_METHOD.DELETE:
                            return endpoint.bCRUDDelete;
                        case CRUD_METHOD.READ:
                            return endpoint.bCRUDRead;
                    }
                }
                return false;
            }
        }
        return false;
    }
};
AccessService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AccessService, deps: [{ token: AuthService }], target: i0.ɵɵFactoryTarget.Injectable });
AccessService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AccessService, providedIn: 'root' });
AccessService = __decorate([
    UntilDestroy()
], AccessService);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AccessService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: AuthService }]; } });

class HasAccessPipe {
    constructor(accessService) {
        this.accessService = accessService;
    }
    transform(id) {
        return this.accessService.hasAccess(id);
    }
}
HasAccessPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: HasAccessPipe, deps: [{ token: AccessService }], target: i0.ɵɵFactoryTarget.Pipe });
HasAccessPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: HasAccessPipe, name: "hasAccess" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: HasAccessPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'hasAccess',
                }]
        }], ctorParameters: function () { return [{ type: AccessService }]; } });

class AuthModule {
    static forRoot(config) {
        return {
            ngModule: AuthModule,
            providers: [
                {
                    provide: AUTH_CONFIG,
                    useValue: config,
                },
            ],
        };
    }
}
AuthModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AuthModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: AuthModule, declarations: [HasAccessPipe], imports: [CommonModule], exports: [HasAccessPipe] });
AuthModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthModule, providers: [{ provide: HTTP_INTERCEPTORS, useClass: BearerTokenAuthInterceptor, multi: true }], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [HasAccessPipe],
                    exports: [HasAccessPipe],
                    providers: [{ provide: HTTP_INTERCEPTORS, useClass: BearerTokenAuthInterceptor, multi: true }],
                }]
        }] });

let AnonymousGuard = class AnonymousGuard {
    constructor(config, authService, router, urlHelper) {
        this.config = config;
        this.authService = authService;
        this.router = router;
        this.urlHelper = urlHelper;
    }
    /**
     * Check navigation
     * Disallow: Authorized/Logged In
     * Allow: Anonymous
     * Allow: On error
     */
    canActivate() {
        return this.check();
    }
    canActivateChild() {
        return this.check();
    }
    check() {
        return this.authService.listen().pipe(filter(() => !this.authService.isLoadingState()), first(), untilDestroyed(this), map((state) => state === AuthStateEnum.ANONYMOUS), map((allowed) => allowed || this.router.parseUrl(this.urlHelper.localizeUrl(this.config.authorizedRoute))), catchError(() => of(true)));
    }
};
AnonymousGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AnonymousGuard, deps: [{ token: AUTH_CONFIG }, { token: AuthService }, { token: i2$1.Router }, { token: UrlHelperService }], target: i0.ɵɵFactoryTarget.Injectable });
AnonymousGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AnonymousGuard, providedIn: 'root' });
AnonymousGuard = __decorate([
    UntilDestroy()
], AnonymousGuard);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AnonymousGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [AUTH_CONFIG]
                }] }, { type: AuthService }, { type: i2$1.Router }, { type: UrlHelperService }]; } });

let AuthorizedGuard = class AuthorizedGuard {
    constructor(config, authService, router, urlHelper) {
        this.config = config;
        this.authService = authService;
        this.router = router;
        this.urlHelper = urlHelper;
    }
    /**
     * Check navigation
     * Disallow: Anonymous
     * Allow: Authorized
     * Allow: On error
     */
    canActivate(next, state) {
        return this.check(next, state);
    }
    canActivateChild() {
        return this.check();
    }
    check(_, state) {
        return this.authService.listen().pipe(filter(() => !this.authService.isLoadingState()), first(), untilDestroyed(this), map((state) => state === AuthStateEnum.AUTHORIZED), map((allowed) => {
            if (allowed)
                return allowed;
            const unAuthorizedUrl = this.urlHelper.localizeUrl(this.config.unauthorizedRoute);
            if (state?.url) {
                const currentUrl = encodeURIComponent(state?.url);
                return this.router.parseUrl(`${unAuthorizedUrl}?redirect=${currentUrl}`);
            }
            return this.router.parseUrl(unAuthorizedUrl);
        }), catchError(() => of(true)));
    }
};
AuthorizedGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthorizedGuard, deps: [{ token: AUTH_CONFIG }, { token: AuthService }, { token: i2$1.Router }, { token: UrlHelperService }], target: i0.ɵɵFactoryTarget.Injectable });
AuthorizedGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthorizedGuard, providedIn: 'root' });
AuthorizedGuard = __decorate([
    UntilDestroy()
], AuthorizedGuard);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: AuthorizedGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [AUTH_CONFIG]
                }] }, { type: AuthService }, { type: i2$1.Router }, { type: UrlHelperService }]; } });

/**
 * To use this guard, define the accessId within the data attribute of the route
 *
 * Example:
 *
 * ```
 *       {
 *         path: 'carts',
 *         canActivate: [AuthorizedGuard, HasAccessGuard],
 *         data: { accessId: 'all-carts' },
 *         loadChildren: () => import('./profile-features/carts/carts.module').then((m) => m.CartsModule),
 *       },
 * ```
 */
let HasAccessGuard = class HasAccessGuard {
    constructor(config, accessService, router, urlHelper) {
        this.config = config;
        this.accessService = accessService;
        this.router = router;
        this.urlHelper = urlHelper;
    }
    /**
     * Check navigation
     * Disallow: User has no access rights
     * Allow: User has access rights
     * Allow: On error
     */
    canActivate(route) {
        return this.check(route);
    }
    canActivateChild(route) {
        return this.check(route);
    }
    check(route) {
        const id = route.data['accessId'];
        return this.accessService.hasAccess(id).pipe(first(), map((allowed) => allowed || this.router.parseUrl(this.urlHelper.localizeUrl(this.config.authorizedRoute))), untilDestroyed(this), catchError(() => of(true)));
    }
};
HasAccessGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: HasAccessGuard, deps: [{ token: AUTH_CONFIG }, { token: AccessService }, { token: i2$1.Router }, { token: UrlHelperService }], target: i0.ɵɵFactoryTarget.Injectable });
HasAccessGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: HasAccessGuard, providedIn: 'root' });
HasAccessGuard = __decorate([
    UntilDestroy()
], HasAccessGuard);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: HasAccessGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [AUTH_CONFIG]
                }] }, { type: AccessService }, { type: i2$1.Router }, { type: UrlHelperService }]; } });

class BreadcrumbsResolver {
    resolve(route, _state) {
        const data = route.data;
        if (!data) {
            return of([]);
        }
        const path = this.getFullPath(route);
        return of([{ label: data['breadcrumbs'], translate: data['translate'], url: path }]);
    }
    getFullPath(route) {
        return this.fetchFullPath(route.pathFromRoot);
    }
    fetchFullPath(routes) {
        return routes.reduce((path, route) => path + this.fetchRelativePath(route.url), '');
    }
    fetchRelativePath(urlSegments) {
        return urlSegments.reduce((path, urlSegment) => path + '/' + urlSegment.path, '');
    }
}

let BreadcrumbsService = class BreadcrumbsService {
    constructor(router, injector, activatedRoute) {
        this.router = router;
        this.injector = injector;
        this.activatedRoute = activatedRoute;
        // Subject emitting the breadcrumb hierarchy
        this._breadcrumbs$ = new BehaviorSubject([]);
        this.defaultResolver = new BreadcrumbsResolver();
        // Observable exposing the breadcrumb hierarchy
        this.breadcrumbs$ = this._breadcrumbs$.asObservable();
        this.router.events
            .pipe(
        // Filter the NavigationEnd events as the breadcrumb is updated only when the route reaches its end
        filter((event) => event instanceof NavigationEnd), map(() => this.activatedRoute), switchMap((route) => this.resolveBreadcrumb(route)), tap((breadcrumbs) => this._breadcrumbs$.next(breadcrumbs)), untilDestroyed(this))
            .subscribe();
    }
    resolveBreadcrumb(route) {
        let result$ = of([]);
        const data = route.snapshot.data;
        if (data && data['breadcrumbs']) {
            let resolver;
            if (data['breadcrumbs'].prototype instanceof BreadcrumbsResolver) {
                resolver = this.injector.get(data['breadcrumbs']);
            }
            else {
                resolver = this.defaultResolver;
            }
            result$ = resolver.resolve(route.snapshot, this.router.routerState.snapshot);
        }
        if (route.firstChild) {
            result$ = concat(result$, this.resolveBreadcrumb(route.firstChild));
        }
        return result$;
    }
};
BreadcrumbsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BreadcrumbsService, deps: [{ token: i2$1.Router }, { token: i0.Injector }, { token: i2$1.ActivatedRoute }], target: i0.ɵɵFactoryTarget.Injectable });
BreadcrumbsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BreadcrumbsService, providedIn: 'root' });
BreadcrumbsService = __decorate([
    UntilDestroy()
], BreadcrumbsService);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BreadcrumbsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i2$1.Router }, { type: i0.Injector }, { type: i2$1.ActivatedRoute }]; } });

/**
 * A factory function that creates CartItem
 */
function createCartItem({ decQuantity = 0, decQuantityDelivered = 0, decQuantityOrdered = 0, sArticleID = '', sArticleName = '', sItemText = '', lngOrderID = 0, shtFixedItemID = 0, oArticle = undefined, decPrice = 0, }) {
    const fakeId = [lngOrderID, shtFixedItemID].join(',');
    return {
        fakeId,
        decQuantity,
        decQuantityDelivered,
        decQuantityOrdered,
        sArticleID,
        sArticleName,
        sItemText,
        lngOrderID,
        shtFixedItemID,
        oArticle,
        decPrice,
    };
}

function createCartHeader({ lngCustomerID = 0, lngContactID = 0, sCartName = 'Cart', sIContact = '' }) {
    return {
        lngCustomerID,
        lngContactID,
        sCartName,
        sIContact,
    };
}

let CartItemStore = class CartItemStore extends EntityStore {
    constructor() {
        super();
    }
    akitaPreAddEntity(x) {
        return {
            ...x,
            fakeId: [x.lngOrderID, x.shtFixedItemID].join(','),
        };
    }
};
CartItemStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartItemStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CartItemStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartItemStore, providedIn: 'root' });
CartItemStore = __decorate([
    StoreConfig({ name: 'cart-items', idKey: 'fakeId', resettable: true })
], CartItemStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartItemStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class CartItemQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
    selectCartItems(cartId) {
        return this.selectAll({
            filterBy: ({ lngOrderID }) => lngOrderID === cartId,
        });
    }
}
CartItemQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartItemQuery, deps: [{ token: CartItemStore }], target: i0.ɵɵFactoryTarget.Injectable });
CartItemQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartItemQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartItemQuery, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: CartItemStore }]; } });

let CartHeaderStore = class CartHeaderStore extends EntityStore {
    constructor() {
        super();
    }
};
CartHeaderStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartHeaderStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CartHeaderStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartHeaderStore, providedIn: 'root' });
CartHeaderStore = __decorate([
    StoreConfig({ name: 'cart-header', idKey: 'lngOrderID', resettable: true })
], CartHeaderStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartHeaderStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class CartHeaderQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
}
CartHeaderQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartHeaderQuery, deps: [{ token: CartHeaderStore }], target: i0.ɵɵFactoryTarget.Injectable });
CartHeaderQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartHeaderQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartHeaderQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: CartHeaderStore }]; } });

let ShippingTypeStore = class ShippingTypeStore extends EntityStore {
    constructor() {
        super();
    }
};
ShippingTypeStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ShippingTypeStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ShippingTypeStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ShippingTypeStore, providedIn: 'root' });
ShippingTypeStore = __decorate([
    StoreConfig({ name: 'shipping-types', idKey: 'shtShippingTypeID' })
], ShippingTypeStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ShippingTypeStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class ShippingTypeQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
}
ShippingTypeQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ShippingTypeQuery, deps: [{ token: ShippingTypeStore }], target: i0.ɵɵFactoryTarget.Injectable });
ShippingTypeQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ShippingTypeQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ShippingTypeQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: ShippingTypeStore }]; } });

let CartItemLocalStore = class CartItemLocalStore extends EntityStore {
    constructor() {
        super();
    }
    akitaPreAddEntity(x) {
        return {
            ...x,
            fakeId: [x.lngOrderID, x.shtFixedItemID].join(','),
        };
    }
};
CartItemLocalStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartItemLocalStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CartItemLocalStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartItemLocalStore, providedIn: 'root' });
CartItemLocalStore = __decorate([
    StoreConfig({ name: 'cart-items-local', idKey: 'fakeId', resettable: true })
], CartItemLocalStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartItemLocalStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class CartItemLocalQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
}
CartItemLocalQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartItemLocalQuery, deps: [{ token: CartItemLocalStore }], target: i0.ɵɵFactoryTarget.Injectable });
CartItemLocalQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartItemLocalQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartItemLocalQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: CartItemLocalStore }]; } });

const CART_CONFIG = new InjectionToken('CartConfig');

let CartHeaderLocalStore = class CartHeaderLocalStore extends EntityStore {
    constructor() {
        super();
    }
};
CartHeaderLocalStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartHeaderLocalStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CartHeaderLocalStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartHeaderLocalStore, providedIn: 'root' });
CartHeaderLocalStore = __decorate([
    StoreConfig({ name: 'cart-header-local', idKey: 'lngOrderID', resettable: true })
], CartHeaderLocalStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartHeaderLocalStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class CartHeaderLocalQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
}
CartHeaderLocalQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartHeaderLocalQuery, deps: [{ token: CartHeaderLocalStore }], target: i0.ɵɵFactoryTarget.Injectable });
CartHeaderLocalQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartHeaderLocalQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartHeaderLocalQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: CartHeaderLocalStore }]; } });

class CartLocalService {
    constructor(cartItemLocalQuery, cartItemLocalStore, cartHeaderLocalQuery, cartHeaderLocalStore, locale) {
        this.cartItemLocalQuery = cartItemLocalQuery;
        this.cartItemLocalStore = cartItemLocalStore;
        this.cartHeaderLocalQuery = cartHeaderLocalQuery;
        this.cartHeaderLocalStore = cartHeaderLocalStore;
        this.locale = locale;
    }
    changeActiveCart(_) {
        const header$ = this.cartHeaderLocalQuery.selectEntity(CartLocalService.ACTIVE_CART_ID);
        const items$ = this.cartItemLocalQuery.selectAll().pipe(map((x) => x.filter((i) => i.lngOrderID == CartLocalService.ACTIVE_CART_ID)));
        return combineLatest([header$, items$]).pipe(switchMap(([header, items]) => {
            const cart = { oSalesMaster: header, oSalesItemList: items };
            return of(cart);
        }));
    }
    createCartHeader(cartHeader) {
        return of(cartHeader).pipe(tap((header) => {
            const cart = { ...header, lngOrderID: CartLocalService.ACTIVE_CART_ID };
            cart.sCartName ?? (cart.sCartName = formatDate(new Date(), 'dd.MM.yyyy HH:mm', this.locale));
            this.cartHeaderLocalStore.add(cart);
        }));
    }
    createCartItem(cartItem, _) {
        const list = (this.cartItemLocalQuery.getAll() || []).sort((c1, c2) => c2.shtFixedItemID - c1.shtFixedItemID);
        const itemId = (list[0]?.shtFixedItemID ?? 0) + 1;
        return of(undefined).pipe(switchMap(() => of(createCartItem({
            oArticle: cartItem.oArticle,
            decQuantity: cartItem.decQuantity,
            decQuantityDelivered: cartItem.decQuantity,
            decQuantityOrdered: cartItem.decQuantity,
            sArticleID: cartItem.sArticleID,
            sArticleName: cartItem.sArticleName,
            sItemText: cartItem.sItemText,
            lngOrderID: CartLocalService.ACTIVE_CART_ID,
            shtFixedItemID: itemId,
            decPrice: cartItem.oArticle.oPriceInfo?.decPriceNet,
        }))), tap((newCartItem) => this.cartItemLocalStore.add(newCartItem)), switchMap(() => of({
            oSalesMaster: this.cartHeaderLocalQuery.getEntity(CartLocalService.ACTIVE_CART_ID),
            oSalesItemList: this.cartItemLocalQuery.getAll(),
        })));
    }
    deleteCart(lngOrderID) {
        return of(undefined).pipe(tap(() => this.cartHeaderLocalStore.remove(lngOrderID)));
    }
    deleteCartItem(cartItem) {
        return of(cartItem).pipe(tap(() => this.cartItemLocalStore.remove(cartItem.fakeId)), switchMap(() => of({
            oSalesMaster: this.cartHeaderLocalQuery.getEntity(CartLocalService.ACTIVE_CART_ID),
            oSalesItemList: this.cartItemLocalQuery.getAll(),
        })));
    }
    getCartById(_) {
        const cart = {
            ...this.cartHeaderLocalQuery.getEntity(CartLocalService.ACTIVE_CART_ID),
            lngOrderID: CartLocalService.ACTIVE_CART_ID,
        };
        cart.sCartName ?? (cart.sCartName = formatDate(new Date(), 'dd.MM.yyyy HH:mm', this.locale));
        this.cartHeaderLocalStore.upsert(CartLocalService.ACTIVE_CART_ID, cart);
        return of({
            oSalesMaster: this.cartHeaderLocalQuery.getEntity(CartLocalService.ACTIVE_CART_ID),
            oSalesItemList: this.cartItemLocalQuery.getAll(),
        });
    }
    getCartHeaders() {
        return this.cartHeaderLocalQuery.selectEntity(CartLocalService.ACTIVE_CART_ID).pipe(filter((x) => !!x), map((a) => [a]));
    }
    submitCart() {
        return of(undefined);
    }
    updateCartHeader(cartHeader) {
        return of(cartHeader).pipe(tap(() => this.cartHeaderLocalStore.update(CartLocalService.ACTIVE_CART_ID, cartHeader)), switchMap(() => of({
            oSalesMaster: this.cartHeaderLocalQuery.getEntity(CartLocalService.ACTIVE_CART_ID),
            oSalesItemList: this.cartItemLocalQuery.getAll(),
        })));
    }
    updateCartItem(cartItem) {
        return of(cartItem).pipe(tap(() => this.cartItemLocalStore.update(cartItem.fakeId, { ...cartItem, decQuantityOrdered: cartItem.decQuantity })), switchMap(() => of({
            oSalesMaster: this.cartHeaderLocalQuery.getEntity(CartLocalService.ACTIVE_CART_ID),
            oSalesItemList: this.cartItemLocalQuery.getAll(),
        })));
    }
    setActiveId(_) {
        return of(CartLocalService.ACTIVE_CART_ID);
    }
    reorderOrder(_1, _2) {
        return of(undefined);
    }
    printCart(_) {
        return of(undefined);
    }
}
CartLocalService.ACTIVE_CART_ID = 1;
CartLocalService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartLocalService, deps: [{ token: CartItemLocalQuery }, { token: CartItemLocalStore }, { token: CartHeaderLocalQuery }, { token: CartHeaderLocalStore }, { token: LOCALE_ID }], target: i0.ɵɵFactoryTarget.Injectable });
CartLocalService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartLocalService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartLocalService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: CartItemLocalQuery }, { type: CartItemLocalStore }, { type: CartHeaderLocalQuery }, { type: CartHeaderLocalStore }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [LOCALE_ID]
                }] }]; } });

class CartHttpService {
    constructor(http, cartConfig) {
        this.http = http;
        this.cartConfig = cartConfig;
    }
    getCartHeaders() {
        return this.http.get(this.cartConfig.apiUrl + '/so-carts');
    }
    getCartById(cartId) {
        return this.http.get(this.cartConfig.apiUrl + '/so-carts/' + cartId);
    }
    createCartHeader(cartHeader) {
        return this.http.post(this.cartConfig.apiUrl + '/so-carts/', cartHeader);
    }
    updateCartHeader(cartHeader) {
        return this.http.put(this.cartConfig.apiUrl + '/so-carts/' + cartHeader.lngOrderID, {
            oSalesMaster: cartHeader,
            oSalesItemList: null,
        });
    }
    deleteCart(cartId) {
        return this.http.delete(this.cartConfig.apiUrl + '/so-carts/' + cartId);
    }
    submitCart(header, _, __, createOffer = false) {
        let params = new HttpParams();
        if (createOffer)
            params = params.set('submit-mode', 'offer');
        return this.http.put(this.cartConfig.apiUrl + '/so-carts/' + header.lngOrderID + '/submit', {}, { params });
    }
    changeActiveCart(cartId) {
        return this.http.put(this.cartConfig.apiUrl + '/so-carts/' + cartId + '/activate', {});
    }
    createCartItem(cartItem, cartId) {
        return this.http.post(this.cartConfig.apiUrl + '/so-carts/' + cartId + '/items', cartItem);
    }
    updateCartItem(cartItem) {
        return this.http.put(this.cartConfig.apiUrl + '/so-carts/' + cartItem.lngOrderID + '/items/' + cartItem.shtFixedItemID, cartItem);
    }
    deleteCartItem(cartItem) {
        return this.http.delete(this.cartConfig.apiUrl + '/so-carts/' + cartItem.lngOrderID + '/items/' + cartItem.shtFixedItemID);
    }
    setActiveId(user) {
        return of(user ? user.lngActiveCartID : -1);
    }
    reorder(lngOrderID, sOrderType) {
        return this.http.post(this.cartConfig.apiUrl + '/so-carts/reorder', {
            lngOrderID,
            sOrderType,
        });
    }
    printCart(cartHeader) {
        const acceptHeaders = new HttpHeaders();
        acceptHeaders.append('Accept', '*/*');
        acceptHeaders.append('Accept-Encoding', 'gzip, deflate, br');
        acceptHeaders.append('Connection', 'keep-alive');
        return this.http.get(this.cartConfig.apiUrl +
            `/sales-documents?sDocumentType=O&lngDocumentID=${cartHeader.lngOrderID}&sOrderType=${cartHeader.sOrderType}`, {
            headers: acceptHeaders,
            responseType: 'blob',
        });
    }
}
CartHttpService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartHttpService, deps: [{ token: i1.HttpClient }, { token: CART_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
CartHttpService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartHttpService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartHttpService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CART_CONFIG]
                }] }]; } });

class CartGuestService {
    constructor(http, cartConfig) {
        this.http = http;
        this.cartConfig = cartConfig;
    }
    changeActiveCart(_) {
        return of(undefined);
    }
    createCartHeader(cartHeader) {
        return of(cartHeader);
    }
    createCartItem(cartItem, _) {
        return this.http.post(this.cartConfig.apiUrl + '/so-carts/-1/items', cartItem).pipe(map((cart) => {
            const guestMaster = { ...cart.oSalesMaster, lngOrderID: -1 };
            const guestSalesItemList = cart.oSalesItemList.map((si) => {
                return { ...si, lngOrderID: -1 };
            });
            return { oSalesMaster: guestMaster, oSalesItemList: guestSalesItemList };
        }));
    }
    deleteCart(_) {
        return of(undefined);
    }
    deleteCartItem(cartItem) {
        return this.http
            .delete(this.cartConfig.apiUrl + '/so-carts/' + cartItem.lngOrderID + '/items/' + cartItem.shtFixedItemID)
            .pipe(map((cart) => {
            const guestMaster = { ...cart.oSalesMaster, lngOrderID: -1 };
            const guestSalesItemList = cart.oSalesItemList.map((si) => {
                return { ...si, lngOrderID: -1 };
            });
            return { oSalesMaster: guestMaster, oSalesItemList: guestSalesItemList };
        }));
    }
    getCartById(id) {
        return this.http.get(this.cartConfig.apiUrl + '/so-carts/' + id).pipe(map((cart) => {
            const guestMaster = { ...cart.oSalesMaster, lngOrderID: -1 };
            const guestSalesItemList = cart.oSalesItemList?.map((si) => {
                return { ...si, lngOrderID: -1 };
            });
            return { oSalesMaster: guestMaster, oSalesItemList: guestSalesItemList };
        }));
    }
    getCartHeaders() {
        return this.http.get(this.cartConfig.apiUrl + '/so-carts').pipe(map((carts) => {
            return carts.map((cart) => {
                return { ...cart, lngOrderID: -1 };
            });
        }));
    }
    setActiveId(_) {
        return of(-1);
    }
    submitCart(_) {
        return of(undefined);
    }
    updateCartHeader(cartHeader) {
        return this.http
            .put(this.cartConfig.apiUrl + '/so-carts/' + cartHeader.lngOrderID, {
            oSalesMaster: cartHeader,
            oSalesItemList: null,
        })
            .pipe(map((cart) => {
            const guestMaster = { ...cart.oSalesMaster, lngOrderID: -1 };
            const guestSalesItemList = cart.oSalesItemList.map((si) => {
                return { ...si, lngOrderID: -1 };
            });
            return { oSalesMaster: guestMaster, oSalesItemList: guestSalesItemList };
        }));
    }
    updateCartItem(cartItem) {
        return this.http.put(this.cartConfig.apiUrl + '/so-carts/-1/items/' + cartItem.shtFixedItemID, cartItem).pipe(map((cart) => {
            const guestMaster = { ...cart.oSalesMaster, lngOrderID: -1 };
            const guestSalesItemList = cart.oSalesItemList.map((si) => {
                return { ...si, lngOrderID: -1 };
            });
            return { oSalesMaster: guestMaster, oSalesItemList: guestSalesItemList };
        }));
    }
    printCart(cartHeader) {
        const acceptHeaders = new HttpHeaders();
        acceptHeaders.append('Accept', '*/*');
        acceptHeaders.append('Accept-Encoding', 'gzip, deflate, br');
        acceptHeaders.append('Connection', 'keep-alive');
        return this.http.get(this.cartConfig.apiUrl +
            `/sales-documents?sDocumentType=O&lngDocumentID=${cartHeader.lngOrderID}&sOrderType=${cartHeader.sOrderType}`, {
            headers: acceptHeaders,
            responseType: 'blob',
        });
    }
}
CartGuestService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartGuestService, deps: [{ token: i1.HttpClient }, { token: CART_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
CartGuestService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartGuestService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartGuestService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CART_CONFIG]
                }] }]; } });

let RoleStore = class RoleStore extends EntityStore {
    constructor() {
        super();
    }
};
RoleStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RoleStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
RoleStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RoleStore, providedIn: 'root' });
RoleStore = __decorate([
    StoreConfig({ name: 'roles', idKey: 'lngAPIAuthorizationGroupID' })
], RoleStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RoleStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class RoleQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
}
RoleQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RoleQuery, deps: [{ token: RoleStore }], target: i0.ɵɵFactoryTarget.Injectable });
RoleQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RoleQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RoleQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: RoleStore }]; } });

class RoleNamePipePipe {
    /**
     * Creates an instance of role name pipe.
     * @param roleQuery the query used to get role names
     */
    constructor(roleQuery) {
        this.roleQuery = roleQuery;
    }
    /**
     * Transforms a role id in a role name
     * @param value the role id value
     * @returns role name
     */
    transform(value) {
        return this.roleQuery.selectAll().pipe(map((roles) => {
            if (roles.length > 0 && value) {
                return roles.find((role) => role.lngAPIAuthorizationGroupID === value).sRoleName;
            }
            else {
                return '';
            }
        }));
    }
}
RoleNamePipePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RoleNamePipePipe, deps: [{ token: RoleQuery }], target: i0.ɵɵFactoryTarget.Pipe });
RoleNamePipePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: RoleNamePipePipe, name: "role" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RoleNamePipePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'role',
                }]
        }], ctorParameters: function () { return [{ type: RoleQuery }]; } });

class UserModule {
}
UserModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UserModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
UserModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: UserModule, declarations: [RoleNamePipePipe], imports: [CommonModule], exports: [RoleNamePipePipe] });
UserModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UserModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UserModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [RoleNamePipePipe],
                    exports: [RoleNamePipePipe],
                }]
        }] });

let UserStore = class UserStore extends EntityStore {
    constructor() {
        super();
    }
};
UserStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UserStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
UserStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UserStore, providedIn: 'root' });
UserStore = __decorate([
    StoreConfig({
        name: 'users',
        idKey: 'lngContactID',
        resettable: true,
    })
], UserStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UserStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

let UserQuery = class UserQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
};
UserQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UserQuery, deps: [{ token: UserStore }], target: i0.ɵɵFactoryTarget.Injectable });
UserQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UserQuery, providedIn: 'root' });
UserQuery = __decorate([
    QueryConfig({ sortBy: 'sFirstName', sortByOrder: Order.ASC })
], UserQuery);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UserQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: UserStore }]; } });

class UserService extends NgEntityService {
    constructor(store) {
        super(store);
        this.store = store;
    }
    getAll() {
        return super.get({ mapResponseFn: (res) => res.data });
    }
}
UserService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UserService, deps: [{ token: UserStore }], target: i0.ɵɵFactoryTarget.Injectable });
UserService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UserService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UserService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: UserStore }]; } });

/**
 * A service to perform CRUD-operations on user entities
 */
class UserFilterService extends AkitaFiltersPlugin {
    constructor(store, userQuery, userService) {
        super(userQuery);
        this.store = store;
        this.userQuery = userQuery;
        this.userService = userService;
        this._total$ = new BehaviorSubject(0);
        this.withServer(this.getOnChangeFilter());
    }
    get total$() {
        return this._total$.asObservable();
    }
    getOnChangeFilter() {
        return (filtersNormalized) => {
            const filtersObject = filtersNormalized;
            return this.userService.get({
                params: filtersObject,
                mapResponseFn: (res) => {
                    this._total$.next(Number(res.total));
                    return res.data;
                },
                observe: 'response', // <----- this
            });
        };
    }
    /**
     * Sets orderBy
     */
    setOrderBy(by, order) {
        this.setSortBy({
            sortBy: by,
            sortByOrder: order === '+' ? Order.ASC : Order.DESC,
        });
    }
}
UserFilterService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UserFilterService, deps: [{ token: UserStore }, { token: UserQuery }, { token: UserService }], target: i0.ɵɵFactoryTarget.Injectable });
UserFilterService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UserFilterService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: UserFilterService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: UserStore }, { type: UserQuery }, { type: UserService }]; } });

function createUser({ lngContactID = undefined, dtLastLogin = undefined, dtRegistration = new Date().valueOf(), lngCustomerID = undefined, shtRoleID = 0, sEmail = '', sFirstName = '', sFormOfAdress = '', sLastName = '', sMobilePhone = '', sPhone = '', sUserName = '', shtLanguageID = 1, bActive = true, sLanguageIsoCode = '', gActiveBookmarkListID = undefined, lngActiveCartID = undefined, sP48SalesRepForUser = '', oP48SalesRepForUser = undefined, bP48CustomerHasFactoryAttestationFlat = undefined, bP48CustomerIsAllowedToOrder = undefined, }) {
    return {
        lngContactID,
        dtLastLogin,
        dtRegistration,
        lngCustomerID,
        shtRoleID,
        sEmail,
        sFirstName,
        sFormOfAdress,
        sLastName,
        sMobilePhone,
        sPhone,
        sUserName,
        shtLanguageID,
        bActive,
        sLanguageIsoCode,
        gActiveBookmarkListID,
        lngActiveCartID,
        sP48SalesRepForUser,
        oP48SalesRepForUser,
        bP48CustomerHasFactoryAttestationFlat,
        bP48CustomerIsAllowedToOrder,
    };
}

class RoleService extends NgEntityService {
    constructor(query, store) {
        super(store);
        this.query = query;
        this.store = store;
        this.loading = false;
    }
    get() {
        if (this.loading) {
            return this.query.selectAll();
        }
        this.loading = true;
        return this.getHttp()
            .get('p48apihost/roles')
            .pipe(tap((roles) => this.store.set(roles)), finalize(() => (this.loading = false)));
    }
}
RoleService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RoleService, deps: [{ token: RoleQuery }, { token: RoleStore }], target: i0.ɵɵFactoryTarget.Injectable });
RoleService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RoleService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RoleService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: RoleQuery }, { type: RoleStore }]; } });

function createInitialState$4() {
    return {};
}
let ParameterStore = class ParameterStore extends Store {
    constructor() {
        super(createInitialState$4());
    }
};
ParameterStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ParameterStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ParameterStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ParameterStore, providedIn: 'root' });
ParameterStore = __decorate([
    StoreConfig({ name: 'parameters' })
], ParameterStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ParameterStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class ParameterService {
    constructor(store, http) {
        this.store = store;
        this.http = http;
    }
    get() {
        return this.http.get(`/p48apihost/param/`).pipe(tap((oParameter) => this.store.update({ params: oParameter })));
    }
}
ParameterService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ParameterService, deps: [{ token: ParameterStore }, { token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
ParameterService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ParameterService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ParameterService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: ParameterStore }, { type: i1.HttpClient }]; } });

class ParameterQuery extends Query {
    constructor(store) {
        super(store);
        this.store = store;
        this.params$ = this.select('params').pipe(filter((p) => !!p));
    }
}
ParameterQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ParameterQuery, deps: [{ token: ParameterStore }], target: i0.ɵɵFactoryTarget.Injectable });
ParameterQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ParameterQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ParameterQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: ParameterStore }]; } });

const defaultParameterConfig = {
    apiUrl: '/p48apihost',
};
const PARAMETER_CONFIG = new InjectionToken('ParameterConfig');

function init_remote_params(parameterService) {
    const result = () => parameterService.get().pipe(first()).subscribe();
    return result;
}
class ParameterModule {
    static forRoot(config = {}) {
        return {
            ngModule: ParameterModule,
            providers: [
                {
                    provide: PARAMETER_CONFIG,
                    useValue: { ...defaultParameterConfig, ...config },
                },
            ],
        };
    }
}
ParameterModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ParameterModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ParameterModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: ParameterModule, imports: [CommonModule] });
ParameterModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ParameterModule, providers: [{ provide: APP_INITIALIZER, useFactory: init_remote_params, deps: [ParameterService], multi: true }], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ParameterModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    providers: [{ provide: APP_INITIALIZER, useFactory: init_remote_params, deps: [ParameterService], multi: true }],
                }]
        }] });

// @dynamic
class CartService {
    constructor(transloco, translocoLocale, cartHttpService, cartLocalService, cartGuestService, authService, cartHeaderStore, cartItemStore, cartItemQuery, cartHeaderQuery, http, userService, paramQuery) {
        this.transloco = transloco;
        this.translocoLocale = translocoLocale;
        this.cartHttpService = cartHttpService;
        this.cartLocalService = cartLocalService;
        this.cartGuestService = cartGuestService;
        this.authService = authService;
        this.cartHeaderStore = cartHeaderStore;
        this.cartItemStore = cartItemStore;
        this.cartItemQuery = cartItemQuery;
        this.cartHeaderQuery = cartHeaderQuery;
        this.http = http;
        this.userService = userService;
        this.paramQuery = paramQuery;
    }
    initializeCarts() {
        return this.authService.authUser$.pipe(tap(() => this.clearCartHeader()), switchMap((user) => this.setActiveId(user)), filter((id) => !!id), switchMap((id) => this.getCartById(id).pipe(catchError(() => of(undefined)))), switchMap(() => this.getCartHeaders()));
    }
    getCartHeaders() {
        this.cartHeaderStore.setLoading(true);
        return this.iif(this.cartHttpService.getCartHeaders(), this.cartLocalService.getCartHeaders(), this.cartGuestService.getCartHeaders()).pipe(tap((carts) => this.cartHeaderStore.upsertMany(carts)), finalize(() => this.cartHeaderStore.setLoading(false)));
    }
    getCartById(cartId) {
        return of(undefined).pipe(tap(() => {
            this.cartHeaderStore.setLoading(true);
            this.cartItemStore.setLoading(true);
        }), switchMap(() => this.iif(this.cartHttpService.getCartById(cartId), this.cartLocalService.getCartById(cartId), this.cartGuestService.getCartById(cartId))), tap((cart) => {
            this.cartHeaderStore.upsert(cart.oSalesMaster.lngOrderID, cart.oSalesMaster);
            this.cartItemStore.upsertMany(this.generateCartItemFakeId(cart.oSalesItemList));
        }), catchError((error) => {
            const user = this.authService.getAuthUser();
            if (user) {
                this.activateRecentOrCreateNew(user).pipe(take(1)).subscribe();
            }
            return throwError(() => error);
        }), finalize(() => {
            this.cartHeaderStore.setLoading(false);
            this.cartItemStore.setLoading(false);
        }));
    }
    createCartHeader(cartHeader) {
        return of(undefined).pipe(tap(() => this.cartHeaderStore.setLoading(true)), switchMap(() => this.iif(this.cartHttpService.createCartHeader(cartHeader), this.cartLocalService.createCartHeader(cartHeader), this.cartGuestService.createCartHeader(cartHeader))), tap((cartHeader) => this.cartHeaderStore.add(cartHeader)), finalize(() => this.cartHeaderStore.setLoading(false)));
    }
    updateCartHeader(cartHeader) {
        return of(undefined).pipe(tap(() => {
            this.cartHeaderStore.setLoading(true);
            this.cartItemStore.setLoading(true);
        }), switchMap(() => this.iif(this.cartHttpService.updateCartHeader(cartHeader), this.cartLocalService.updateCartHeader(cartHeader), this.cartGuestService.updateCartHeader(cartHeader))), tap((cart) => {
            if (cart) {
                this.cartHeaderStore.upsert(cart.oSalesMaster.lngOrderID, cart.oSalesMaster);
                this.cartItemStore.upsertMany(this.generateCartItemFakeId(cart.oSalesItemList));
            }
        }), finalize(() => {
            this.cartHeaderStore.setLoading(false);
            this.cartItemStore.setLoading(false);
        }));
    }
    deleteCart(cartId) {
        return of(undefined).pipe(tap(() => {
            this.cartHeaderStore.setLoading(true);
            this.cartItemStore.setLoading(true);
        }), switchMap(() => this.iif(this.cartHttpService.deleteCart(cartId), this.cartLocalService.deleteCart(cartId), this.cartGuestService.deleteCart(cartId))), tap(() => {
            this.cartHeaderStore.remove((header) => header.lngOrderID.toString() === cartId.toString());
            this.cartItemStore.remove((item) => item.lngOrderID.toString() === cartId.toString());
        }), finalize(() => {
            this.cartHeaderStore.setLoading(false);
            this.cartItemStore.setLoading(false);
        }));
    }
    submitCart(header, _, user, createOffer = false) {
        return of(undefined).pipe(tap(() => {
            this.cartHeaderStore.setLoading(true);
            this.cartItemStore.setLoading(true);
        }), switchMap(() => this.iif(this.cartHttpService.submitCart(header, undefined, undefined, createOffer), this.cartLocalService.submitCart(), this.cartGuestService.submitCart(header))), tap(() => {
            this.cartHeaderStore.remove(header.lngOrderID);
            this.cartItemStore.remove((item) => item.lngOrderID === header.lngOrderID);
        }), switchTap(() => this.activateRecentOrCreateNew(user)), finalize(() => {
            this.cartHeaderStore.setLoading(false);
            this.cartItemStore.setLoading(false);
        }));
    }
    changeActiveCart(cartId) {
        return of(undefined).pipe(tap(() => {
            this.cartHeaderStore.setLoading(true);
            this.cartItemStore.setLoading(true);
        }), switchMap(() => this.cartHttpService.changeActiveCart(cartId)), tap((cart) => {
            this.cartHeaderStore.upsert(cart.oSalesMaster.lngOrderID, cart.oSalesMaster);
            this.cartItemStore.upsertMany(this.generateCartItemFakeId(cart.oSalesItemList));
            this.cartHeaderStore.setActive(cart.oSalesMaster.lngOrderID);
        }), switchTap((cart) => this.authService.updateAuthUser({ lngActiveCartID: cart.oSalesMaster.lngOrderID })), finalize(() => {
            this.cartHeaderStore.setLoading(false);
            this.cartItemStore.setLoading(false);
        }));
    }
    createCartItem(cartItem) {
        const activeCartId = this.cartHeaderQuery.getActiveId();
        return of(undefined).pipe(tap(() => {
            this.cartHeaderStore.setLoading(true);
            this.cartItemStore.setLoading(true);
        }), switchMap(() => this.iif(this.cartHttpService.createCartItem(cartItem, activeCartId), this.cartLocalService.createCartItem(cartItem, activeCartId), this.cartGuestService.createCartItem(cartItem, activeCartId))), tap((cart) => {
            this.cartHeaderStore.upsert(cart.oSalesMaster?.lngOrderID, cart.oSalesMaster);
            this.cartItemStore.upsertMany(this.generateCartItemFakeId(cart.oSalesItemList));
        }), first(), finalize(() => {
            this.cartHeaderStore.setLoading(false);
            this.cartItemStore.setLoading(false);
        }));
    }
    updateCartItem(cartItem) {
        return of(undefined).pipe(tap(() => {
            this.cartHeaderStore.setLoading(true);
            this.cartItemStore.setLoading(true);
        }), switchMap(() => this.iif(this.cartHttpService.updateCartItem(cartItem), this.cartLocalService.updateCartItem(cartItem), this.cartGuestService.updateCartItem(cartItem))), tap((cart) => {
            this.cartHeaderStore.upsert(cart.oSalesMaster.lngOrderID, cart.oSalesMaster);
            this.cartItemStore.upsertMany(this.generateCartItemFakeId(cart.oSalesItemList));
        }), finalize(() => {
            this.cartHeaderStore.setLoading(false);
            this.cartItemStore.setLoading(false);
        }));
    }
    deleteCartItem(cartItem) {
        return of(undefined).pipe(tap(() => {
            this.cartHeaderStore.setLoading(true);
            this.cartItemStore.setLoading(true);
        }), switchMap(() => this.iif(this.cartHttpService.deleteCartItem(cartItem), this.cartLocalService.deleteCartItem(cartItem), this.cartGuestService.deleteCartItem(cartItem))), tap((cart) => {
            this.cartItemStore.remove((item) => item.lngOrderID === cart.oSalesMaster.lngOrderID);
            this.cartHeaderStore.upsert(cart.oSalesMaster.lngOrderID, cart.oSalesMaster);
            this.cartItemStore.upsertMany(this.generateCartItemFakeId(cart.oSalesItemList));
        }), finalize(() => {
            this.cartHeaderStore.setLoading(false);
            this.cartItemStore.setLoading(false);
        }));
    }
    setActiveId(user) {
        return of(undefined).pipe(tap(() => this.cartHeaderStore.setLoading(true)), switchMap(() => this.iif(this.cartHttpService.setActiveId(user), this.cartLocalService.setActiveId(user), this.cartGuestService.setActiveId(user))), tap((id) => this.cartHeaderStore.setActive(id)), finalize(() => this.cartHeaderStore.setLoading(false)));
    }
    /**
     * Converts any locally saved cart items to a newly created remote cart
     * and removes the local items in that process
     */
    convertLocalToRemote(_) {
        const end$ = new Subject();
        return this.cartLocalService.getCartById(CartLocalService.ACTIVE_CART_ID).pipe(first(), switchMap((cart) => iif(
        // no or empty cart
        () => !cart?.oSalesItemList?.length, 
        // true
        of(cart), 
        // false
        combineLatest([of(cart), this.createCartHeader(cart.oSalesMaster)]).pipe(switchTap(([_, header]) => this.changeActiveCart(header.lngOrderID)), mergeMap(([cart, _]) => {
            const creationObservables = [];
            creationObservables.push(this.cartLocalService.deleteCart(CartLocalService.ACTIVE_CART_ID));
            for (const cartItem of cart.oSalesItemList) {
                creationObservables.push(this.cartLocalService.deleteCartItem(cartItem));
                creationObservables.push(this.createCartItem({
                    oArticle: cartItem.oArticle,
                    decQuantity: cartItem.decQuantity,
                    sQuantityUnit: cartItem.sQuantityUnit,
                    sArticleID: cartItem.sArticleID,
                    sArticleName: cartItem.sArticleName,
                    sItemText: cartItem.sItemText,
                    emitHook: false,
                }));
            }
            // end stream, so toArray() can work
            creationObservables.push(of(undefined).pipe(tap(() => {
                end$.next(true);
                end$.complete();
            })));
            return merge(creationObservables);
        }), mergeMap(($req) => $req, 1), takeUntil(end$), toArray(), map((carts) => carts[0])))));
    }
    activateRecentOrCreateNew(user) {
        return this.getCartHeaders().pipe(switchMap((list) => iif(
        // user has already carts
        () => list.length > 0, 
        // true
        of(list.sort((a, b) => b.dtEntryDate - a.dtEntryDate)[0]), 
        // false
        this.createEmptyList(user))), switchTap((header) => this.changeActiveCart(header.lngOrderID)));
    }
    clearCartHeader() {
        this.cartHeaderStore.reset();
    }
    acceptOffer(lngOrderID, sOrderType) {
        return this.cartHttpService.reorder(lngOrderID, sOrderType).pipe(filter((cart) => !!cart));
    }
    reorderOrder(lngOrderID, sOrderType) {
        return of(undefined).pipe(tap(() => {
            this.cartHeaderStore.setLoading(true);
            this.cartItemStore.setLoading(true);
        }), switchMap(() => this.cartHttpService.reorder(lngOrderID, sOrderType)), filter((cart) => !!cart), tap((cart) => {
            this.cartHeaderStore.upsert(cart.oSalesMaster.lngOrderID, cart.oSalesMaster);
            this.cartItemStore.upsertMany(this.generateCartItemFakeId(cart.oSalesItemList));
        }), switchTap((cart) => {
            const user = this.authService.getAuthUser();
            return this.userService
                .update(user?.lngContactID, {
                ...user,
                lngActiveCartID: cart.oSalesMaster.lngOrderID,
            })
                .pipe(switchMap((user) => {
                this.cartHeaderStore.setActive(user.lngActiveCartID);
                return this.authService.updateAuthUser({ lngActiveCartID: user.lngActiveCartID });
            }));
        }), finalize(() => {
            this.cartHeaderStore.setLoading(false);
            this.cartItemStore.setLoading(false);
        }));
    }
    createEmptyList(user) {
        // when injecting transloco scope, it is the wrong one, so it is added hard-coded here
        const scope = 'cart';
        return this.transloco
            .selectTranslate('domain.new-cart.name', { date: this.translocoLocale.localizeDate(Date.now(), undefined, { dateStyle: 'medium', timeStyle: 'short' }) }, scope)
            .pipe(switchMap((name) => this.createCartHeader(createCartHeader({
            lngCustomerID: user.lngCustomerID,
            lngContactID: user.lngContactID,
            sCartName: name,
            sIContact: `${user.sFormOfAdress} ${user.sFirstName} ${user.sLastName}`,
        }))));
    }
    generateCartItemFakeId(cartItems = []) {
        return cartItems.map((item) => {
            return { ...item, fakeId: [item.lngOrderID, item.shtFixedItemID].join(',') };
        });
    }
    iif(userCart, localCart, guestCart) {
        return combineLatest([this.authService.authUser$, this.paramQuery.params$]).pipe(filter(([, params]) => !!params), switchMap(([authUser, params]) => (authUser ? userCart : params.sP48GuestCartMode === 'enventa_offer' ? guestCart : localCart)), first());
    }
    printCart(cartHeader) {
        return this.iif(this.cartHttpService.printCart(cartHeader), this.cartLocalService.printCart(cartHeader), this.cartGuestService.printCart(cartHeader)).pipe(filter((res) => !!res), tap((res) => {
            this.saveFileAs(res, `${cartHeader.sCartName}_${cartHeader.lngOrderID.toString()}.pdf`);
        }));
    }
    saveFileAs(fileBlob, fileName) {
        const file = new Blob([fileBlob], { type: 'application/pdf' });
        saveAs(file, fileName);
    }
}
CartService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartService, deps: [{ token: i1$1.TranslocoService }, { token: i2$2.TranslocoLocaleService }, { token: CartHttpService }, { token: CartLocalService }, { token: CartGuestService }, { token: AuthService }, { token: CartHeaderStore }, { token: CartItemStore }, { token: CartItemQuery }, { token: CartHeaderQuery }, { token: i1.HttpClient }, { token: UserService }, { token: ParameterQuery }], target: i0.ɵɵFactoryTarget.Injectable });
CartService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartService, providedIn: 'root' });
__decorate([
    TrackMethod({
        provide: ([header, items]) => {
            return {
                name: 'purchase',
                payload: {
                    ecommerce: {
                        transaction_id: header.lngOrderID.toString(),
                        currency: items[0].oArticle.oPriceInfo?.sCurrencyCode,
                        value: items.reduce((last, current) => last + current.decItemNetAmountFC, 0),
                        items: items.map((item) => ({
                            item_id: item.sArticleID.toString(),
                            item_name: item.sArticleName,
                            price: item.decItemNetAmountFC,
                            ...GoogleTagManagerHelper.hierarchicalCategoriesToItemCategories(item.oArticle.oProductInfo?.length ? item.oArticle.oProductInfo[0].listHierarchicalCategories : []),
                            quantity: item.decQuantityOrdered,
                        })),
                    },
                },
            };
        },
    })
], CartService.prototype, "submitCart", null);
__decorate([
    TrackMethod({
        provide: ([cartItem]) => {
            const quantity = cartItem.decQuantityOrdered || 1;
            return {
                name: 'add_to_cart',
                payload: {
                    ecommerce: {
                        currency: '',
                        value: GoogleTagManagerHelper.articlePrice(cartItem) * quantity,
                        items: [
                            {
                                item_id: cartItem.sArticleID.toString(),
                                item_name: cartItem.sArticleName,
                                price: cartItem.decItemNetAmountFC,
                                ...GoogleTagManagerHelper.hierarchicalCategoriesToItemCategories(cartItem.oArticle.oProductInfo?.length ? cartItem.oArticle.oProductInfo[0].listHierarchicalCategories : []),
                                quantity,
                            },
                        ],
                    },
                },
            };
        },
    }),
    Hook({
        id: { type: 'WorkflowCreateHook', entity: 'CreateCartItemHook' },
        returnType: HookReturnType.OBSERVABLE,
    })
], CartService.prototype, "createCartItem", null);
__decorate([
    Hook({
        id: { type: 'WorkflowUpdateHook', entity: 'UpdateCartItemHook' },
        returnType: HookReturnType.OBSERVABLE,
    })
], CartService.prototype, "updateCartItem", null);
__decorate([
    TrackMethod({
        provide: ([cartItem]) => {
            const quantity = cartItem.decQuantityOrdered || 1;
            return {
                name: 'remove_from_cart',
                payload: {
                    ecommerce: {
                        currency: '',
                        value: GoogleTagManagerHelper.articlePrice(cartItem) * quantity,
                        items: [{ ...GoogleTagManagerHelper.articleToItem(cartItem), quantity }],
                    },
                },
            };
        },
    }),
    Hook({
        id: { type: 'WorkflowDeleteHook', entity: 'DeleteCartItemHook' },
        returnType: HookReturnType.OBSERVABLE,
    })
], CartService.prototype, "deleteCartItem", null);
__decorate([
    Hook({
        id: { type: 'WorkflowCreateHook', entity: 'CreateOrderFromOfferHook' },
        returnType: HookReturnType.OBSERVABLE,
    })
], CartService.prototype, "acceptOffer", null);
__decorate([
    Hook({
        id: { type: 'WorkflowCreateHook', entity: 'CreateCartFromOrderHook' },
        returnType: HookReturnType.OBSERVABLE,
    })
], CartService.prototype, "reorderOrder", null);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1$1.TranslocoService }, { type: i2$2.TranslocoLocaleService }, { type: CartHttpService }, { type: CartLocalService }, { type: CartGuestService }, { type: AuthService }, { type: CartHeaderStore }, { type: CartItemStore }, { type: CartItemQuery }, { type: CartHeaderQuery }, { type: i1.HttpClient }, { type: UserService }, { type: ParameterQuery }]; }, propDecorators: { submitCart: [], createCartItem: [], updateCartItem: [], deleteCartItem: [], acceptOffer: [], reorderOrder: [] } });

class ShippingTypeService {
    constructor(store, http) {
        this.store = store;
        this.http = http;
    }
    getShippingTypes(lngCartID) {
        return this.http.get(`p48apihost/shipping-types/${lngCartID}`).pipe(tap((response) => {
            this.store.set(response);
        }));
    }
}
ShippingTypeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ShippingTypeService, deps: [{ token: ShippingTypeStore }, { token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
ShippingTypeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ShippingTypeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ShippingTypeService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: ShippingTypeStore }, { type: i1.HttpClient }]; } });

class ShippingConditionPipe {
    constructor(shippingTypeQuery, shippingTypeService) {
        this.shippingTypeQuery = shippingTypeQuery;
        this.shippingTypeService = shippingTypeService;
    }
    transform(value, lngOrderID) {
        return of(undefined).pipe(switchMap(() => iif(() => this.shippingTypeQuery.hasEntity(value), this.shippingTypeQuery.selectEntity(value), iif(() => !!lngOrderID, this.shippingTypeService.getShippingTypes(lngOrderID).pipe(switchMap(() => this.shippingTypeQuery.selectEntity(value))), of(undefined)))), map((shippingType) => {
            if (shippingType) {
                return shippingType.sName;
            }
            else {
                return 'Error shipping-Type not found!';
            }
        }));
        /*return this.shippingTypeQuery.selectEntity(value).pipe(
          map((shippingType: ShippingType | undefined) => {
            if (shippingType) {
              return shippingType.sName;
            } else {
              return 'Error shipping-Type not found!';
            }
          }),
        );*/
    }
}
ShippingConditionPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ShippingConditionPipe, deps: [{ token: ShippingTypeQuery }, { token: ShippingTypeService }], target: i0.ɵɵFactoryTarget.Pipe });
ShippingConditionPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: ShippingConditionPipe, name: "shippingCondition" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ShippingConditionPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'shippingCondition',
                }]
        }], ctorParameters: function () { return [{ type: ShippingTypeQuery }, { type: ShippingTypeService }]; } });

class CartModule {
    static forRoot(config) {
        return {
            ngModule: CartModule,
            providers: [
                {
                    provide: CART_CONFIG,
                    useValue: config,
                },
            ],
        };
    }
}
CartModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CartModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: CartModule, declarations: [ShippingConditionPipe], imports: [CommonModule, TranslocoModule], exports: [ShippingConditionPipe] });
CartModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartModule, imports: [CommonModule, TranslocoModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CartModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, TranslocoModule],
                    declarations: [ShippingConditionPipe],
                    exports: [ShippingConditionPipe],
                }]
        }] });

function createLocalSearchString({ id = guid(), searchTerm = '', entryDate = new Date() }) {
    return {
        id,
        searchTerm,
        entryDate,
    };
}

let ArticleAccessoriesStore = class ArticleAccessoriesStore extends EntityStore {
    constructor() {
        super();
    }
};
ArticleAccessoriesStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleAccessoriesStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ArticleAccessoriesStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleAccessoriesStore, providedIn: 'root' });
ArticleAccessoriesStore = __decorate([
    StoreConfig({ name: 'article-accessories', idKey: 'sArticleID' })
], ArticleAccessoriesStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleAccessoriesStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class ArticleAccessoriesQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
}
ArticleAccessoriesQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleAccessoriesQuery, deps: [{ token: ArticleAccessoriesStore }], target: i0.ɵɵFactoryTarget.Injectable });
ArticleAccessoriesQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleAccessoriesQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleAccessoriesQuery, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: ArticleAccessoriesStore }]; } });

const defaultPageViewConfig = {
    sortBy: 'relevance',
    viewMode: 'grid',
    pageIndex: 0,
    pageSize: 12,
};
const defaultCatalogConfig = {
    apiUrl: '',
    ...defaultPageViewConfig,
};
const CATALOG_CONFIG = new InjectionToken('CatalogConfig');

function createInitialState$3(catalogConfig) {
    return {
        articles: [],
        facets: [],
        totalHits: 0,
        cachedHits: [],
        ...catalogConfig,
    };
}
let ArticleFacetedSearchStore = class ArticleFacetedSearchStore extends Store {
    constructor(catalogConfig) {
        super(createInitialState$3(catalogConfig));
        this.catalogConfig = catalogConfig;
    }
};
ArticleFacetedSearchStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleFacetedSearchStore, deps: [{ token: CATALOG_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
ArticleFacetedSearchStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleFacetedSearchStore });
ArticleFacetedSearchStore = __decorate([
    StoreConfig({ name: 'product-faceted-search' })
], ArticleFacetedSearchStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleFacetedSearchStore, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [CATALOG_CONFIG]
                }] }]; } });

class ArticleFacetedSearchQuery extends Query {
    constructor(store) {
        super(store);
        this.store = store;
        this.articles$ = this.select('articles');
        this.facets$ = this.select('facets').pipe(map((facets) => {
            return facets.filter((facet) => facet.shtWebshopFilter === 1);
        }));
        this.totalHits$ = this.select('totalHits');
        this.cachedHits$ = this.select('cachedHits');
        this.pageIndex$ = this.select('pageIndex');
        this.pageSize$ = this.select('pageSize');
        this.sortBy$ = this.select('sortBy');
        this.viewMode$ = this.select('viewMode');
    }
    cachedFor(slug) {
        return this.cachedHits$.pipe(map((hits) => hits.filter((hit) => hit.id === slug)), map((hits) => (hits.length ? hits[0].hits : [])));
    }
    cachedTotalFor(slug) {
        return this.cachedHits$.pipe(map((hits) => hits.filter((hit) => hit.id === slug)), map((hits) => (hits.length ? hits[0].totalHits : 0)));
    }
}
ArticleFacetedSearchQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleFacetedSearchQuery, deps: [{ token: ArticleFacetedSearchStore }], target: i0.ɵɵFactoryTarget.Injectable });
ArticleFacetedSearchQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleFacetedSearchQuery });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleFacetedSearchQuery, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: ArticleFacetedSearchStore }]; } });

class ArticleFactory {
    static getArticleUnitSelected(article) {
        let oUnitSelected = undefined;
        article.oUnitColl.forEach((oUnit) => {
            if (oUnit.sQuantityUnitInput === article.sQuantityUnitSales) {
                oUnitSelected = oUnit;
            }
        });
        if (oUnitSelected == undefined) {
            article.oUnitColl.forEach((oUnit) => {
                if (oUnit.shtStandard === 1) {
                    oUnitSelected = oUnit;
                }
            });
        }
        if (oUnitSelected == undefined) {
            if (article.oUnitColl.length > 0) {
                oUnitSelected = article.oUnitColl[0];
            }
        }
        article.oUnitSelected = oUnitSelected;
        return article;
    }
    static create(article) {
        return ArticleFactory.getArticleUnitSelected({
            ...article,
            decQty: article.decQuantityPackage > 0 ? article.decQuantityPackage : 1,
            decFactor1: 0,
            decFactor2: 0,
            decFactor3: 0,
        });
    }
}

let ArticleStore = class ArticleStore extends EntityStore {
    /**
     * Creates an instance of article store.
     */
    constructor() {
        super();
    }
};
ArticleStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ArticleStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleStore, providedIn: 'root' });
ArticleStore = __decorate([
    StoreConfig({ name: 'article', idKey: 'sArticleID' })
], ArticleStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class ArticleQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
    selectEntity(sArticleID) {
        return super.selectEntity(sArticleID).pipe(filter((article) => !!article), 
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        map((article) => ArticleFactory.create(article)));
    }
}
ArticleQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleQuery, deps: [{ token: ArticleStore }], target: i0.ɵɵFactoryTarget.Injectable });
ArticleQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleQuery, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: ArticleStore }]; } });

let CatalogStore = class CatalogStore extends EntityStore {
    constructor() {
        super();
    }
};
CatalogStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CatalogStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CatalogStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CatalogStore, providedIn: 'root' });
CatalogStore = __decorate([
    StoreConfig({ name: 'catalog', resettable: true })
], CatalogStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CatalogStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class CatalogQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
    selectCategoriesByParentGroupId$(parentGroupId) {
        return this.selectAll({
            filterBy: ({ lngParentGroup }) => lngParentGroup === parentGroupId,
            sortBy: (category1, category2) => {
                if (category1.lngItemId !== category2.lngItemId) {
                    return category1.lngItemId - category2.lngItemId;
                }
                return category1.sTitle.toLowerCase().localeCompare(category2.sTitle.toLowerCase());
            },
        });
    }
    getCategoriesByParentGroupId(parentGroupId) {
        return this.getAll({
            filterBy: ({ lngParentGroup }) => lngParentGroup === parentGroupId,
            sortBy: (category1, category2) => {
                if (category1.lngItemId !== category2.lngItemId) {
                    return category1.lngItemId - category2.lngItemId;
                }
                return category2.sTitle.toLowerCase().localeCompare(category1.sTitle.toLowerCase());
            },
        });
    }
    getHasCategoriesByParentGroupId(parentGroupId) {
        const categories = this.getAll({
            filterBy: ({ lngParentGroup }) => lngParentGroup === parentGroupId,
        });
        return !!(categories && categories.length);
    }
    selectCategoryByGroupId$(groupId) {
        return this.selectAll({
            filterBy: ({ lngGroup }) => lngGroup === groupId,
        }).pipe(map((groups) => groups[0]));
    }
    getHasCategoryByGroupId(groupId) {
        return !!this.getAll({
            filterBy: ({ lngGroup }) => lngGroup === groupId,
        });
    }
}
CatalogQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CatalogQuery, deps: [{ token: CatalogStore }], target: i0.ɵɵFactoryTarget.Injectable });
CatalogQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CatalogQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CatalogQuery, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: CatalogStore }]; } });

function createInitialState$2(catalogConfig) {
    return {
        category: undefined,
        articleCount: [],
        facets: [],
        hits: [],
        cachedHits: [],
        subLevelCategories: [],
        totalHits: 0,
        ...catalogConfig,
    };
}
let CategoryFacetedSearchStore = class CategoryFacetedSearchStore extends Store {
    constructor(catalogConfig) {
        super(createInitialState$2(catalogConfig));
        this.catalogConfig = catalogConfig;
    }
};
CategoryFacetedSearchStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CategoryFacetedSearchStore, deps: [{ token: CATALOG_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
CategoryFacetedSearchStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CategoryFacetedSearchStore });
CategoryFacetedSearchStore = __decorate([
    StoreConfig({ name: 'category-faceted-search' })
], CategoryFacetedSearchStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CategoryFacetedSearchStore, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [CATALOG_CONFIG]
                }] }]; } });

class CategoryFacetedSearchQuery extends Query {
    constructor(store) {
        super(store);
        this.store = store;
        this.category$ = this.select('category');
        this.facets$ = this.select('facets').pipe(map((facets) => {
            return facets.filter((facet) => facet.shtWebshopFilter === 1);
        }));
        this.hits$ = this.select('hits');
        this.cachedHits$ = this.select('cachedHits');
        this.pageIndex$ = this.select('pageIndex');
        this.pageSize$ = this.select('pageSize');
        this.sortBy$ = this.select('sortBy');
        this.viewMode$ = this.select('viewMode');
        this.subLevelCategories$ = this.select('subLevelCategories');
        this.totalHits$ = this.select('totalHits');
        this.articleCount$ = this.select('articleCount');
    }
    cachedFor(category) {
        return this.cachedHits$.pipe(map((hits) => hits.filter((hit) => hit.id === category.sUrlPath)), map((hits) => (hits.length ? hits[0].hits : [])));
    }
    cachedTotalFor(category) {
        return this.cachedHits$.pipe(map((hits) => hits.filter((hit) => hit.id === category.sUrlPath)), map((hits) => (hits.length ? hits[0].totalHits : 0)));
    }
}
CategoryFacetedSearchQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CategoryFacetedSearchQuery, deps: [{ token: CategoryFacetedSearchStore }], target: i0.ɵɵFactoryTarget.Injectable });
CategoryFacetedSearchQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CategoryFacetedSearchQuery });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CategoryFacetedSearchQuery, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: CategoryFacetedSearchStore }]; } });

let CategoryStore = class CategoryStore extends EntityStore {
    constructor() {
        super();
    }
};
CategoryStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CategoryStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CategoryStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CategoryStore, providedIn: 'root' });
CategoryStore = __decorate([
    StoreConfig({ name: 'category', resettable: true })
], CategoryStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CategoryStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class CategoryQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
    selectCategoriesByParentGroupId$(parentGroupId) {
        return this.selectAll({
            filterBy: ({ lngParentGroup }) => lngParentGroup === parentGroupId,
            sortBy: (category1, category2) => {
                if (category1.lngItemId !== category2.lngItemId) {
                    return category1.lngItemId - category2.lngItemId;
                }
                return category1.sTitle.toLowerCase().localeCompare(category2.sTitle.toLowerCase());
            },
        });
    }
    getHasCategoriesByParentGroupId(parentGroupId) {
        const categories = this.getAll({
            filterBy: ({ lngParentGroup }) => lngParentGroup === parentGroupId,
        });
        return !!(categories && categories.length);
    }
    selectById(id) {
        return this.selectAll({
            filterBy: (category) => category.Id === id,
        }).pipe(map((data) => {
            return data[0];
        }));
    }
}
CategoryQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CategoryQuery, deps: [{ token: CategoryStore }], target: i0.ɵɵFactoryTarget.Injectable });
CategoryQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CategoryQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CategoryQuery, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: CategoryStore }]; } });

function createInitialState$1(catalogConfig) {
    return {
        categorySuggestions: [],
        cmsSuggestions: [],
        facets: [],
        hits: [],
        cachedHits: [],
        subLevelCategories: [],
        totalHits: 0,
        ...catalogConfig,
    };
}
let FacetedSearchResultStore = class FacetedSearchResultStore extends Store {
    constructor(catalogConfig) {
        super(createInitialState$1(catalogConfig));
        this.catalogConfig = catalogConfig;
    }
};
FacetedSearchResultStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FacetedSearchResultStore, deps: [{ token: CATALOG_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
FacetedSearchResultStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FacetedSearchResultStore });
FacetedSearchResultStore = __decorate([
    StoreConfig({ name: 'faceted-search-result' })
], FacetedSearchResultStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FacetedSearchResultStore, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [CATALOG_CONFIG]
                }] }]; } });

class FacetedSearchResultQuery extends Query {
    constructor(store) {
        super(store);
        this.store = store;
        this.categorySuggestions$ = this.select('categorySuggestions');
        this.cmsSuggestions$ = this.select('cmsSuggestions');
        this.facets$ = this.select('facets').pipe(map((facets) => {
            return facets.filter((facet) => facet.shtWebshopFilter === 1);
        }));
        this.hits$ = this.select('hits').pipe(map((articles) => articles.map((article) => ArticleFactory.create(article))));
        this.cachedHits$ = this.select('cachedHits');
        this.pageIndex$ = this.select('pageIndex');
        this.pageSize$ = this.select('pageSize');
        this.sortBy$ = this.select('sortBy');
        this.viewMode$ = this.select('viewMode');
        this.subLevelCategories$ = this.select('subLevelCategories');
        this.totalHits$ = this.select('totalHits');
    }
    cachedFor(searchTerm) {
        return this.cachedHits$.pipe(map((hits) => hits.filter((hit) => hit.id === searchTerm)), map((hits) => (hits.length ? hits[0].hits : [])));
    }
    cachedTotalFor(searchTerm) {
        return this.cachedHits$.pipe(map((hits) => hits.filter((hit) => hit.id === searchTerm)), map((hits) => (hits.length ? hits[0].totalHits : 0)));
    }
}
FacetedSearchResultQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FacetedSearchResultQuery, deps: [{ token: FacetedSearchResultStore }], target: i0.ɵɵFactoryTarget.Injectable });
FacetedSearchResultQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FacetedSearchResultQuery });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FacetedSearchResultQuery, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: FacetedSearchResultStore }]; } });

let FilterStore = class FilterStore extends EntityStore {
    constructor() {
        super();
    }
};
FilterStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FilterStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
FilterStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FilterStore });
FilterStore = __decorate([
    StoreConfig({ name: 'filter' })
], FilterStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FilterStore, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

class FilterQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
}
FilterQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FilterQuery, deps: [{ token: FilterStore }], target: i0.ɵɵFactoryTarget.Injectable });
FilterQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FilterQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FilterQuery, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: FilterStore }]; } });

let LocalSearchStringStore = class LocalSearchStringStore extends EntityStore {
    constructor() {
        super();
    }
};
LocalSearchStringStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LocalSearchStringStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
LocalSearchStringStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LocalSearchStringStore, providedIn: 'root' });
LocalSearchStringStore = __decorate([
    StoreConfig({ name: 'local-search-string' })
], LocalSearchStringStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LocalSearchStringStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

let LocalSearchStringQuery = class LocalSearchStringQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
};
LocalSearchStringQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LocalSearchStringQuery, deps: [{ token: LocalSearchStringStore }], target: i0.ɵɵFactoryTarget.Injectable });
LocalSearchStringQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LocalSearchStringQuery, providedIn: 'root' });
LocalSearchStringQuery = __decorate([
    QueryConfig({ sortBy: 'entryDate', sortByOrder: Order.DESC })
], LocalSearchStringQuery);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LocalSearchStringQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: LocalSearchStringStore }]; } });

let PriceStore = class PriceStore extends EntityStore {
    constructor() {
        super();
    }
};
PriceStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PriceStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
PriceStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PriceStore, providedIn: 'root' });
PriceStore = __decorate([
    StoreConfig({
        name: 'prices',
        idKey: 'sArticleID',
        resettable: true,
    })
], PriceStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PriceStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

let PriceQuery = class PriceQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
    selectHasEntity(id) {
        return this.selectEntity(id).pipe(map((entity) => !!entity));
    }
};
PriceQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PriceQuery, deps: [{ token: PriceStore }], target: i0.ɵɵFactoryTarget.Injectable });
PriceQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PriceQuery, providedIn: 'root' });
PriceQuery = __decorate([
    QueryConfig({
        sortBy: 'sArticleID',
        sortByOrder: Order.ASC, // Order.DESC
    })
], PriceQuery);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PriceQuery, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: PriceStore }]; } });

let ProductStore = class ProductStore extends EntityStore {
    constructor() {
        super();
    }
};
ProductStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProductStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ProductStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProductStore, providedIn: 'root' });
ProductStore = __decorate([
    StoreConfig({
        name: 'product',
        idKey: 'sUrlPath',
        resettable: true,
    })
], ProductStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProductStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class ProductQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
    selectById(id) {
        return this.selectAll({
            filterBy: (product) => product.Id === id,
        }).pipe(map((data) => {
            return data[0];
        }), filter((p) => !!p), map((product) => {
            return {
                ...product,
                oArticles: product.oArticles.map((article) => ArticleFactory.create(article)),
            };
        }));
    }
    selectByGroupIds(groupIds) {
        return this.selectAll({
            filterBy: (product) => groupIds.includes(product.lngGroup),
        });
    }
    getMainProductImage(_) {
        return '';
    }
}
ProductQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProductQuery, deps: [{ token: ProductStore }], target: i0.ɵɵFactoryTarget.Injectable });
ProductQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProductQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProductQuery, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: ProductStore }]; } });

function createInitialState() {
    return {
        articles: [],
        hits: {
            hits: [],
            total: 0,
        },
        totalArticles: 0,
        categories: [],
        categorySuggestions: [],
        articleSuggestions: [],
        categoryHighlights: [],
        articleHighlights: [],
        cmsSuggestions: [],
        pages: [],
        searchTerm: '',
    };
}
let SearchStore = class SearchStore extends Store {
    constructor() {
        super(createInitialState());
    }
};
SearchStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SearchStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SearchStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SearchStore, providedIn: 'root' });
SearchStore = __decorate([
    StoreConfig({ name: 'search', resettable: true })
], SearchStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SearchStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class SearchQuery extends Query {
    constructor(store) {
        super(store);
        this.store = store;
        this.articles$ = this.select('articles');
        this.categories$ = this.select('categories');
        this.categorySuggestions$ = this.select('categorySuggestions');
        this.cmsSuggestions$ = this.select('cmsSuggestions');
        this.pages$ = this.select('pages');
        this.totalArticles$ = this.select('totalArticles');
        this.hits$ = this.select('hits');
        this.searchTerm$ = this.select('searchTerm');
        this.articleHighlights$ = this.select('articleHighlights');
        this.categoryHighlights$ = this.select('categoryHighlights');
    }
}
SearchQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SearchQuery, deps: [{ token: SearchStore }], target: i0.ɵɵFactoryTarget.Injectable });
SearchQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SearchQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SearchQuery, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: SearchStore }]; } });

let StockAvailabilityStore = class StockAvailabilityStore extends EntityStore {
    constructor() {
        super();
    }
};
StockAvailabilityStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: StockAvailabilityStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
StockAvailabilityStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: StockAvailabilityStore, providedIn: 'root' });
StockAvailabilityStore = __decorate([
    StoreConfig({
        name: 'storages',
        idKey: 'sArticleID',
    })
], StockAvailabilityStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: StockAvailabilityStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

let StockAvailabilityQuery = class StockAvailabilityQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
};
StockAvailabilityQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: StockAvailabilityQuery, deps: [{ token: StockAvailabilityStore }], target: i0.ɵɵFactoryTarget.Injectable });
StockAvailabilityQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: StockAvailabilityQuery, providedIn: 'root' });
StockAvailabilityQuery = __decorate([
    QueryConfig({
        sortBy: 'sArticleID',
        sortByOrder: Order.ASC, // Order.DESC
    })
], StockAvailabilityQuery);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: StockAvailabilityQuery, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: StockAvailabilityStore }]; } });

var PageViewParameterMapping;
(function (PageViewParameterMapping) {
    PageViewParameterMapping["STRING"] = "STRING";
    PageViewParameterMapping["NUMBER"] = "NUMBER";
})(PageViewParameterMapping || (PageViewParameterMapping = {}));
/**
 * Handles page view related tasks
 * E.g. page size, sort, view mode, etc.
 */
class PageViewFilterAbstract {
    constructor(store, location, urlHelper) {
        this.store = store;
        this.location = location;
        this.urlHelper = urlHelper;
        this.defaults = {};
        /**
         * When converting url parameter back, we need some kind of mapping, which type they will take in the store
         * Reading them from the Url, will otherwise always result in a String.
         */
        this.urlParameterTypeMapping = {
            pageIndex: PageViewParameterMapping.NUMBER,
            pageSize: PageViewParameterMapping.NUMBER,
            sortBy: PageViewParameterMapping.STRING,
            viewMode: PageViewParameterMapping.STRING,
        };
    }
    updateDefaults(defaults) {
        this.defaults = defaults;
        this.store.update(defaults);
    }
    /**
     * Reset the store values by checking the url params
     */
    resetByUrlParams(values) {
        const urlValues = {};
        Object.entries(this.urlHelper.getQueryParams()).forEach(([key, value]) => {
            if (!key.match(/^_/)) {
                return;
            }
            const index = StringHelper.ltrim(key, '_');
            if (!this.urlParameterTypeMapping[index]) {
                return;
            }
            switch (this.urlParameterTypeMapping[index]) {
                case PageViewParameterMapping.NUMBER:
                    urlValues[index] = +value;
                    break;
                default:
                    urlValues[index] = '' + value;
            }
        });
        this.store.update({ ...values, ...urlValues });
    }
    /**
     * Updates the sort
     */
    updateSort(sortBy) {
        this.updateSettingsInUrl('sortBy', sortBy);
        this.store.update({ sortBy });
    }
    /**
     * Updates all pagination values at once
     */
    updatePagination(pageIndex, pageSize) {
        this.updateSettingsInUrl('pageIndex', pageIndex);
        this.updateSettingsInUrl('pageSize', pageSize);
        this.store.update({ pageIndex, pageSize });
    }
    /**
     * Updates the current page
     */
    updateCurrentPage(pageIndex) {
        this.updateSettingsInUrl('pageIndex', pageIndex);
        this.store.update({ pageIndex });
    }
    /**
     * Updates the page size
     */
    updatePageSize(pageSize) {
        this.updateSettingsInUrl('pageSize', pageSize);
        this.store.update({ pageSize });
    }
    /**
     * Updates the display mode
     */
    updateViewMode(viewMode) {
        this.updateSettingsInUrl('viewMode', viewMode);
        this.store.update({ viewMode });
    }
    updateSettingsInUrl(key, value) {
        const currentUrlParams = this.urlHelper.getQueryParams();
        const currentValues = this.store.getValue();
        if (this.normalizeValue(currentValues[key]) === this.normalizeValue(value)) {
            return;
        }
        currentUrlParams[`_${key}`] = this.normalizeValue(value);
        // using location.go() here, so routing is not triggered entirely
        this.location.go(this.urlHelper.getPath(), this.urlHelper.objectToQueryParams(currentUrlParams));
    }
    /**
     * Normalizes all values to string, so we can compare them strictly
     */
    normalizeValue(value) {
        return '' + value;
    }
}

function buildArticleAccessoriesQuery(oAccessories) {
    return {
        size: oAccessories.length,
        query: {
            bool: {
                must: [{ terms: { 'sArticleID.keyword': oAccessories.map((accessory) => accessory.sArticleID) } }],
            },
        },
    };
}

class ArticleAccessoriesService {
    constructor(elasticSearch, store, catalogConfig) {
        this.elasticSearch = elasticSearch;
        this.store = store;
        this.catalogConfig = catalogConfig;
    }
    getArticleAccessories(article) {
        const query = buildArticleAccessoriesQuery(article.oAccessories);
        return this.elasticSearch
            .execute(this.catalogConfig.apiUrl + '/es/search', {
            index: this.elasticSearch.localizedIndex('article'),
            query: JSON.stringify(query),
        })
            .pipe(map((response) => {
            return response.hits.hits.map((article) => {
                return article._source;
            });
        }), map((articles) => {
            const accessories = [];
            articles.forEach((accessoryArticle) => {
                const foundArticle = article.oAccessories.find((accessory) => accessoryArticle.sArticleID === accessory.sArticleID);
                if (foundArticle) {
                    accessories.push({
                        sAccessoriesType: foundArticle.sAccessoriesType,
                        oArticle: ArticleFactory.create(accessoryArticle),
                    });
                }
            });
            return accessories;
        }), map((accessories) => {
            const groups = new GroupByPipe().transform(accessories, 'sAccessoriesType');
            return { sArticleID: article.sArticleID, accessories: groups };
        }), tap((accessories) => {
            this.store.upsertMany([accessories]);
        }));
    }
}
ArticleAccessoriesService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleAccessoriesService, deps: [{ token: ElasticSearchService }, { token: ArticleAccessoriesStore }, { token: CATALOG_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
ArticleAccessoriesService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleAccessoriesService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleAccessoriesService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: ElasticSearchService }, { type: ArticleAccessoriesStore }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CATALOG_CONFIG]
                }] }]; } });

function articleFiltersToSearchQuery(activeFilters, nestedPath) {
    const queryFilters = [];
    const groupedFilters = groupBy(activeFilters, 'name');
    Object.entries(groupedFilters).forEach((filterGroup) => {
        const filters = filterGroup[1].map((filter) => {
            return {
                nested: {
                    query: {
                        bool: {
                            filter: [
                                {
                                    term: {
                                        [`${nestedPath}.sName.keyword`]: {
                                            value: filter.name,
                                        },
                                    },
                                },
                                {
                                    terms: {
                                        [`${nestedPath}.sValue.keyword`]: [filter.value],
                                    },
                                },
                            ],
                        },
                    },
                    path: nestedPath,
                },
            };
        });
        queryFilters.push({
            bool: {
                should: [...filters],
            },
        });
    });
    return queryFilters;
}
function buildArticleFacetedMultiSearchQuery(sUrlPath, queryFilters, size = 200, page = 0, sortBy = 'relevance', index) {
    const sort = Array.isArray(sortBy) ? sortBy : sortBy === 'title' ? [{ 'sName.keyword': 'asc' }] : [];
    return [
        { index },
        {
            from: page,
            size: size,
            sort: sort,
            post_filter: {
                bool: {
                    filter: {
                        bool: {
                            must: [
                                {
                                    bool: {
                                        must: [
                                            {
                                                prefix: {
                                                    'sUrlPath.keyword': sUrlPath,
                                                },
                                            },
                                        ],
                                    },
                                },
                                ...articleFiltersToSearchQuery(queryFilters, 'oFeatures'),
                            ],
                        },
                    },
                },
            },
            aggs: {
                agg_full: {
                    filter: {
                        bool: {
                            must: [
                                {
                                    bool: {
                                        must: [
                                            {
                                                prefix: {
                                                    'sUrlPath.keyword': sUrlPath,
                                                },
                                            },
                                        ],
                                    },
                                },
                            ],
                        },
                    },
                    aggs: {
                        filtered: {
                            nested: {
                                path: 'oFeatures',
                            },
                            aggs: {
                                attr_name: {
                                    terms: {
                                        size: 1000,
                                        field: 'oFeatures.sName.keyword',
                                    },
                                    aggs: {
                                        feature: {
                                            top_hits: {
                                                size: 1,
                                                _source: {
                                                    include: ['oFeatures.shtRepresentationType', 'oFeatures.shtWebshopFilter'],
                                                },
                                            },
                                        },
                                        attr_value: {
                                            terms: {
                                                size: 1000,
                                                field: 'oFeatures.sValue.keyword',
                                            },
                                            aggs: {
                                                option: {
                                                    top_hits: {
                                                        size: 1,
                                                        _source: {
                                                            include: ['oFeatures.shtPos'],
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    },
                                },
                            },
                        },
                    },
                },
                agg_filtered: {
                    filter: {
                        bool: {
                            must: [
                                {
                                    bool: {
                                        must: [
                                            {
                                                prefix: {
                                                    'sUrlPath.keyword': sUrlPath,
                                                },
                                            },
                                        ],
                                    },
                                },
                                ...articleFiltersToSearchQuery(queryFilters, 'oFeatures'),
                            ],
                        },
                    },
                    aggs: {
                        filtered: {
                            nested: {
                                path: 'oFeatures',
                            },
                            aggs: {
                                attr_name: {
                                    terms: {
                                        size: 1000,
                                        field: 'oFeatures.sName.keyword',
                                    },
                                    aggs: {
                                        attr_value: {
                                            terms: {
                                                size: 1000,
                                                field: 'oFeatures.sValue.keyword',
                                            },
                                        },
                                    },
                                },
                            },
                        },
                    },
                },
            },
        },
        { index },
        {
            size: 1,
            query: {
                bool: {
                    filter: {
                        term: {
                            'sUrlPath.keyword': sUrlPath,
                        },
                    },
                },
            },
        },
    ];
}

class FacetedSearchAbstract {
    constructor() {
        this.defaultSort = 'title';
        this.sortMapping = {
            relevance: [],
            title: [{ 'sName.keyword': { order: 'asc' } }],
        };
    }
    setDefaultSort(sort) {
        this.defaultSort = sort;
    }
    getDefaultSort() {
        return this.defaultSort;
    }
    addSortMapping(key, sort) {
        this.sortMapping[key] = sort;
    }
    getSortMapping(key) {
        if (!this.sortMapping[key]) {
            return [];
        }
        return this.sortMapping[key];
    }
}
FacetedSearchAbstract.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FacetedSearchAbstract, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
FacetedSearchAbstract.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FacetedSearchAbstract });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FacetedSearchAbstract, decorators: [{
            type: Injectable
        }] });

/**
 * A service to perform CRUD-operations on product entities
 */
class FilterService extends AkitaFiltersPlugin {
    /**
     * Creates an instance of product service.
     */
    constructor(filterQuery, location, urlHelper) {
        super(filterQuery);
        this.filterQuery = filterQuery;
        this.location = location;
        this.urlHelper = urlHelper;
    }
    /**
     * sets all the filters by the already existing query params (only on initialization)
     */
    setFiltersByUrlParams() {
        super.clearFilters();
        Object.entries(this.urlHelper.getQueryParams()).forEach(([key, value]) => {
            if (key.match(/^_/)) {
                return;
            }
            if (value.match(/;/)) {
                const multiValue = value.split(';');
                multiValue.forEach((innerVal) => {
                    super.setFilter({
                        id: `${key}-${innerVal}`,
                        name: key,
                        value: innerVal,
                    });
                });
            }
            else if (Array.isArray(value)) {
                value.forEach((innerVal) => {
                    super.setFilter({
                        id: `${key}-${innerVal}`,
                        name: key,
                        value: innerVal,
                    });
                });
            }
            else {
                super.setFilter({
                    id: `${key}-${value}`,
                    name: key,
                    value: value,
                });
            }
        });
    }
    /**
     * removes a specific filter (serves as a proxy for the removeFilter function from akita-filter-plugin)
     * @param option
     * @param facetName
     */
    removeFilterWithParam(option, facetName) {
        const filterToRemove = this.getFilters().find((item) => item.name === facetName && item.value === option);
        if (filterToRemove) {
            super.removeFilter(filterToRemove.id);
        }
        this.updateFilterInUrl();
    }
    /**
     * Create or update a filter
     */
    setFilter(filter) {
        super.setFilter(filter);
        this.updateFilterInUrl();
    }
    /**
     * clears all Filters and removes them from the query params
     */
    clearFilters() {
        super.clearFilters();
        this.updateFilterInUrl();
    }
    updateFilterInUrl() {
        const currentUrlParams = this.urlHelper.getQueryParams();
        const groupedFilters = groupBy(this.getFilters(), 'name');
        Object.keys(groupedFilters).forEach((item) => {
            groupedFilters[item] = groupedFilters[item].map((filterValue) => filterValue.value);
        });
        Object.keys(currentUrlParams).forEach((key) => {
            if (!key.match(/^_/)) {
                return;
            }
            groupedFilters[key] = currentUrlParams[key];
        });
        // using location.go() here, so routing is not triggered entirely
        this.location.go(this.urlHelper.getPath(), this.urlHelper.objectToQueryParams(groupedFilters));
    }
}
FilterService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FilterService, deps: [{ token: FilterQuery }, { token: i2.Location }, { token: UrlHelperService }], target: i0.ɵɵFactoryTarget.Injectable });
FilterService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FilterService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FilterService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: FilterQuery }, { type: i2.Location }, { type: UrlHelperService }]; } });

class ArticleFacetedSearchService extends FacetedSearchAbstract {
    constructor(elasticSearch, store, productQuery, filterService, translocoService, catalogConfig) {
        super();
        this.elasticSearch = elasticSearch;
        this.store = store;
        this.productQuery = productQuery;
        this.filterService = filterService;
        this.translocoService = translocoService;
        this.catalogConfig = catalogConfig;
        this.setDefaultSort('title');
    }
    search(slug, queryFilters, sortBy) {
        this.store.setLoading(true);
        const query = buildArticleFacetedMultiSearchQuery(slug, queryFilters, 200, 0, this.getSortMapping(sortBy || this.getDefaultSort()), this.elasticSearch.localizedIndex('article'));
        return this.elasticSearch
            .execute(this.catalogConfig.apiUrl + '/es/msearch', {
            index: this.elasticSearch.localizedIndex('article'),
            query: query.map((entry) => JSON.stringify(entry)).join('\n') + '\n',
        })
            .pipe(tap((response) => {
            this.doMappings(response);
        }), tap(() => this.store.setLoading(false)));
    }
    searchByFiltersCached(slug, queryFilters, viewConfig) {
        const storedValues = this.store.getValue();
        const cachedHits = storedValues.cachedHits || [];
        const filteredHits = cachedHits.filter((hit) => hit.id === slug);
        if (filteredHits.length) {
            const hit = filteredHits[0];
            if (isEqual(viewConfig, hit.config) && isEqual(queryFilters, hit.queryFilters)) {
                return of(hit.hits);
            }
            // if anything but page size changed, reset store
            if (!isEqualWith(viewConfig, hit.config, (_, _2, key) => key === 'pageSize' ? true : undefined) ||
                !isEqual(queryFilters, hit.queryFilters)) {
                this.store.update({
                    cachedHits: [...cachedHits.filter((cachedHit) => cachedHit.id !== slug)],
                });
            }
        }
        this.store.setLoading(true);
        const query = buildArticleFacetedMultiSearchQuery(slug, queryFilters, viewConfig.pageSize, viewConfig.pageIndex, this.getSortMapping(viewConfig.sortBy || this.getDefaultSort()), this.elasticSearch.localizedIndex('article'));
        return this.elasticSearch
            .execute(this.catalogConfig.apiUrl + '/es/msearch', {
            index: this.elasticSearch.localizedIndex('article'),
            query: query.map((entry) => JSON.stringify(entry)).join('\n') + '\n',
        })
            .pipe(map((response) => {
            this.doMappings(response);
            return this._setCachedHits(cachedHits, slug, viewConfig, response.responses[0].hits, queryFilters);
        }), tap(() => this.store.setLoading(false)));
    }
    /** saves the hits to the store */
    _setCachedHits(cachedHits, slug, config, hits, queryFilters) {
        const articles = hits.hits.map((product) => {
            return { score: product._score, ...product._source };
        });
        this.store.update({
            cachedHits: [
                ...cachedHits.filter((cachedHit) => cachedHit.id !== slug),
                { hits: articles, id: slug, totalHits: hits.total.value, config, queryFilters },
            ],
        });
        return articles;
    }
    /** Calls all other mapping functions **/
    doMappings(response) {
        this._setArticles(response.responses[0].hits);
        this._setFacets(response.responses[0].aggregations, response.responses[1]?.hits?.hits[0]?._source?.oProductInfo[0]?.oFeatureClass);
    }
    _setFacets(aggregations, featureClass) {
        const fullAggregations = this._transformAggregations(aggregations.agg_full.filtered);
        const filteredAggregations = this._transformAggregations(aggregations.agg_filtered.filtered);
        const facets = this._buildFacets(fullAggregations, filteredAggregations, featureClass);
        this.store.update({ facets });
    }
    /** saves the hits to the store */
    _setArticles(hits) {
        const articles = hits.hits
            .map((hit) => ({ score: hit._score, ...hit._source }))
            .map((article) => ArticleFactory.create(article));
        this.store.update({ articles });
        this.store.update({ totalHits: hits.total.value });
    }
    _transformAggregations(aggregations) {
        return aggregations.attr_name.buckets.map((facet) => {
            const values = facet.attr_value.buckets.map((option) => {
                return { key: option.key, count: option.doc_count, disabled: false };
            });
            let newFacet = {
                key: facet.key,
                values,
                shtRepresentationType: 1,
                shtWebshopFilter: 1,
            };
            if (facet.feature && facet.feature.hits.hits.length > 0) {
                const featureConfig = facet.feature.hits.hits[0]._source;
                newFacet = {
                    ...newFacet,
                    shtRepresentationType: featureConfig.shtRepresentationType,
                    shtWebshopFilter: featureConfig.shtWebshopFilter,
                };
            }
            return newFacet;
        });
    }
    _buildFacets(fullAggregations, filteredAggregations, featureClass) {
        let facets = fullAggregations.map((facetFull) => {
            const facetFiltered = filteredAggregations.find((item) => item.key === facetFull.key);
            let newValues;
            if (facetFiltered) {
                newValues = facetFull.values.map((option) => {
                    const optionFiltered = facetFiltered.values.find((item) => item.key === option.key);
                    let optionCopy;
                    if (optionFiltered) {
                        optionCopy = { ...option, count: optionFiltered.count };
                    }
                    else {
                        optionCopy = { ...option, disabled: true, count: 0 };
                    }
                    return !this.isFirstFilter(facetFull.key) ? optionCopy : { ...optionCopy, disabled: false, count: option.count };
                });
            }
            else {
                newValues = facetFull.values.map((option) => {
                    return { ...option, disabled: true, count: 0 };
                });
            }
            newValues = newValues.map((facetOption) => {
                return {
                    ...facetOption,
                    isActive: this.isActiveFilter(facetFull.key, facetOption.key),
                };
            });
            return {
                ...facetFull,
                values: newValues.sort((a, b) => a.key.localeCompare(b.key, this.translocoService.getActiveLang(), { numeric: true })),
            };
        });
        if (featureClass?.length > 0) {
            facets = this.reduceFacetsByFeatureClass(facets, featureClass);
            facets = this.addPositionFromFeatureClass(facets, featureClass);
        }
        return facets.sort((a, b) => {
            if (a?.shtFCFPos != b?.shtFCFPos) {
                return a.shtFCFPos - b.shtFCFPos;
            }
            return a.key > b.key ? 1 : a.key === b.key ? 0 : -1;
        });
    }
    reduceFacetsByFeatureClass(facets, featureClass) {
        return facets.filter((facet) => featureClass.find((fClass) => fClass.sName === facet.key));
    }
    addPositionFromFeatureClass(facets, featureClass) {
        return facets.map((facet) => {
            return { ...facet, shtFCFPos: featureClass.find((fclass) => fclass.sName === facet.key)?.shtFCFPos };
        });
    }
    isActiveFilter(key, value) {
        return !!this.filterService.getFilters().find((filter) => filter.name === key && filter.value === value);
    }
    isFirstFilter(key) {
        return this.filterService.getFilters().length > 0 ? this.filterService.getFilters()[0].name === key : false;
    }
}
ArticleFacetedSearchService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleFacetedSearchService, deps: [{ token: ElasticSearchService }, { token: ArticleFacetedSearchStore }, { token: ProductQuery }, { token: FilterService }, { token: i1$1.TranslocoService }, { token: CATALOG_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
ArticleFacetedSearchService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleFacetedSearchService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleFacetedSearchService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: ElasticSearchService }, { type: ArticleFacetedSearchStore }, { type: ProductQuery }, { type: FilterService }, { type: i1$1.TranslocoService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CATALOG_CONFIG]
                }] }]; } });

/**
 * Handles page view related tasks
 * E.g. page size, sort, view mode, etc.
 */
class ProductPageViewFilterService extends PageViewFilterAbstract {
    constructor(store, location, urlHelper, catalogConfig) {
        super(store, location, urlHelper);
        this.catalogConfig = catalogConfig;
    }
    /**
     * Reset the store values by checking the url params
     */
    resetByUrlParams() {
        super.resetByUrlParams({ ...this.catalogConfig, ...this.defaults });
    }
}
ProductPageViewFilterService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProductPageViewFilterService, deps: [{ token: ArticleFacetedSearchStore }, { token: i2.Location }, { token: UrlHelperService }, { token: CATALOG_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
ProductPageViewFilterService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProductPageViewFilterService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProductPageViewFilterService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: ArticleFacetedSearchStore }, { type: i2.Location }, { type: UrlHelperService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CATALOG_CONFIG]
                }] }]; } });

/**
 * A service to manage article entities
 */
class ArticleService {
    constructor(store, elasticSearch, catalogConfig) {
        this.store = store;
        this.elasticSearch = elasticSearch;
        this.catalogConfig = catalogConfig;
    }
    getArticleByEAN(sEAN, limit = 1) {
        const query = {
            size: limit,
            query: { terms: { sEAN: [sEAN] } },
        };
        return this.elasticSearch
            .execute(this.catalogConfig.apiUrl + '/es/search', {
            index: this.elasticSearch.localizedIndex('article'),
            query: JSON.stringify(query),
        })
            .pipe(map((response) => {
            return response.hits?.hits?.map((esArticle) => esArticle._source) || [];
        }), tap((articles) => {
            this.store.upsertMany(articles);
        }));
    }
    getArticleByID(sArticleID) {
        const query = {
            query: {
                bool: {
                    filter: [
                        {
                            term: {
                                'sArticleID.keyword': {
                                    value: sArticleID,
                                },
                            },
                        },
                    ],
                },
            },
        };
        return this.elasticSearch
            .execute(this.catalogConfig.apiUrl + '/es/search', {
            index: this.elasticSearch.localizedIndex('article'),
            query: JSON.stringify(query),
        })
            .pipe(tap((response) => {
            const oArticle = response.hits?.hits[0]?._source;
            if (oArticle) {
                this.store.upsert(sArticleID, oArticle);
            }
            else {
                //todo log error
            }
        }));
    }
    getArticleReplaceBy(sArticleID) {
        const query = {
            query: {
                bool: {
                    filter: [
                        {
                            term: {
                                'sForerunner.keyword': {
                                    value: sArticleID,
                                },
                            },
                        },
                    ],
                },
            },
        };
        return this.elasticSearch
            .execute(this.catalogConfig.apiUrl + '/es/search', {
            index: this.elasticSearch.localizedIndex('article'),
            query: JSON.stringify(query),
        })
            .pipe(map((response) => response.hits?.hits?.map((esArticle) => esArticle._source) || []), tap((articles) => {
            if (articles) {
                this.store.upsertMany(articles);
            }
            else {
                //todo log error
            }
        }));
    }
    getArticleByMultiMatchQuery(searchTerm, fields, limit = 1) {
        const query = {
            query: {
                multi_match: {
                    query: searchTerm,
                    fields: fields,
                },
            },
            size: limit,
        };
        return this.elasticSearch
            .execute(this.catalogConfig.apiUrl + '/es/search', {
            index: this.elasticSearch.localizedIndex('article'),
            query: JSON.stringify(query),
        })
            .pipe(map((response) => {
            return response.hits?.hits?.map((esArticle) => esArticle._source) || [];
        }), tap((articles) => {
            this.store.upsertMany(articles);
        }));
    }
    getArticlesByIds(articleIds) {
        const query = {
            query: {
                bool: {
                    must: [{ terms: { 'sArticleID.keyword': articleIds } }],
                },
            },
        };
        return this.elasticSearch
            .execute(this.catalogConfig.apiUrl + '/es/search', {
            index: this.elasticSearch.localizedIndex('article'),
            query: JSON.stringify(query),
        })
            .pipe(filter((response) => {
            if (response?.hits?.hits && response.hits.hits.length > 0) {
                return true;
            }
            return false;
        }), map((response) => {
            return response.hits.hits.map((esArticle) => esArticle._source);
        }), tap((articles) => {
            this.store.upsertMany(articles);
        }));
    }
}
ArticleService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleService, deps: [{ token: ArticleStore }, { token: ElasticSearchService }, { token: CATALOG_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
ArticleService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ArticleService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: ArticleStore }, { type: ElasticSearchService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CATALOG_CONFIG]
                }] }]; } });

function buildCatalogQuery() {
    return {
        size: 10000,
        query: { bool: { filter: [{ terms: { 'sType.keyword': ['G', 'K'] } }] } },
        _source: ['sUrlPath', 'lngParentGroup', 'sTitle', 'lngGroup', 'sType', 'lngItemId'],
    };
}
function buildCatalogByLevelQuery(groupdId) {
    return {
        size: 10000,
        query: {
            bool: {
                filter: {
                    bool: {
                        must: [
                            {
                                term: {
                                    ['lngParentGroup']: {
                                        value: groupdId,
                                    },
                                },
                            },
                            {
                                term: {
                                    ['sType.keyword']: {
                                        value: 'G',
                                    },
                                },
                            },
                        ],
                    },
                },
            },
        },
        _source: ['sUrlPath', 'lngParentGroup', 'sTitle', 'lngGroup', 'sType'],
    };
}
function buildCatalgQueryByGroupId(groupdId) {
    return {
        size: 10000,
        query: {
            bool: {
                filter: [
                    {
                        term: {
                            ['lngGroup']: {
                                value: groupdId,
                            },
                        },
                    },
                ],
            },
        },
        _source: ['sUrlPath', 'lngParentGroup', 'sTitle', 'lngGroup', 'sType', 'lngItemId'],
    };
}

class CatalogService {
    /**
     * Creates an instance of catalog service.
     */
    constructor(store, elasticSearch, catalogConfig) {
        this.store = store;
        this.elasticSearch = elasticSearch;
        this.catalogConfig = catalogConfig;
    }
    getMenuTree() {
        const query = buildCatalogQuery();
        return this.elasticSearch
            .execute(this.catalogConfig.apiUrl + '/es/search', {
            index: this.elasticSearch.localizedIndex('category'),
            query: JSON.stringify(query),
        })
            .pipe(filter((data) => !!data?.hits?.hits), tap((response) => {
            const catalogs = response?.hits?.hits.map((group) => {
                return { ...group._source, id: group._source.lngGroup };
            });
            this.store.upsertMany(catalogs);
        }));
    }
    getCatalogLevelByParentGroupId(groupdId) {
        const query = buildCatalogByLevelQuery(groupdId);
        return this.elasticSearch
            .execute(this.catalogConfig.apiUrl + '/es/search', {
            index: this.elasticSearch.localizedIndex('category'),
            query: JSON.stringify(query),
        })
            .pipe(tap((response) => {
            const categories = response?.hits?.hits.map((group) => {
                return { ...group._source, id: group._source.lngGroup };
            });
            this.store.upsertMany(categories);
        }));
    }
    buildCatalogTree(items, id = 0) {
        return items
            .filter((item) => item.lngParentGroup === id)
            .map((item) => ({
            ...item,
            children: this.buildCatalogTree(items, item.lngGroup),
        }));
    }
}
CatalogService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CatalogService, deps: [{ token: CatalogStore }, { token: ElasticSearchService }, { token: CATALOG_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
CatalogService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CatalogService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CatalogService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: CatalogStore }, { type: ElasticSearchService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CATALOG_CONFIG]
                }] }]; } });

function categoryFiltersToSearchQuery(activeFilters, nestedPath) {
    const queryFilters = [];
    const groupedFilters = groupBy(activeFilters, 'name');
    Object.entries(groupedFilters).forEach((filterGroup) => {
        const filters = filterGroup[1].map((filter) => {
            return {
                nested: {
                    query: {
                        bool: {
                            filter: [
                                {
                                    term: {
                                        [`${nestedPath}.sName.keyword`]: {
                                            value: filter.name,
                                        },
                                    },
                                },
                                {
                                    terms: {
                                        [`${nestedPath}.sValue.keyword`]: [filter.value],
                                    },
                                },
                            ],
                        },
                    },
                    path: nestedPath,
                },
            };
        });
        queryFilters.push({
            bool: {
                should: [...filters],
            },
        });
    });
    return queryFilters;
}
function buildCategoryFacetedMultiSearchQuery(sUrlPath, queryFilters, size = 12, page = 0, sortBy = 'relevance', articleIndex, categoryIndex, exclude = []) {
    const sort = Array.isArray(sortBy) ? sortBy : sortBy === 'title' ? [{ 'sTitle.keyword': 'asc' }] : [];
    return [
        { index: articleIndex },
        {
            size: 0,
            aggs: {
                agg_full: {
                    filter: {
                        bool: {
                            must: [
                                {
                                    bool: {
                                        must: [
                                            {
                                                prefix: {
                                                    'sUrlPath.keyword': sUrlPath,
                                                },
                                            },
                                        ],
                                    },
                                },
                            ],
                        },
                    },
                    aggs: {
                        article_count: {
                            terms: {
                                size: 100000,
                                field: 'oProductInfo.lngGroup',
                            },
                        },
                        filtered: {
                            nested: {
                                path: 'oFeatures',
                            },
                            aggs: {
                                attr_name: {
                                    terms: {
                                        size: 1000,
                                        field: 'oFeatures.sName.keyword',
                                    },
                                    aggs: {
                                        feature: {
                                            top_hits: {
                                                size: 1,
                                                _source: {
                                                    include: ['oFeatures.shtRepresentationType', 'oFeatures.shtWebshopFilter'],
                                                },
                                            },
                                        },
                                        attr_value: {
                                            terms: {
                                                size: 1000,
                                                field: 'oFeatures.sValue.keyword',
                                            },
                                            aggs: {
                                                option: {
                                                    top_hits: {
                                                        size: 1,
                                                        _source: {
                                                            include: ['oFeatures.shtPos'],
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    },
                                },
                            },
                        },
                    },
                },
                agg_filtered: {
                    filter: {
                        bool: {
                            must: [
                                {
                                    bool: {
                                        must: [
                                            {
                                                prefix: {
                                                    'sUrlPath.keyword': sUrlPath,
                                                },
                                            },
                                        ],
                                    },
                                },
                                ...categoryFiltersToSearchQuery(queryFilters, 'oFeatures'),
                            ],
                        },
                    },
                    aggs: {
                        article_count: {
                            terms: {
                                size: 100000,
                                field: 'oProductInfo.lngGroup',
                            },
                            aggs: {
                                article: {
                                    top_hits: {
                                        size: 1,
                                        _source: {
                                            include: ['sArticleID'],
                                        },
                                    },
                                },
                            },
                        },
                        filtered: {
                            nested: {
                                path: 'oFeatures',
                            },
                            aggs: {
                                attr_name: {
                                    terms: {
                                        size: 1000,
                                        field: 'oFeatures.sName.keyword',
                                    },
                                    aggs: {
                                        attr_value: {
                                            terms: {
                                                size: 1000,
                                                field: 'oFeatures.sValue.keyword',
                                            },
                                        },
                                    },
                                },
                            },
                        },
                    },
                },
            },
        },
        { index: categoryIndex },
        {
            from: page,
            size: size,
            sort: sort,
            _source: { exclude },
            post_filter: {
                bool: {
                    filter: {
                        bool: {
                            must: [
                                {
                                    prefix: {
                                        'sUrlPath.keyword': sUrlPath,
                                    },
                                },
                                { term: { 'sType.keyword': 'P' } },
                                ...categoryFiltersToSearchQuery(queryFilters, 'oArticles.oFeatures'),
                            ],
                        },
                    },
                },
            },
        },
        { index: categoryIndex },
        {
            size: 1,
            query: {
                bool: {
                    filter: {
                        term: {
                            'sUrlPath.keyword': sUrlPath,
                        },
                    },
                },
            },
        },
    ];
}

class CategoryFacetedSearchService extends FacetedSearchAbstract {
    /**
     * Creates an instance of search service.
     */
    constructor(elasticSearch, store, catalogQuery, filterService, translocoService, catalogConfig) {
        super();
        this.elasticSearch = elasticSearch;
        this.store = store;
        this.catalogQuery = catalogQuery;
        this.filterService = filterService;
        this.translocoService = translocoService;
        this.catalogConfig = catalogConfig;
        this.addSortMapping('title', [{ 'sTitle.keyword': { order: 'asc' } }]);
    }
    searchByFiltersCached(slug, queryFilters, viewConfig, exclude = []) {
        const storedValues = this.store.getValue();
        const cachedHits = storedValues.cachedHits || [];
        const filteredHits = cachedHits.filter((hit) => hit.id === slug);
        if (filteredHits.length) {
            const hit = filteredHits[0];
            if (isEqual(viewConfig, hit.config) && isEqual(queryFilters, hit.queryFilters)) {
                return of(hit.hits);
            }
            // if anything but page size changed, reset store
            if (!isEqualWith(viewConfig, hit.config, (_, _2, key) => key === 'pageSize' ? true : undefined) ||
                !isEqual(queryFilters, hit.queryFilters)) {
                this.store.update({
                    cachedHits: [...cachedHits.filter((cachedHit) => cachedHit.id !== slug)],
                });
            }
        }
        this.store.setLoading(true);
        const query = buildCategoryFacetedMultiSearchQuery(slug, queryFilters, viewConfig.pageSize, viewConfig.pageIndex, this.getSortMapping(viewConfig.sortBy || this.getDefaultSort()), this.elasticSearch.localizedIndex('article'), this.elasticSearch.localizedIndex('category'), exclude);
        return this.elasticSearch
            .execute(this.catalogConfig.apiUrl + '/es/msearch', {
            index: this.elasticSearch.localizedIndex('article'),
            query: query.map((entry) => JSON.stringify(entry)).join('\n') + '\n',
        })
            .pipe(map((response) => {
            this.doMappings(response);
            return this._setCachedHits(cachedHits, slug, viewConfig, response.responses[1].hits, queryFilters);
        }), tap(() => this.store.setLoading(false)));
    }
    searchByFilters(slug, queryFilters, pageIndex = 0, pageSize = 12, sortBy, exclude = []) {
        this.store.setLoading(true);
        const query = buildCategoryFacetedMultiSearchQuery(slug, queryFilters, pageSize, pageIndex, this.getSortMapping(sortBy || this.getDefaultSort()), this.elasticSearch.localizedIndex('article'), this.elasticSearch.localizedIndex('category'), exclude);
        return this.elasticSearch
            .execute(this.catalogConfig.apiUrl + '/es/msearch', {
            index: this.elasticSearch.localizedIndex('article'),
            query: query.map((entry) => JSON.stringify(entry)).join('\n') + '\n',
        })
            .pipe(tap((response) => this.doMappings(response)), tap(() => this.store.setLoading(false)));
    }
    /** Calls all other mapping functions **/
    doMappings(response) {
        this._setArticleCount(response.responses[0].aggregations.agg_filtered.article_count);
        this._setFacets(response.responses[2].hits, response.responses[0].aggregations);
        this._setHits(response.responses[1].hits);
        this._setCategory(response.responses[2].hits);
        this._setSubLevelCategories(response.responses[2].hits);
    }
    /** sets the article count, respect the current set of filter */
    _setArticleCount(aggregations) {
        this.store.update({
            articleCount: aggregations.buckets.map((bucket) => ({
                lngGroupId: bucket.key,
                count: bucket.doc_count,
                sArticleID: bucket.article.hits.hits[0]._source.sArticleID,
            })),
        });
    }
    /** set the facets by es-aggregations and the provided category and filters */
    _setFacets(category, aggregations) {
        const featureClass = category.hits[0]._source.oFeatureClass;
        const fullAggregations = this._transformAggregations(aggregations.agg_full.filtered);
        const filteredAggregations = this._transformAggregations(aggregations.agg_filtered.filtered);
        const facets = this._buildFacets(fullAggregations, filteredAggregations, featureClass);
        this.store.update({ facets });
    }
    /** set the subLevelCategories store */
    _setSubLevelCategories(category) {
        const parentGroupId = category.hits[0]._source.lngGroup;
        const categories = this.catalogQuery.getCategoriesByParentGroupId(parentGroupId);
        this.store.update({ subLevelCategories: categories });
    }
    /** saves the hits to the store */
    _setHits(hits) {
        const products = hits.hits.map((product) => ({
            score: product._score,
            ...product._source,
        }));
        this.store.update({ hits: products });
        this.store.update({ totalHits: hits.total.value });
    }
    /** saves the hits to the store */
    _setCachedHits(cachedHits, slug, config, hits, queryFilters) {
        const products = hits.hits.map((product) => ({
            score: product._score,
            ...product._source,
        }));
        this.store.update({
            cachedHits: [
                ...cachedHits.filter((cachedHit) => cachedHit.id !== slug),
                { hits: products, id: slug, totalHits: hits.total.value, config, queryFilters },
            ],
        });
        return products;
    }
    /** saves the category to the store */
    _setCategory(category) {
        category = category.hits[0]._source;
        this.store.update({ category });
    }
    /** transforms es aggregations to key values object */
    _transformAggregations(aggregations) {
        return aggregations.attr_name.buckets.map((facet) => {
            const values = facet.attr_value.buckets.map((option) => {
                return { key: option.key, count: option.doc_count, disabled: false };
            });
            let newFacet = {
                key: facet.key,
                values,
                shtRepresentationType: 1,
                shtWebshopFilter: 1,
            };
            if (facet.feature && facet.feature.hits.hits.length > 0) {
                const featureConfig = facet.feature.hits.hits[0]._source;
                newFacet = {
                    ...newFacet,
                    shtRepresentationType: featureConfig.shtRepresentationType,
                    shtWebshopFilter: featureConfig.shtWebshopFilter,
                };
            }
            return newFacet;
        });
    }
    /** builds the facets by the transformed aggregations */
    _buildFacets(fullAggregations, filteredAggregations, featureClass) {
        let facets = fullAggregations.map((facetFull) => {
            const facetFiltered = filteredAggregations.find((item) => item.key === facetFull.key);
            let newValues;
            if (facetFiltered) {
                newValues = facetFull.values.map((option) => {
                    const optionFiltered = facetFiltered.values.find((item) => item.key === option.key);
                    let optionCopy;
                    if (optionFiltered) {
                        optionCopy = { ...option, count: optionFiltered.count };
                    }
                    else {
                        optionCopy = { ...option, disabled: true, count: 0 };
                    }
                    return !this.isFirstFilter(facetFull.key) ? optionCopy : { ...optionCopy, disabled: false, count: option.count };
                });
            }
            else {
                newValues = facetFull.values.map((option) => {
                    return { ...option, disabled: true, count: 0 };
                });
            }
            newValues = newValues.map((facetOption) => {
                return {
                    ...facetOption,
                    isActive: this.isActiveFilter(facetFull.key, facetOption.key),
                };
            });
            // TODO sort values
            return {
                ...facetFull,
                values: newValues.sort((a, b) => a.key.localeCompare(b.key, this.translocoService.getActiveLang(), { numeric: true })),
            };
        });
        if (featureClass?.length > 0) {
            facets = this.reduceFacetsByFeatureClass(facets, featureClass);
            facets = this.addPositionFromFeatureClass(facets, featureClass);
        }
        return facets.sort((a, b) => {
            if (a?.shtFCFPos != b?.shtFCFPos) {
                return a.shtFCFPos - b.shtFCFPos;
            }
            return a.key > b.key ? 1 : a.key === b.key ? 0 : -1;
        });
    }
    reduceFacetsByFeatureClass(facets, featureClass) {
        return facets.filter((facet) => featureClass.find((fClass) => fClass.sName === facet.key));
    }
    addPositionFromFeatureClass(facets, featureClass) {
        return facets.map((facet) => {
            return { ...facet, shtFCFPos: featureClass.find((fclass) => fclass.sName === facet.key)?.shtFCFPos };
        });
    }
    isActiveFilter(key, value) {
        return !!this.filterService.getFilters().find((filter) => filter.name === key && filter.value === value);
    }
    isFirstFilter(key) {
        return this.filterService.getFilters().length > 0 ? this.filterService.getFilters()[0].name === key : false;
    }
}
CategoryFacetedSearchService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CategoryFacetedSearchService, deps: [{ token: ElasticSearchService }, { token: CategoryFacetedSearchStore }, { token: CatalogQuery }, { token: FilterService }, { token: i1$1.TranslocoService }, { token: CATALOG_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
CategoryFacetedSearchService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CategoryFacetedSearchService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CategoryFacetedSearchService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: ElasticSearchService }, { type: CategoryFacetedSearchStore }, { type: CatalogQuery }, { type: FilterService }, { type: i1$1.TranslocoService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CATALOG_CONFIG]
                }] }]; } });

/**
 * Handles page view related tasks
 * E.g. page size, sort, view mode, etc.
 */
class CategoryPageViewFilterService extends PageViewFilterAbstract {
    constructor(store, location, urlHelper, catalogConfig) {
        super(store, location, urlHelper);
        this.catalogConfig = catalogConfig;
    }
    /**
     * Reset the store values by checking the url params
     */
    resetByUrlParams() {
        super.resetByUrlParams({ ...this.catalogConfig, ...this.defaults });
    }
}
CategoryPageViewFilterService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CategoryPageViewFilterService, deps: [{ token: CategoryFacetedSearchStore }, { token: i2.Location }, { token: UrlHelperService }, { token: CATALOG_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
CategoryPageViewFilterService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CategoryPageViewFilterService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CategoryPageViewFilterService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: CategoryFacetedSearchStore }, { type: i2.Location }, { type: UrlHelperService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CATALOG_CONFIG]
                }] }]; } });

function buildCategoriesByParentGroupIdQuery(parentId) {
    return {
        size: 10000,
        query: {
            bool: {
                filter: {
                    bool: {
                        must: [
                            {
                                term: {
                                    ['lngParentGroup']: {
                                        value: parentId,
                                    },
                                },
                            },
                            {
                                term: {
                                    ['sType.keyword']: {
                                        value: 'G',
                                    },
                                },
                            },
                        ],
                    },
                },
            },
        },
    };
}
function buildCategoriesByGroupIdQuery(groupId) {
    return {
        size: 10000,
        query: {
            bool: {
                filter: {
                    bool: {
                        must: [
                            {
                                term: {
                                    ['lngGroup']: {
                                        value: groupId,
                                    },
                                },
                            },
                            {
                                term: {
                                    ['sType.keyword']: {
                                        value: 'G',
                                    },
                                },
                            },
                        ],
                    },
                },
            },
        },
    };
}

class CategoryService extends NgEntityService {
    /**
     * Creates an instance of catalog service.
     */
    constructor(elasticSearch, store, catalogConfig) {
        super(store);
        this.elasticSearch = elasticSearch;
        this.catalogConfig = catalogConfig;
    }
    getByGroupId(id) {
        const query = buildCategoriesByGroupIdQuery(id);
        return this.elasticSearch
            .execute(this.catalogConfig.apiUrl + '/es/search', {
            index: this.elasticSearch.localizedIndex('category'),
            query: JSON.stringify(query),
        })
            .pipe(share(), map((response) => response.hits.hits[0]._source), tap((category) => this.store.upsert(category.Id, category)));
    }
    getById(id) {
        const query = {
            query: {
                ids: {
                    values: [id],
                },
            },
        };
        return this.elasticSearch
            .execute(this.catalogConfig.apiUrl + '/es/search', {
            index: this.elasticSearch.localizedIndex('category'),
            query: JSON.stringify(query),
        })
            .pipe(share(), map((response) => response.hits.hits[0]._source), tap((category) => this.store.upsert(category.Id, category)));
    }
    getByParentGroupId(id) {
        const query = buildCategoriesByParentGroupIdQuery(id);
        return this.elasticSearch
            .execute(this.catalogConfig.apiUrl + '/es/search', {
            index: this.elasticSearch.localizedIndex('category'),
            query: JSON.stringify(query),
        })
            .pipe(share(), filter((response) => response.hits.hits.length > 0), map((response) => {
            return response.hits.hits.map((category) => ({ id: category._id, ...category._source }));
        }), tap((categories) => this.store.upsertMany(categories)));
    }
}
CategoryService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CategoryService, deps: [{ token: ElasticSearchService }, { token: CategoryStore }, { token: CATALOG_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
CategoryService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CategoryService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CategoryService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: ElasticSearchService }, { type: CategoryStore }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CATALOG_CONFIG]
                }] }]; } });

function buildSearchQuery(searchString) {
    return {
        multi_match: {
            query: searchString,
            fields: [
                'sArticleID^5',
                'sName^4',
                'sDescription^3',
                'sEAN^5',
                'oProductInfo.sTitle^2',
                'oProductInfo.sDescription^2',
                'oProductInfo.sDescriptionShort^2',
                'sCustomerArticleID^1',
                'sAlternativeArticleID^1',
                'sExternalArticleID^1',
                'sPurchOrderNumber^1',
                'oProductInfo.keywords^1',
                'sUniformNumber^3',
            ],
            fuzziness: 'AUTO',
            minimum_should_match: '66%',
        },
    };
}
function buildCategorySearchQuery(searchString) {
    return {
        multi_match: {
            query: searchString,
            fields: ['sTitle^5', 'keywords^2'],
            fuzziness: 'AUTO',
            minimum_should_match: '66%',
        },
    };
}
function buildMultiSearchQuery(searchString, size = 5, articleIndex, categoryIndex) {
    return [
        { index: articleIndex },
        {
            size: size,
            query: buildSearchQuery(searchString),
            highlight: {
                order: 'score',
                fields: {
                    sArticleID: {},
                    sName: {},
                    sDescription: {},
                    sEAN: {},
                    'oProductInfo.sTitle': {},
                    'oProductInfo.sDescription': {},
                    'oProductInfo.sDescriptionShort': {},
                    sCustomerArticleID: {},
                    sAlternativeArticleID: {},
                    sExternalArticleID: {},
                    sPurchOrderNumber: {},
                    'oProductInfo.keywords': {},
                    sUniformNumber: {},
                },
            },
        },
        { index: categoryIndex },
        {
            size: size,
            query: buildCategorySearchQuery(searchString),
            highlight: {
                order: 'score',
                fields: {
                    sTitle: {},
                    keywords: {},
                },
            },
        },
    ];
}
function parseHits(hits) {
    const articles = hits.hits.map((article) => {
        return { score: article._score, ...article._source };
    });
    return { total: hits.total.value, hits: articles };
}

function filtersToSearchQuery(activeFilters, nestedPath) {
    const queryFilters = [];
    const groupedFilters = groupBy(activeFilters, 'name');
    Object.entries(groupedFilters).forEach((filterGroup) => {
        const filters = filterGroup[1].map((filter) => {
            return {
                nested: {
                    query: {
                        bool: {
                            filter: [
                                {
                                    term: {
                                        [`${nestedPath}.sName.keyword`]: {
                                            value: filter.name,
                                        },
                                    },
                                },
                                {
                                    terms: {
                                        [`${nestedPath}.sValue.keyword`]: [filter.value],
                                    },
                                },
                            ],
                        },
                    },
                    path: nestedPath,
                },
            };
        });
        queryFilters.push({
            bool: {
                should: [...filters],
            },
        });
    });
    return queryFilters;
}
function buildFacetedMultiSearchQuery(searchString, queryFilters, size = 12, page = 1, sortBy = 'relevance', articleIndex, categoryIndex) {
    const sort = Array.isArray(sortBy) ? sortBy : sortBy === 'title' ? [{ 'sName.keyword': 'asc' }] : [];
    return [
        { index: articleIndex },
        {
            from: page,
            size: size,
            sort: sort,
            query: buildSearchQuery(searchString),
            post_filter: {
                bool: {
                    must: [...filtersToSearchQuery(queryFilters, 'oFeatures')],
                },
            },
            aggs: {
                agg_full: {
                    nested: {
                        path: 'oFeatures',
                    },
                    aggs: {
                        attr_name: {
                            terms: {
                                field: 'oFeatures.sName.keyword',
                            },
                            aggs: {
                                feature: {
                                    top_hits: {
                                        size: 1,
                                        _source: {
                                            include: ['oFeatures.shtRepresentationType', 'oFeatures.shtWebshopFilter'],
                                        },
                                    },
                                },
                                attr_value: {
                                    terms: {
                                        field: 'oFeatures.sValue.keyword',
                                    },
                                    aggs: {
                                        option: {
                                            top_hits: {
                                                size: 1,
                                                _source: {
                                                    include: ['oFeatures.shtPos'],
                                                },
                                            },
                                        },
                                    },
                                },
                            },
                        },
                    },
                },
                agg_filtered: {
                    filter: {
                        bool: {
                            must: [buildSearchQuery(searchString), ...filtersToSearchQuery(queryFilters, 'oFeatures')],
                        },
                    },
                    aggs: {
                        filtered: {
                            nested: {
                                path: 'oFeatures',
                            },
                            aggs: {
                                attr_name: {
                                    terms: {
                                        field: 'oFeatures.sName.keyword',
                                    },
                                    aggs: {
                                        attr_value: {
                                            terms: {
                                                field: 'oFeatures.sValue.keyword',
                                            },
                                        },
                                    },
                                },
                            },
                        },
                    },
                },
            },
        },
        { index: categoryIndex },
        {
            size: 10,
            query: {
                query_string: { query: `*${searchString}*`, default_field: 'sTitle' },
            },
        },
    ];
}

class FacetedSearchResultService extends FacetedSearchAbstract {
    /**
     * Creates an instance of search service.
     */
    constructor(elasticSearch, store, catalogQuery, filterService, translocoService, catalogConfig) {
        super();
        this.elasticSearch = elasticSearch;
        this.store = store;
        this.catalogQuery = catalogQuery;
        this.filterService = filterService;
        this.translocoService = translocoService;
        this.catalogConfig = catalogConfig;
    }
    searchByFiltersCached(searchTerm, queryFilters, viewConfig) {
        const storedValues = this.store.getValue();
        const cachedHits = storedValues.cachedHits || [];
        const filteredHits = cachedHits.filter((hit) => hit.id === searchTerm);
        if (filteredHits.length) {
            const hit = filteredHits[0];
            if (isEqual(viewConfig, hit.config) && isEqual(queryFilters, hit.queryFilters)) {
                return of(hit.hits);
            }
            // if anything but page size changed, reset store
            if (!isEqualWith(viewConfig, hit.config, (_, _2, key) => key === 'pageSize' ? true : undefined) ||
                !isEqual(queryFilters, hit.queryFilters)) {
                this.store.update({
                    cachedHits: [...cachedHits.filter((cachedHit) => cachedHit.id !== searchTerm)],
                });
            }
        }
        this.store.setLoading(true);
        const query = buildFacetedMultiSearchQuery(searchTerm, queryFilters, viewConfig.pageSize, viewConfig.pageIndex, this.getSortMapping(viewConfig.sortBy || this.getDefaultSort()), this.elasticSearch.localizedIndex('article'), this.elasticSearch.localizedIndex('category'));
        return this.elasticSearch
            .execute(this.catalogConfig.apiUrl + '/es/msearch', {
            index: this.elasticSearch.localizedIndex('article'),
            query: query.map((entry) => JSON.stringify(entry)).join('\n') + '\n',
        })
            .pipe(map((response) => {
            this.doMappings(response);
            return this._setCachedHits(cachedHits, searchTerm, viewConfig, response.responses[0].hits, queryFilters);
        }), tap(() => this.store.setLoading(false)));
    }
    searchByFilters(searchTerm, queryFilters, page = 1, pageSize = 12, sortBy) {
        this.store.setLoading(true);
        const query = buildFacetedMultiSearchQuery(searchTerm, queryFilters, pageSize, page, this.getSortMapping(sortBy || this.getDefaultSort()), this.elasticSearch.localizedIndex('article'), this.elasticSearch.localizedIndex('category'));
        return this.elasticSearch
            .execute(this.catalogConfig.apiUrl + '/es/msearch', {
            index: this.elasticSearch.localizedIndex('article'),
            query: query.map((entry) => JSON.stringify(entry)).join('\n') + '\n',
        })
            .pipe(tap((response) => {
            this.doMappings(response);
        }), tap(() => this.store.setLoading(false)));
    }
    /** Calls all other mapping functions **/
    doMappings(response) {
        this._setFacets(response.responses[0].aggregations);
        this._setHits(response.responses[0].hits);
    }
    /** set the facets by es-aggregations and the provided category and filters */
    _setFacets(aggregations) {
        const fullAggregations = this._transformAggregations(aggregations.agg_full);
        const filteredAggregations = this._transformAggregations(aggregations.agg_filtered.filtered);
        const facets = this._buildFacets(fullAggregations, filteredAggregations);
        this.store.update({ facets });
    }
    /** saves the hits to the store */
    _setHits(hits) {
        const articles = hits.hits.map((product) => {
            return { score: product._score, ...product._source };
        });
        this.store.update({ hits: articles });
        this.store.update({ totalHits: hits.total.value });
    }
    /** saves the hits to the store */
    _setCachedHits(cachedHits, searchTerm, config, hits, queryFilters) {
        const articles = hits.hits.map((product) => {
            return { score: product._score, ...product._source };
        });
        this.store.update({
            cachedHits: [
                ...cachedHits.filter((cachedHit) => cachedHit.id !== searchTerm),
                { hits: articles, id: searchTerm, totalHits: hits.total.value, config, queryFilters },
            ],
        });
        return articles;
    }
    /** transforms es aggregations to key values object */
    _transformAggregations(aggregations) {
        return aggregations.attr_name.buckets.map((facet) => {
            const values = facet.attr_value.buckets.map((option) => {
                return { key: option.key, count: option.doc_count, disabled: false };
            });
            let newFacet = {
                key: facet.key,
                values,
                shtRepresentationType: 1,
                shtWebshopFilter: 1,
            };
            if (facet.feature && facet.feature.hits.hits.length > 0) {
                const featureConfig = facet.feature.hits.hits[0]._source;
                newFacet = {
                    ...newFacet,
                    shtRepresentationType: featureConfig.shtRepresentationType,
                    shtWebshopFilter: featureConfig.shtWebshopFilter,
                };
            }
            return newFacet;
        });
    }
    /** builds the facets by the transformed aggregations */
    _buildFacets(fullAggregations, filteredAggregations) {
        const facets = fullAggregations.map((facetFull) => {
            const facetFiltered = filteredAggregations.find((item) => item.key === facetFull.key);
            let newValues;
            if (facetFiltered) {
                newValues = facetFull.values.map((option) => {
                    const optionFiltered = facetFiltered.values.find((item) => item.key === option.key);
                    let optionCopy;
                    if (optionFiltered) {
                        optionCopy = { ...option, count: optionFiltered.count };
                    }
                    else {
                        optionCopy = { ...option, disabled: true, count: 0 };
                    }
                    return !this.isFirstFilter(facetFull.key) ? optionCopy : { ...optionCopy, disabled: false, count: option.count };
                });
            }
            else {
                newValues = facetFull.values.map((option) => {
                    return { ...option, disabled: true, count: 0 };
                });
            }
            newValues = newValues.map((facetOption) => {
                return {
                    ...facetOption,
                    isActive: this.isActiveFilter(facetFull.key, facetOption.key),
                };
            });
            return {
                ...facetFull,
                values: newValues.sort((a, b) => a.key.localeCompare(b.key, this.translocoService.getActiveLang(), { numeric: true })),
            };
        });
        return facets.sort((a, b) => (a['key'] > b['key'] ? 1 : a['key'] === b['key'] ? 0 : -1));
    }
    isActiveFilter(key, value) {
        return !!this.filterService.getFilters().find((filter) => filter.name === key && filter.value === value);
    }
    isFirstFilter(key) {
        return this.filterService.getFilters().length > 0 ? this.filterService.getFilters()[0].name === key : false;
    }
}
FacetedSearchResultService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FacetedSearchResultService, deps: [{ token: ElasticSearchService }, { token: FacetedSearchResultStore }, { token: CatalogQuery }, { token: FilterService }, { token: i1$1.TranslocoService }, { token: CATALOG_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
FacetedSearchResultService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FacetedSearchResultService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FacetedSearchResultService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: ElasticSearchService }, { type: FacetedSearchResultStore }, { type: CatalogQuery }, { type: FilterService }, { type: i1$1.TranslocoService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CATALOG_CONFIG]
                }] }]; } });

class LocalSearchStringService {
    constructor(localSearchStringStore) {
        this.localSearchStringStore = localSearchStringStore;
    }
    add(localSearchString) {
        this.localSearchStringStore.add(localSearchString);
    }
    update(id, localSearchString) {
        this.localSearchStringStore.update(id, localSearchString);
    }
    remove(id) {
        this.localSearchStringStore.remove(id);
    }
}
LocalSearchStringService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LocalSearchStringService, deps: [{ token: LocalSearchStringStore }], target: i0.ɵɵFactoryTarget.Injectable });
LocalSearchStringService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LocalSearchStringService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LocalSearchStringService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: LocalSearchStringStore }]; } });

class PriceService {
    constructor(store, http, catalogConfig, query, debouncer) {
        this.store = store;
        this.http = http;
        this.catalogConfig = catalogConfig;
        this.query = query;
        this.debouncer = debouncer;
    }
    get(articleId, unit, currency, qty = 0, factor1 = 0, factor2 = 0, factor3 = 0) {
        const params = new HttpParams({ encoder: new CustomHttpParameterCodec() })
            .set('articleId', articleId.toString())
            .set('unit', unit)
            .set('currency', currency)
            .set('qty', qty.toString())
            .set('factor1', factor1.toString())
            .set('factor2', factor2.toString())
            .set('factor3', factor3.toString());
        return this.debouncer.debounce(articleId, this.store, this.query, this.http.get(this.catalogConfig.apiUrl + `/price`, { params: params }));
    }
}
PriceService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PriceService, deps: [{ token: PriceStore }, { token: i1.HttpClient }, { token: CATALOG_CONFIG }, { token: PriceQuery }, { token: RequestDebounceService }], target: i0.ɵɵFactoryTarget.Injectable });
PriceService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PriceService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PriceService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: PriceStore }, { type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CATALOG_CONFIG]
                }] }, { type: PriceQuery }, { type: RequestDebounceService }]; } });

class ProductService extends NgEntityService {
    /**
     * Creates an instance of product service.
     */
    constructor(store, elasticSearch, parameterQuery, urlHelper, catalogConfig) {
        super(store);
        this.store = store;
        this.elasticSearch = elasticSearch;
        this.parameterQuery = parameterQuery;
        this.urlHelper = urlHelper;
        this.catalogConfig = catalogConfig;
    }
    getById(id) {
        const query = {
            query: {
                ids: {
                    values: [id],
                },
            },
        };
        return this.executeAndMap(query);
    }
    getByUrl(url) {
        return this.parameterQuery.params$.pipe(map((params) => {
            const catalog = StringHelper.trim('/' + this.urlHelper.normalize(params.sCatalogName), '/');
            const urlPath = StringHelper.trim(url.substring(3), '/');
            const path = `/${catalog}/${urlPath}/`;
            return {
                query: {
                    bool: {
                        filter: [
                            {
                                term: {
                                    'sUrlPath.keyword': {
                                        value: path,
                                    },
                                },
                            },
                        ],
                    },
                },
            };
        }), switchMap((query) => this.executeAndMap(query)));
    }
    executeAndMap(query) {
        return this.elasticSearch
            .execute(this.catalogConfig.apiUrl + '/es/search', {
            index: this.elasticSearch.localizedIndex('category'),
            query: JSON.stringify(query),
        })
            .pipe(map((response) => this.parseHits(response?.hits)), filter((products) => !!products?.hits), map((products) => products?.hits[0]), tap((product) => this.store.add(product)), shareReplay());
    }
    parseHits(hits) {
        const products = hits?.hits.map((product) => {
            return { score: product._score, ...product._source };
        });
        return { total: hits?.total?.value, hits: products };
    }
    getProductsByIds(productIds) {
        const query = {
            query: {
                bool: {
                    must: [{ terms: { lngGroup: productIds } }],
                },
            },
        };
        return this.elasticSearch
            .execute(this.catalogConfig.apiUrl + '/es/search', {
            index: this.elasticSearch.localizedIndex('category'),
            query: JSON.stringify(query),
        })
            .pipe(filter((response) => response.hits.hits.length > 0), map((response) => {
            return response.hits.hits.map((product) => product._source);
        }), tap((products) => {
            this.store.upsertMany(products);
        }));
    }
}
ProductService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProductService, deps: [{ token: ProductStore }, { token: ElasticSearchService }, { token: ParameterQuery }, { token: UrlHelperService }, { token: CATALOG_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
ProductService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProductService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProductService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: ProductStore }, { type: ElasticSearchService }, { type: ParameterQuery }, { type: UrlHelperService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CATALOG_CONFIG]
                }] }]; } });

/**
 * Handles page view related tasks
 * E.g. page size, sort, view mode, etc.
 */
class SearchPageViewFilterService extends PageViewFilterAbstract {
    constructor(store, location, urlHelper, catalogConfig) {
        super(store, location, urlHelper);
        this.catalogConfig = catalogConfig;
    }
    /**
     * Reset the store values by checking the url params
     */
    resetByUrlParams() {
        super.resetByUrlParams({ ...this.catalogConfig, ...this.defaults });
    }
}
SearchPageViewFilterService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SearchPageViewFilterService, deps: [{ token: FacetedSearchResultStore }, { token: i2.Location }, { token: UrlHelperService }, { token: CATALOG_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
SearchPageViewFilterService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SearchPageViewFilterService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SearchPageViewFilterService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: FacetedSearchResultStore }, { type: i2.Location }, { type: UrlHelperService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CATALOG_CONFIG]
                }] }]; } });

class SearchService {
    /**
     * Creates an instance of search service.
     */
    constructor(store, elasticSearch, catalogConfig) {
        this.store = store;
        this.elasticSearch = elasticSearch;
        this.catalogConfig = catalogConfig;
    }
    search(searchTerm) {
        const query = buildMultiSearchQuery(searchTerm, undefined, this.elasticSearch.localizedIndex('article'), this.elasticSearch.localizedIndex('category'));
        this.store.setLoading(true);
        return this.elasticSearch
            .execute(this.catalogConfig.apiUrl + '/es/msearch', {
            index: this.elasticSearch.localizedIndex('article'),
            query: query.map((entry) => JSON.stringify(entry)).join('\n') + '\n',
        })
            .pipe(tap((response) => {
            const articles = response.responses[0].hits.hits.map((article) => {
                return article._source;
            });
            const articleHighlights = response.responses[0].hits.hits.map((article) => {
                return {
                    id: article._id,
                    highlights: article.highlight,
                };
            });
            const categories = response.responses[1].hits.hits.map((category) => category._source);
            const categoryHighlights = response.responses[1].hits.hits.map((category) => {
                return {
                    id: category._id,
                    highlights: category.highlight,
                };
            });
            this.store.update({
                articles: articles,
                totalArticles: response.responses[0].hits.total.value,
                categories: categories,
                searchTerm: searchTerm,
                articleHighlights,
                categoryHighlights,
            });
            this.store.setLoading(false);
        }), catchError((error) => {
            this.store.setLoading(false);
            return throwError(() => error);
        }));
    }
    getHighlightKeys(id, highlights = []) {
        const hit = highlights.find((highlight) => highlight.id === id);
        return hit ? Object.keys(hit.highlights) : [];
    }
    resetStore() {
        this.store.reset();
    }
}
SearchService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SearchService, deps: [{ token: SearchStore }, { token: ElasticSearchService }, { token: CATALOG_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
SearchService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SearchService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SearchService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: SearchStore }, { type: ElasticSearchService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CATALOG_CONFIG]
                }] }]; } });

class StockAvailabilityService {
    constructor(store, http, catalogConfig, query, debouncer) {
        this.store = store;
        this.http = http;
        this.catalogConfig = catalogConfig;
        this.query = query;
        this.debouncer = debouncer;
    }
    get(sArticleID) {
        const encodedArticleID = encodeURIComponent(sArticleID);
        return this.debouncer.debounce(sArticleID, this.store, this.query, this.http.get(this.catalogConfig.apiUrl + `/stock-availability/${encodedArticleID}`));
    }
}
StockAvailabilityService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: StockAvailabilityService, deps: [{ token: StockAvailabilityStore }, { token: i1.HttpClient }, { token: CATALOG_CONFIG }, { token: StockAvailabilityQuery }, { token: RequestDebounceService }], target: i0.ɵɵFactoryTarget.Injectable });
StockAvailabilityService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: StockAvailabilityService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: StockAvailabilityService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: StockAvailabilityStore }, { type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CATALOG_CONFIG]
                }] }, { type: StockAvailabilityQuery }, { type: RequestDebounceService }]; } });

class CatalogModule {
    static forRoot(config) {
        return {
            ngModule: CatalogModule,
            providers: [
                {
                    provide: CATALOG_CONFIG,
                    useValue: { ...defaultCatalogConfig, ...config },
                },
            ],
        };
    }
}
CatalogModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CatalogModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CatalogModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: CatalogModule, imports: [CommonModule, TranslocoModule] });
CatalogModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CatalogModule, imports: [CommonModule, TranslocoModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CatalogModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, TranslocoModule],
                }]
        }] });

function buildFacetedBrandMultiSearchQuery(featureID, featureValue, queryFilters, size = 12, page = 1, sortBy = 'relevance', articleIndex) {
    const sort = Array.isArray(sortBy) ? sortBy : sortBy === 'title' ? [{ 'sName.keyword': 'asc' }] : [];
    return [
        { index: articleIndex },
        {
            from: page,
            size: size,
            sort: sort,
            query: {
                nested: {
                    path: 'oFeatures',
                    query: {
                        bool: {
                            must: [
                                {
                                    term: {
                                        'oFeatures.lngFeatureID': featureID,
                                    },
                                },
                                {
                                    term: {
                                        'oFeatures.sValue.keyword': featureValue,
                                    },
                                },
                            ],
                        },
                    },
                },
            },
            post_filter: {
                bool: {
                    must: [...filtersToSearchQuery(queryFilters, 'oFeatures')],
                },
            },
            aggs: {
                agg_full: {
                    nested: {
                        path: 'oFeatures',
                    },
                    aggs: {
                        attr_name: {
                            terms: {
                                field: 'oFeatures.sName.keyword',
                            },
                            aggs: {
                                feature: {
                                    top_hits: {
                                        size: 1,
                                        _source: {
                                            include: ['oFeatures.shtRepresentationType', 'oFeatures.shtWebshopFilter'],
                                        },
                                    },
                                },
                                attr_value: {
                                    terms: {
                                        field: 'oFeatures.sValue.keyword',
                                    },
                                    aggs: {
                                        option: {
                                            top_hits: {
                                                size: 1,
                                                _source: {
                                                    include: ['oFeatures.shtPos'],
                                                },
                                            },
                                        },
                                    },
                                },
                            },
                        },
                    },
                },
                agg_filtered: {
                    filter: {
                        bool: {
                            must: [
                                {
                                    nested: {
                                        path: 'oFeatures',
                                        query: {
                                            bool: {
                                                must: [
                                                    {
                                                        term: {
                                                            'oFeatures.lngFeatureID': featureID,
                                                        },
                                                    },
                                                    {
                                                        term: {
                                                            'oFeatures.sValue.keyword': featureValue,
                                                        },
                                                    },
                                                ],
                                            },
                                        },
                                    },
                                },
                                ...filtersToSearchQuery(queryFilters, 'oFeatures'),
                            ],
                        },
                    },
                    aggs: {
                        filtered: {
                            nested: {
                                path: 'oFeatures',
                            },
                            aggs: {
                                attr_name: {
                                    terms: {
                                        field: 'oFeatures.sName.keyword',
                                    },
                                    aggs: {
                                        attr_value: {
                                            terms: {
                                                field: 'oFeatures.sValue.keyword',
                                            },
                                        },
                                    },
                                },
                            },
                        },
                    },
                },
            },
        },
    ];
}

class FacetedSpecificFeatureResultService extends FacetedSearchAbstract {
    /**
     * Creates an instance of search service.
     */
    constructor(elasticSearch, store, catalogQuery, filterService, translocoService, catalogConfig) {
        super();
        this.elasticSearch = elasticSearch;
        this.store = store;
        this.catalogQuery = catalogQuery;
        this.filterService = filterService;
        this.translocoService = translocoService;
        this.catalogConfig = catalogConfig;
    }
    searchByFiltersCached(featureId, featureValue, queryFilters, viewConfig) {
        const storedValues = this.store.getValue();
        const cachedHits = storedValues.cachedHits || [];
        const filteredHits = cachedHits.filter((hit) => hit.id === `${featureId}__${featureValue}`);
        if (filteredHits.length) {
            const hit = filteredHits[0];
            if (isEqual(viewConfig, hit.config) && isEqual(queryFilters, hit.queryFilters)) {
                return of(hit.hits);
            }
            // if anything but page size changed, reset store
            if (!isEqualWith(viewConfig, hit.config, (_, _2, key) => key === 'pageSize' ? true : undefined) ||
                !isEqual(queryFilters, hit.queryFilters)) {
                this.store.update({
                    cachedHits: [...cachedHits.filter((cachedHit) => cachedHit.id !== `${featureId}__${featureValue}`)],
                });
            }
        }
        this.store.setLoading(true);
        const query = buildFacetedBrandMultiSearchQuery(featureId, featureValue, queryFilters, viewConfig.pageSize, viewConfig.pageIndex, this.getSortMapping(viewConfig.sortBy || this.getDefaultSort()), this.elasticSearch.localizedIndex('article'));
        return this.elasticSearch
            .execute(this.catalogConfig.apiUrl + '/es/msearch', {
            index: this.elasticSearch.localizedIndex('article'),
            query: query.map((entry) => JSON.stringify(entry)).join('\n') + '\n',
        })
            .pipe(map((response) => {
            this.doMappings(response);
            return this._setCachedHits(cachedHits, `${featureId}__${featureValue}`, viewConfig, response.responses[0].hits, queryFilters);
        }), tap(() => this.store.setLoading(false)));
    }
    searchByFilters(searchTerm, queryFilters, page = 1, pageSize = 12, sortBy) {
        this.store.setLoading(true);
        const query = buildFacetedMultiSearchQuery(searchTerm, queryFilters, pageSize, page, this.getSortMapping(sortBy || this.getDefaultSort()), this.elasticSearch.localizedIndex('article'), this.elasticSearch.localizedIndex('category'));
        return this.elasticSearch
            .execute(this.catalogConfig.apiUrl + '/es/msearch', {
            index: this.elasticSearch.localizedIndex('article'),
            query: query.map((entry) => JSON.stringify(entry)).join('\n') + '\n',
        })
            .pipe(tap((response) => {
            this.doMappings(response);
        }), tap(() => this.store.setLoading(false)));
    }
    /** Calls all other mapping functions **/
    doMappings(response) {
        this._setFacets(response.responses[0].aggregations);
        this._setHits(response.responses[0].hits);
    }
    /** set the facets by es-aggregations and the provided category and filters */
    _setFacets(aggregations) {
        const fullAggregations = this._transformAggregations(aggregations.agg_full);
        const filteredAggregations = this._transformAggregations(aggregations.agg_filtered.filtered);
        const facets = this._buildFacets(fullAggregations, filteredAggregations);
        this.store.update({ facets });
    }
    /** saves the hits to the store */
    _setHits(hits) {
        const articles = hits.hits.map((product) => {
            return { score: product._score, ...product._source };
        });
        this.store.update({ hits: articles });
        this.store.update({ totalHits: hits.total.value });
    }
    /** saves the hits to the store */
    _setCachedHits(cachedHits, searchTerm, config, hits, queryFilters) {
        const articles = hits.hits.map((product) => {
            return { score: product._score, ...product._source };
        });
        this.store.update({
            cachedHits: [
                ...cachedHits.filter((cachedHit) => cachedHit.id !== searchTerm),
                { hits: articles, id: searchTerm, totalHits: hits.total.value, config, queryFilters },
            ],
        });
        return articles;
    }
    /** transforms es aggregations to key values object */
    _transformAggregations(aggregations) {
        return aggregations.attr_name.buckets.map((facet) => {
            const values = facet.attr_value.buckets.map((option) => {
                return { key: option.key, count: option.doc_count, disabled: false };
            });
            let newFacet = {
                key: facet.key,
                values,
                shtRepresentationType: 1,
                shtWebshopFilter: 1,
            };
            if (facet.feature && facet.feature.hits.hits.length > 0) {
                const featureConfig = facet.feature.hits.hits[0]._source;
                newFacet = {
                    ...newFacet,
                    shtRepresentationType: featureConfig.shtRepresentationType,
                    shtWebshopFilter: featureConfig.shtWebshopFilter,
                };
            }
            return newFacet;
        });
    }
    /** builds the facets by the transformed aggregations */
    _buildFacets(fullAggregations, filteredAggregations) {
        const facets = fullAggregations.map((facetFull) => {
            const facetFiltered = filteredAggregations.find((item) => item.key === facetFull.key);
            let newValues;
            if (facetFiltered) {
                newValues = facetFull.values.map((option) => {
                    const optionFiltered = facetFiltered.values.find((item) => item.key === option.key);
                    let optionCopy;
                    if (optionFiltered) {
                        optionCopy = { ...option, count: optionFiltered.count };
                    }
                    else {
                        optionCopy = { ...option, disabled: true, count: 0 };
                    }
                    return !this.isFirstFilter(facetFull.key) ? optionCopy : { ...optionCopy, disabled: false, count: option.count };
                });
            }
            else {
                newValues = facetFull.values.map((option) => {
                    return { ...option, disabled: true, count: 0 };
                });
            }
            newValues = newValues.map((facetOption) => {
                return {
                    ...facetOption,
                    isActive: this.isActiveFilter(facetFull.key, facetOption.key),
                };
            });
            return {
                ...facetFull,
                values: newValues.sort((a, b) => a.key.localeCompare(b.key, this.translocoService.getActiveLang(), { numeric: true })),
            };
        });
        return facets.sort((a, b) => (a['key'] > b['key'] ? 1 : a['key'] === b['key'] ? 0 : -1));
    }
    isActiveFilter(key, value) {
        return !!this.filterService.getFilters().find((filter) => filter.name === key && filter.value === value);
    }
    isFirstFilter(key) {
        return this.filterService.getFilters().length > 0 ? this.filterService.getFilters()[0].name === key : false;
    }
}
FacetedSpecificFeatureResultService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FacetedSpecificFeatureResultService, deps: [{ token: ElasticSearchService }, { token: FacetedSearchResultStore }, { token: CatalogQuery }, { token: FilterService }, { token: i1$1.TranslocoService }, { token: CATALOG_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
FacetedSpecificFeatureResultService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FacetedSpecificFeatureResultService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FacetedSpecificFeatureResultService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: ElasticSearchService }, { type: FacetedSearchResultStore }, { type: CatalogQuery }, { type: FilterService }, { type: i1$1.TranslocoService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CATALOG_CONFIG]
                }] }]; } });

/**
 * A service for building an image path.
 * Should be used statically
 */
class ImagePathHelperService {
    static getImagePath(imagePath, cloudinary) {
        if (!imagePath) {
            return `https://res.cloudinary.com/${cloudinary.cloudName}/image/fetch/${cloudinary.fetchImageUrl}/no-image.png`;
        }
        if (cloudinary.active) {
            return `https://res.cloudinary.com/${cloudinary.cloudName}/image/fetch/w_500,h_500,c_pad/${cloudinary.fetchImageUrl}/${imagePath}`;
        }
        return imagePath;
    }
}
ImagePathHelperService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ImagePathHelperService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ImagePathHelperService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ImagePathHelperService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ImagePathHelperService, decorators: [{
            type: Injectable
        }] });

const CLOUDINARY_CONFIG = new InjectionToken('CloudinaryConfig');

class CloudinaryImagePathPipe {
    constructor(cloudinaryConfig) {
        this.cloudinaryConfig = cloudinaryConfig;
    }
    transform(value) {
        return ImagePathHelperService.getImagePath(value, this.cloudinaryConfig);
    }
}
CloudinaryImagePathPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CloudinaryImagePathPipe, deps: [{ token: CLOUDINARY_CONFIG }], target: i0.ɵɵFactoryTarget.Pipe });
CloudinaryImagePathPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: CloudinaryImagePathPipe, name: "cloudinaryImagePath" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CloudinaryImagePathPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'cloudinaryImagePath',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [CLOUDINARY_CONFIG]
                }] }]; } });

class CloudinaryModule {
    static forRoot(config) {
        return {
            ngModule: CloudinaryModule,
            providers: [
                {
                    provide: CLOUDINARY_CONFIG,
                    useValue: config,
                },
            ],
        };
    }
}
CloudinaryModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CloudinaryModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CloudinaryModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: CloudinaryModule, declarations: [CloudinaryImagePathPipe], imports: [CommonModule], exports: [CloudinaryImagePathPipe] });
CloudinaryModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CloudinaryModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CloudinaryModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [CloudinaryImagePathPipe],
                    exports: [CloudinaryImagePathPipe],
                }]
        }] });

let ContactStore = class ContactStore extends EntityStore {
    constructor() {
        super();
    }
};
ContactStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ContactStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ContactStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ContactStore, providedIn: 'root' });
ContactStore = __decorate([
    StoreConfig({ name: 'contacts', idKey: 'lngContactId' })
], ContactStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ContactStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

let ContactQuery = class ContactQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
};
ContactQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ContactQuery, deps: [{ token: ContactStore }], target: i0.ɵɵFactoryTarget.Injectable });
ContactQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ContactQuery, providedIn: 'root' });
ContactQuery = __decorate([
    QueryConfig({ sortBy: 'sFirstName', sortByOrder: Order.ASC })
], ContactQuery);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ContactQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: ContactStore }]; } });

class ContactService extends NgEntityService {
    constructor(store) {
        super(store);
        this.store = store;
    }
    getAll() {
        return this.getHttp()
            .get(this.getConfig().baseUrl + '/contacts/all')
            .pipe(tap((entities) => {
            this.store.set(entities);
        }));
    }
    getAllByCustomerId(lngCustomerId) {
        return this.getHttp()
            .get(this.getConfig().baseUrl + '/sales-rep/customer-contacts/' + lngCustomerId)
            .pipe(tap((entities) => this.store.set(entities)));
    }
}
ContactService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ContactService, deps: [{ token: ContactStore }], target: i0.ɵɵFactoryTarget.Injectable });
ContactService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ContactService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ContactService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: ContactStore }]; } });

class ContactModule {
}
ContactModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ContactModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ContactModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: ContactModule, imports: [CommonModule] });
ContactModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ContactModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ContactModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }] });

let CompareService = class CompareService {
    constructor(platformId) {
        this.platformId = platformId;
        this.data = {
            items: [],
        };
        this.itemsSubject$ = new BehaviorSubject([]);
        this.onAddingSubject$ = new Subject();
        this.items$ = this.itemsSubject$.pipe(untilDestroyed(this));
        this.onAdding$ = this.onAddingSubject$.asObservable();
        if (isPlatformBrowser(this.platformId)) {
            this.load();
        }
    }
    add(product) {
        // timer only for demo
        return of(undefined).pipe(map(() => {
            this.onAddingSubject$.next(product);
            const index = this.data.items.findIndex((item) => item.sArticleID === product.sArticleID);
            if (index === -1) {
                this.data.items.push(product);
                this.save();
            }
        }));
    }
    remove(product) {
        // timer only for demo
        return of(undefined).pipe(map(() => {
            const index = this.data.items.findIndex((item) => item.sArticleID === product.sArticleID);
            if (index !== -1) {
                this.data.items.splice(index, 1);
                this.save();
            }
        }));
    }
    removeAll() {
        this.data.items = [];
        this.save();
    }
    save() {
        localStorage.setItem('compareItems', JSON.stringify(this.data.items));
        this.itemsSubject$.next(this.data.items);
    }
    load() {
        const items = localStorage.getItem('compareItems');
        if (items) {
            this.data.items = JSON.parse(items);
            this.itemsSubject$.next(this.data.items);
        }
    }
};
CompareService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CompareService, deps: [{ token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable });
CompareService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CompareService, providedIn: 'root' });
CompareService = __decorate([
    UntilDestroy()
], CompareService);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CompareService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }]; } });

let CountryStore = class CountryStore extends EntityStore {
    constructor() {
        super();
    }
};
CountryStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CountryStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CountryStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CountryStore, providedIn: 'root' });
CountryStore = __decorate([
    StoreConfig({ name: 'country', idKey: 'shtCountryID' })
], CountryStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CountryStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class CountryQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
}
CountryQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CountryQuery, deps: [{ token: CountryStore }], target: i0.ɵɵFactoryTarget.Injectable });
CountryQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CountryQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CountryQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: CountryStore }]; } });

class CountryService {
    constructor(store, http) {
        this.store = store;
        this.http = http;
    }
    get() {
        return this.http.get('p48apihost/countries').pipe(tap((countries) => this.store.set(countries)));
    }
}
CountryService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CountryService, deps: [{ token: CountryStore }, { token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
CountryService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CountryService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CountryService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: CountryStore }, { type: i1.HttpClient }]; } });

class CountryModule {
}
CountryModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CountryModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CountryModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: CountryModule, imports: [CommonModule] });
CountryModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CountryModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CountryModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }] });

let CustomerStore = class CustomerStore extends EntityStore {
    constructor() {
        super();
    }
};
CustomerStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CustomerStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CustomerStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CustomerStore, providedIn: 'root' });
CustomerStore = __decorate([
    StoreConfig({ name: 'customers', idKey: 'lngCustomerID' })
], CustomerStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CustomerStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

let CustomerQuery = class CustomerQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
};
CustomerQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CustomerQuery, deps: [{ token: CustomerStore }], target: i0.ɵɵFactoryTarget.Injectable });
CustomerQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CustomerQuery, providedIn: 'root' });
CustomerQuery = __decorate([
    QueryConfig({ sortBy: 'sCompany1', sortByOrder: Order.ASC })
], CustomerQuery);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CustomerQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: CustomerStore }]; } });

class CustomerService {
    constructor(store, http) {
        this.store = store;
        this.http = http;
    }
    getById(id) {
        return this.http.get(`p48apihost/customers/${id}`).pipe(tap((customer) => this.store.set([customer])));
    }
}
CustomerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CustomerService, deps: [{ token: CustomerStore }, { token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
CustomerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CustomerService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CustomerService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: CustomerStore }, { type: i1.HttpClient }]; } });

class CustomerModule {
}
CustomerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CustomerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CustomerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: CustomerModule, imports: [CommonModule] });
CustomerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CustomerModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CustomerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }] });

let FileTreeStore = class FileTreeStore extends EntityStore {
    constructor() {
        super();
    }
};
FileTreeStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FileTreeStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
FileTreeStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FileTreeStore, providedIn: 'root' });
FileTreeStore = __decorate([
    StoreConfig({ name: 'download-tree', idKey: 'sName', resettable: true })
], FileTreeStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FileTreeStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class FileTreeQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
}
FileTreeQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FileTreeQuery, deps: [{ token: FileTreeStore }], target: i0.ɵɵFactoryTarget.Injectable });
FileTreeQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FileTreeQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FileTreeQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: FileTreeStore }]; } });

const DOWNLOAD_CONFIG = new InjectionToken('DownloadConfig');

/**
 * A service to manage file-tree entities,
 */
class FileTreeService extends NgEntityService {
    constructor(store, downloadConfig) {
        super(store);
        this.store = store;
        this.downloadConfig = downloadConfig;
    }
    downloadFile(node) {
        const params = new HttpParams().append('sPath', node.sPath);
        return this.getHttp()
            .get(this.downloadConfig.fileDownloadUrl + '/download-file', {
            params,
            responseType: 'blob',
        })
            .subscribe((res) => {
            this.saveFileAs(res, node.sName, node.sType);
        });
    }
    downloadTree() {
        return this.getHttp().get(this.downloadConfig.fileDownloadUrl + '/download-tree');
    }
    saveFileAs(fileBlob, fileName, fileExtension) {
        const fileType = 'application/' + fileExtension;
        const file = new Blob([fileBlob], { type: fileType });
        saveAs(file, fileName);
    }
}
FileTreeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FileTreeService, deps: [{ token: FileTreeStore }, { token: DOWNLOAD_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
FileTreeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FileTreeService, providedIn: 'any' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FileTreeService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'any' }]
        }], ctorParameters: function () { return [{ type: FileTreeStore }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOWNLOAD_CONFIG]
                }] }]; } });

class DownloadModule {
    static forRoot(config) {
        return {
            ngModule: DownloadModule,
            providers: [{ provide: DOWNLOAD_CONFIG, useValue: config }],
        };
    }
}
DownloadModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DownloadModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DownloadModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: DownloadModule, imports: [CommonModule, TranslocoModule] });
DownloadModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DownloadModule, imports: [CommonModule, TranslocoModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DownloadModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, TranslocoModule],
                }]
        }] });

/**
 * A factory function that creates FavoriteItem
 */
function createFavoriteItem({ gListID = '', decQuantity = 1, oArticle = undefined, shtItemID = 0, fakeId = '', }) {
    return {
        fakeId,
        gListID,
        decQuantity,
        oArticle,
        shtItemID,
    };
}

/**
 * A factory function that creates FavoriteList
 */
function createFavoriteList({ sListname = 'Merkliste' }) {
    return {
        sListname,
    };
}

let FavoriteItemStore = class FavoriteItemStore extends EntityStore {
    constructor() {
        super();
    }
    akitaPreAddEntity(x) {
        return {
            ...x,
            fakeId: [x.gListID, x.shtItemID].join(','),
        };
    }
};
FavoriteItemStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteItemStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
FavoriteItemStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteItemStore, providedIn: 'root' });
FavoriteItemStore = __decorate([
    StoreConfig({ name: 'favorite-item', idKey: 'fakeId', resettable: true })
], FavoriteItemStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteItemStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class FavoriteItemQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
    selectFavoriteItems(favoriteListId) {
        return this.selectAll({
            filterBy: ({ gListID }) => gListID === favoriteListId,
        });
    }
}
FavoriteItemQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteItemQuery, deps: [{ token: FavoriteItemStore }], target: i0.ɵɵFactoryTarget.Injectable });
FavoriteItemQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteItemQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteItemQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: FavoriteItemStore }]; } });

let FavoriteListStore = class FavoriteListStore extends EntityStore {
    constructor() {
        super();
    }
};
FavoriteListStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteListStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
FavoriteListStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteListStore, providedIn: 'root' });
FavoriteListStore = __decorate([
    StoreConfig({ name: 'favorites', idKey: 'gListID', resettable: true })
], FavoriteListStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteListStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

let FavoriteListQuery = class FavoriteListQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
};
FavoriteListQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteListQuery, deps: [{ token: FavoriteListStore }], target: i0.ɵɵFactoryTarget.Injectable });
FavoriteListQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteListQuery, providedIn: 'root' });
FavoriteListQuery = __decorate([
    QueryConfig({ sortBy: 'sListname', sortByOrder: Order.ASC })
], FavoriteListQuery);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteListQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: FavoriteListStore }]; } });

let FavoriteItemLocalStore = class FavoriteItemLocalStore extends EntityStore {
    constructor() {
        super();
    }
    akitaPreAddEntity(x) {
        return {
            ...x,
            fakeId: [x.gListID, x.shtItemID].join(','),
        };
    }
};
FavoriteItemLocalStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteItemLocalStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
FavoriteItemLocalStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteItemLocalStore, providedIn: 'root' });
FavoriteItemLocalStore = __decorate([
    StoreConfig({ name: 'favorite-item-local', idKey: 'fakeId', resettable: true })
], FavoriteItemLocalStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteItemLocalStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class FavoriteItemLocalQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
}
FavoriteItemLocalQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteItemLocalQuery, deps: [{ token: FavoriteItemLocalStore }], target: i0.ɵɵFactoryTarget.Injectable });
FavoriteItemLocalQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteItemLocalQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteItemLocalQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: FavoriteItemLocalStore }]; } });

const FAVORITE_CONFIG = new InjectionToken('FavoriteConfig');

class FavoriteHttpService {
    constructor(http, favoriteConfig) {
        this.http = http;
        this.favoriteConfig = favoriteConfig;
    }
    createFavoriteItem(favoriteItem, favoriteListId) {
        return this.http.post(this.favoriteConfig.apiUrl + '/favorites/' + favoriteListId + '/items', favoriteItem);
    }
    createFavoriteList(favoriteList) {
        return this.http.post(this.favoriteConfig.apiUrl + '/favorites', favoriteList);
    }
    deleteFavoriteItem(favoriteItem) {
        return this.http.delete(this.favoriteConfig.apiUrl + '/favorites/' + favoriteItem.gListID + '/items/' + favoriteItem.shtItemID);
    }
    deleteFavoriteList(favoriteList) {
        return this.http.delete(this.favoriteConfig.apiUrl + '/favorites/' + favoriteList.gListID);
    }
    getFavoriteItems(favoriteListId) {
        return this.http.get(this.favoriteConfig.apiUrl + '/favorites/' + favoriteListId + '/items').pipe(map((favoriteItems) => {
            /**
             * The API returns an object instead of an empty array, when no items are in a list
             */
            if (!Array.isArray(favoriteItems)) {
                return [];
            }
            return favoriteItems;
        }));
    }
    getFavoriteLists() {
        return this.http.get(this.favoriteConfig.apiUrl + '/favorites').pipe(map((favoriteLists) => {
            /**
             * The API returns an object instead of an empty array, when no list is saved yet
             */
            if (!Array.isArray(favoriteLists)) {
                return [];
            }
            return favoriteLists;
        }));
    }
    getFavoriteListById(favoriteListId) {
        return this.http.get(this.favoriteConfig.apiUrl + '/favorites/' + favoriteListId);
    }
    updateFavoriteItem(favoriteItem) {
        return this.http.put(this.favoriteConfig.apiUrl + '/favorites/' + favoriteItem.gListID + '/items/' + favoriteItem.shtItemID, favoriteItem);
    }
    updateFavoriteList(favoriteList) {
        return this.http.put(this.favoriteConfig.apiUrl + '/favorites/' + favoriteList.gListID, favoriteList);
    }
    setActiveFavorite(user) {
        return of(user?.gActiveBookmarkListID);
    }
}
FavoriteHttpService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteHttpService, deps: [{ token: i1.HttpClient }, { token: FAVORITE_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
FavoriteHttpService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteHttpService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteHttpService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [FAVORITE_CONFIG]
                }] }]; } });

class FavoriteLocalService {
    constructor(favoriteItemLocalQuery, favoriteItemLocalStore) {
        this.favoriteItemLocalQuery = favoriteItemLocalQuery;
        this.favoriteItemLocalStore = favoriteItemLocalStore;
    }
    createFavoriteItem(favoriteItem) {
        const list = (this.favoriteItemLocalQuery.getAll() || []).sort((f1, f2) => f2.shtItemID - f1.shtItemID);
        const itemId = (list[0]?.shtItemID ?? 0) + 1;
        const newFavoriteItem = {
            ...favoriteItem,
            gListID: FavoriteLocalService.ACTIVE_FAVORITE_ID,
            shtItemID: itemId,
        };
        return of({
            ...newFavoriteItem,
            fakeId: [newFavoriteItem.gListID, newFavoriteItem.shtItemID].join(','),
        }).pipe(tap(() => this.favoriteItemLocalStore.add(newFavoriteItem)));
    }
    createFavoriteList(favoriteList) {
        return of(favoriteList);
    }
    deleteFavoriteItem(favoriteItem) {
        return of(undefined).pipe(tap(this.favoriteItemLocalStore.remove(favoriteItem.fakeId)));
    }
    deleteFavoriteList(_) {
        return of(undefined);
    }
    getFavoriteItems(_) {
        return this.favoriteItemLocalQuery.selectAll();
    }
    getFavoriteLists() {
        return of([]);
    }
    getFavoriteListById(_) {
        return of(undefined);
    }
    updateFavoriteItem(favoriteItem) {
        return of(undefined).pipe(tap(() => this.favoriteItemLocalStore.update(favoriteItem.fakeId, favoriteItem)), switchMap(() => this.favoriteItemLocalQuery.selectEntity(favoriteItem.fakeId)));
    }
    updateFavoriteList(favoriteList) {
        return of(favoriteList);
    }
    setActiveFavorite(_) {
        return of(FavoriteLocalService.ACTIVE_FAVORITE_ID);
    }
}
FavoriteLocalService.ACTIVE_FAVORITE_ID = '1';
FavoriteLocalService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteLocalService, deps: [{ token: FavoriteItemLocalQuery }, { token: FavoriteItemLocalStore }], target: i0.ɵɵFactoryTarget.Injectable });
FavoriteLocalService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteLocalService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteLocalService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: FavoriteItemLocalQuery }, { type: FavoriteItemLocalStore }]; } });

var FavoriteService_1;
// @dynamic
let FavoriteService = FavoriteService_1 = class FavoriteService {
    constructor(transloco, translocoLocale, authService, favoriteHttpService, favoriteLocalService, favoriteItemStore, favoriteListStore, userService) {
        this.transloco = transloco;
        this.translocoLocale = translocoLocale;
        this.authService = authService;
        this.favoriteHttpService = favoriteHttpService;
        this.favoriteLocalService = favoriteLocalService;
        this.favoriteItemStore = favoriteItemStore;
        this.favoriteListStore = favoriteListStore;
        this.userService = userService;
    }
    createFavoriteItem(favoriteItem, favoriteListId) {
        return this.iif(this.favoriteHttpService.createFavoriteItem(favoriteItem, favoriteListId), this.favoriteLocalService.createFavoriteItem(favoriteItem)).pipe(tap((favoriteItemResponse) => this.favoriteItemStore.add(favoriteItemResponse)), map(() => favoriteItem));
    }
    deleteFavoriteItem(favoriteItem) {
        return this.iif(this.favoriteHttpService.deleteFavoriteItem(favoriteItem), this.favoriteLocalService.deleteFavoriteItem(favoriteItem)).pipe(tap(() => this.favoriteItemStore.remove(favoriteItem.fakeId)));
    }
    createFavoriteList(favoriteList) {
        return this.iif(this.favoriteHttpService.createFavoriteList(favoriteList), this.favoriteLocalService.createFavoriteList(favoriteList)).pipe(tap((favoriteListResponse) => this.favoriteListStore.add(favoriteListResponse)));
    }
    deleteFavoriteList(favoriteList) {
        return this.iif(this.favoriteHttpService.deleteFavoriteList(favoriteList), this.favoriteLocalService.deleteFavoriteList(favoriteList)).pipe(tap(() => this.favoriteListStore.remove(favoriteList.gListID)));
    }
    getFavoriteItems(favoriteListId) {
        return this.iif(this.favoriteHttpService.getFavoriteItems(favoriteListId), this.favoriteLocalService.getFavoriteItems(favoriteListId)).pipe(tap((favoriteItems) => this.favoriteItemStore.upsertMany(this.generateFavoriteItemFakeId(favoriteItems))));
    }
    getFavoriteLists() {
        return this.iif(this.favoriteHttpService.getFavoriteLists(), this.favoriteLocalService.getFavoriteLists()).pipe(tap((favoriteLists) => this.favoriteListStore.upsertMany(favoriteLists)));
    }
    getFavoriteListById(favoriteListId) {
        return this.iif(this.favoriteHttpService.getFavoriteListById(favoriteListId), this.favoriteLocalService.getFavoriteListById(favoriteListId)).pipe(tap((favoriteList) => {
            if (favoriteList) {
                this.favoriteListStore.upsert(favoriteList.gListID, favoriteList);
            }
        }));
    }
    updateFavoriteItem(favoriteItem) {
        return this.iif(this.favoriteHttpService.updateFavoriteItem(favoriteItem), this.favoriteLocalService.updateFavoriteItem(favoriteItem)).pipe(tap((favoriteItemResponse) => {
            if (favoriteItemResponse) {
                this.favoriteItemStore.update([favoriteItemResponse.gListID, favoriteItemResponse.shtItemID].join(','), favoriteItemResponse);
            }
        }));
    }
    updateFavoriteList(favoriteList) {
        return this.iif(this.favoriteHttpService.updateFavoriteList(favoriteList), this.favoriteLocalService.updateFavoriteList(favoriteList)).pipe(tap((favoriteList) => this.favoriteListStore.update(favoriteList.gListID, favoriteList)));
    }
    setActiveFavoriteId(id) {
        const user = this.authService.getAuthUser();
        this.userService
            .update(user.lngContactID, { ...user, gActiveBookmarkListID: id })
            .pipe(switchMap((user) => this.authService.updateAuthUser({ gActiveBookmarkListID: user.gActiveBookmarkListID })), untilDestroyed(this), take(1))
            .subscribe();
        this.favoriteListStore.setActive(id);
    }
    setActiveFavorite(user) {
        return this.iif(this.favoriteHttpService.setActiveFavorite(user), this.favoriteLocalService.setActiveFavorite(user)).pipe(tap((id) => {
            if (id) {
                this.favoriteListStore.setActive(id);
            }
        }));
    }
    /**
     * Converts any locally saved favorites to a newly created remote favorites
     * and removes the local items in that process
     */
    convertLocalToRemote() {
        const end$ = new Subject();
        return this.favoriteLocalService.getFavoriteItems(undefined).pipe(first(), switchMap((favorites) => iif(
        // no or empty favorites
        () => !favorites?.length, 
        // true
        of(favorites), 
        // false
        combineLatest([of(favorites), this.createEmptyList()]).pipe(tap(([_, list]) => this.setActiveFavoriteId(list.gListID)), mergeMap(([favorites, list]) => {
            const creationObservables = [];
            for (const favorite of favorites) {
                creationObservables.push(this.favoriteLocalService.deleteFavoriteItem(favorite));
                creationObservables.push(this.createFavoriteItem({ ...favorite, gListID: list.gListID }, list.gListID));
            }
            // end stream, so toArray() can work
            creationObservables.push(of(undefined).pipe(tap(() => {
                end$.next(true);
                end$.complete();
            })));
            return merge(creationObservables);
        }), mergeMap(($req) => $req, 1), takeUntil(end$), filter((f) => !!f), toArray()))));
    }
    activateRecentOrCreateNew() {
        return this.getFavoriteLists().pipe(switchMap((list) => iif(
        // user has already favorites
        () => list.length > 0, 
        // true
        of(list.sort((a, b) => b.dtP48EntryDate - a.dtP48EntryDate)[0]), 
        // false
        this.createEmptyList())), tap((list) => this.setActiveFavoriteId(list.gListID)));
    }
    clearFavoriteLists() {
        this.favoriteListStore.reset();
    }
    createEmptyList() {
        return this.transloco
            .selectTranslate('domain.new-list.name', { date: this.translocoLocale.localizeDate(Date.now(), undefined, { dateStyle: 'medium', timeStyle: 'short' }) }, FavoriteService_1.TRANSLOCO_SCOPE)
            .pipe(switchMap((name) => this.createFavoriteList(createFavoriteList({ sListname: name }))));
    }
    iif(positive, negative) {
        return this.authService.authUser$.pipe(map((user) => !!user), switchMap((status) => (status ? positive : negative)), first());
    }
    generateFavoriteItemFakeId(favoriteItems) {
        return favoriteItems.map((item) => {
            return { ...item, fakeId: [item.gListID, item.shtItemID].join(',') };
        });
    }
};
/**
 * When injecting transloco scope, it is the wrong one, so it is added hard-coded here
 */
FavoriteService.TRANSLOCO_SCOPE = 'favorite';
FavoriteService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteService, deps: [{ token: i1$1.TranslocoService }, { token: i2$2.TranslocoLocaleService }, { token: AuthService }, { token: FavoriteHttpService }, { token: FavoriteLocalService }, { token: FavoriteItemStore }, { token: FavoriteListStore }, { token: UserService }], target: i0.ɵɵFactoryTarget.Injectable });
FavoriteService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteService, providedIn: 'root' });
__decorate([
    TrackMethod({
        provide: ([favoriteItem]) => {
            const article = favoriteItem.oArticle;
            const quantity = favoriteItem.decQuantity || 1;
            return {
                name: 'add_to_wishlist',
                payload: {
                    ecommerce: {
                        currency: article.oPriceInfo?.sCurrencyCode,
                        value: GoogleTagManagerHelper.articlePrice(article) * quantity,
                        items: [{ ...GoogleTagManagerHelper.articleToItem(article), quantity }],
                    },
                },
            };
        },
    }),
    Hook({
        id: { type: 'WorkflowCreateHook', entity: 'CreateFavoriteItemHook' },
        returnType: HookReturnType.OBSERVABLE,
    })
], FavoriteService.prototype, "createFavoriteItem", null);
__decorate([
    Hook({
        id: { type: 'WorkflowDeleteHook', entity: 'DeleteFavoriteItemHook' },
        returnType: HookReturnType.OBSERVABLE,
    })
], FavoriteService.prototype, "deleteFavoriteItem", null);
FavoriteService = FavoriteService_1 = __decorate([
    UntilDestroy()
], FavoriteService);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1$1.TranslocoService }, { type: i2$2.TranslocoLocaleService }, { type: AuthService }, { type: FavoriteHttpService }, { type: FavoriteLocalService }, { type: FavoriteItemStore }, { type: FavoriteListStore }, { type: UserService }]; }, propDecorators: { createFavoriteItem: [], deleteFavoriteItem: [] } });

class FavoriteModule {
    static forRoot(config) {
        return {
            ngModule: FavoriteModule,
            providers: [
                {
                    provide: FAVORITE_CONFIG,
                    useValue: config,
                },
            ],
        };
    }
}
FavoriteModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FavoriteModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: FavoriteModule, imports: [CommonModule, TranslocoModule, TranslocoLocaleModule] });
FavoriteModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteModule, imports: [CommonModule, TranslocoModule, TranslocoLocaleModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FavoriteModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, TranslocoModule, TranslocoLocaleModule],
                }]
        }] });

let LanguageStore = class LanguageStore extends EntityStore {
    constructor() {
        super();
    }
};
LanguageStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LanguageStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
LanguageStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LanguageStore, providedIn: 'root' });
LanguageStore = __decorate([
    StoreConfig({ name: 'languages', idKey: 'shtLanguageID' })
], LanguageStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LanguageStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

let LanguageQuery = class LanguageQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
};
LanguageQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LanguageQuery, deps: [{ token: LanguageStore }], target: i0.ɵɵFactoryTarget.Injectable });
LanguageQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LanguageQuery, providedIn: 'root' });
LanguageQuery = __decorate([
    QueryConfig({ sortBy: 'languageName', sortByOrder: Order.ASC })
], LanguageQuery);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LanguageQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: LanguageStore }]; } });

class LanguageService {
    constructor(query, store, http, debouncer) {
        this.query = query;
        this.store = store;
        this.http = http;
        this.debouncer = debouncer;
    }
    get() {
        return this.debouncer.debounceAll(this.store, this.http.get('p48apihost/languages'));
    }
}
LanguageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LanguageService, deps: [{ token: LanguageQuery }, { token: LanguageStore }, { token: i1.HttpClient }, { token: RequestDebounceService }], target: i0.ɵɵFactoryTarget.Injectable });
LanguageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LanguageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LanguageService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: LanguageQuery }, { type: LanguageStore }, { type: i1.HttpClient }, { type: RequestDebounceService }]; } });

class LanguageNamePipePipe {
    constructor(languageQuery) {
        this.languageQuery = languageQuery;
    }
    transform(value) {
        return this.languageQuery.selectAll().pipe(map((languages) => {
            if (languages.length > 0 && value) {
                return languages.find((lang) => lang.shtLanguageID === value).sLanguageName;
            }
            else {
                return '';
            }
        }));
    }
}
LanguageNamePipePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LanguageNamePipePipe, deps: [{ token: LanguageQuery }], target: i0.ɵɵFactoryTarget.Pipe });
LanguageNamePipePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: LanguageNamePipePipe, name: "language" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LanguageNamePipePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'language',
                }]
        }], ctorParameters: function () { return [{ type: LanguageQuery }]; } });

class LanguageModule {
}
LanguageModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LanguageModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
LanguageModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: LanguageModule, declarations: [LanguageNamePipePipe], imports: [CommonModule], exports: [LanguageNamePipePipe] });
LanguageModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LanguageModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LanguageModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [LanguageNamePipePipe],
                    exports: [LanguageNamePipePipe],
                }]
        }] });

// @dynamic
class LinkTagService {
    constructor(document) {
        this.document = document;
    }
    /**
     * Add a link tag without further checks
     */
    add(linkTag) {
        return this.createElement(linkTag);
    }
    /**
     * Creates or updates the canonical tag
     */
    updateCanonicalTag(href) {
        return this.getOrCreateElement({ rel: 'canonical', href });
    }
    /**
     * Removes the canonical tag
     */
    removeCanonicalTag() {
        const element = this.getSingleElement({ rel: 'canonical' });
        if (element)
            element.remove();
        return element;
    }
    getSingleElement(linkTag) {
        return this.document.querySelector(`link[rel=${linkTag.rel}]`) ?? undefined;
    }
    getOrCreateElement(linkTag) {
        const element = this.getSingleElement(linkTag);
        if (element !== undefined)
            return element;
        return this.createElement(linkTag);
    }
    createElement(linkTag) {
        const element = this.document.head.appendChild(this.document.createElement('link'));
        this.setLinkElementAttributes(linkTag, element);
        return element;
    }
    setLinkElementAttributes(linkTag, element) {
        Object.keys(linkTag).forEach((prop) => {
            element[prop] = linkTag[prop];
        });
    }
}
LinkTagService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LinkTagService, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
LinkTagService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LinkTagService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LinkTagService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

/**
 * Injects or updates a JSON-LD script tag
 *
 * @see https://developers.google.com/search/docs/advanced/structured-data/intro-structured-data
 * @see https://developers.google.com/search/docs/advanced/structured-data/product#json-ld
 * @see https://search.google.com/test/rich-results
 *
 * @example
 * This can be generated
 * {
 *   "@context": "https://schema.org/",
 *   "@type": "Product",
 *   "name": "Executive Anvil",
 *   "image": [
 *     "https://example.com/photos/1x1/photo.jpg",
 *     "https://example.com/photos/4x3/photo.jpg",
 *     "https://example.com/photos/16x9/photo.jpg"
 *   ],
 *   "description": "Sleeker than ACME's Classic Anvil, the Executive Anvil is perfect for the business traveler looking for something to drop from a height.",
 *   "sku": "0446310786",
 *   "mpn": "925872",
 *   "brand": {
 *     "@type": "Brand",
 *     "name": "ACME"
 *   },
 *   "review": {
 *     "@type": "Review",
 *     "reviewRating": {
 *       "@type": "Rating",
 *       "ratingValue": "4",
 *       "bestRating": "5"
 *     },
 *     "author": {
 *       "@type": "Person",
 *       "name": "Fred Benson"
 *     }
 *   },
 *   "aggregateRating": {
 *     "@type": "AggregateRating",
 *     "ratingValue": "4.4",
 *     "reviewCount": "89"
 *   },
 *   "offers": {
 *     "@type": "Offer",
 *     "url": "https://example.com/anvil",
 *     "priceCurrency": "USD",
 *     "price": "119.99",
 *     "priceValidUntil": "2020-11-20",
 *     "itemCondition": "https://schema.org/UsedCondition",
 *     "availability": "https://schema.org/InStock"
 *   }
 * }
 */
class JsonLdService {
    constructor(document) {
        this.document = document;
    }
    set(data) {
        this.inject(data);
    }
    reset() {
        const ldJsonScriptTag = this.document.head.querySelector(`script[type='application/ld+json']`);
        if (ldJsonScriptTag) {
            ldJsonScriptTag.remove();
        }
    }
    getObject(type, rawData, context = 'https://schema.org') {
        let data = {
            '@context': context,
            '@type': type,
        };
        if (rawData) {
            data = {
                ...data,
                ...rawData,
            };
        }
        return data;
    }
    inject(data) {
        let ldJsonScriptTag = this.document.head.querySelector(`script[type='application/ld+json']`);
        if (ldJsonScriptTag) {
            ldJsonScriptTag.textContent = JSON.stringify(data);
        }
        else {
            ldJsonScriptTag = this.document.createElement('script');
            ldJsonScriptTag.setAttribute('type', 'application/ld+json');
            ldJsonScriptTag.textContent = JSON.stringify(data);
            this.document.head.appendChild(ldJsonScriptTag);
        }
    }
}
JsonLdService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: JsonLdService, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
JsonLdService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: JsonLdService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: JsonLdService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

var PageMetaService_1;
let PageMetaService = PageMetaService_1 = class PageMetaService {
    constructor(scope, metaTagService, linkTagService, titleService, jsonLdService, trackingFactory, transloco, urlHelper) {
        this.scope = scope;
        this.metaTagService = metaTagService;
        this.linkTagService = linkTagService;
        this.titleService = titleService;
        this.jsonLdService = jsonLdService;
        this.trackingFactory = trackingFactory;
        this.transloco = transloco;
        this.urlHelper = urlHelper;
        this.initiallySet = false;
        this.defaultMetadata = {};
        combineLatest([
            this.transloco.selectTranslate('util-page-meta.default.title', undefined, PageMetaService_1.TRANSLOCO_SCOPE),
            this.transloco.selectTranslate('util-page-meta.default.description', undefined, PageMetaService_1.TRANSLOCO_SCOPE),
            this.transloco.selectTranslate('util-page-meta.default.author', undefined, PageMetaService_1.TRANSLOCO_SCOPE),
            this.transloco.selectTranslate('util-page-meta.default.keywords', undefined, PageMetaService_1.TRANSLOCO_SCOPE),
            this.transloco.selectTranslate('util-page-meta.default.type', undefined, PageMetaService_1.TRANSLOCO_SCOPE),
            this.transloco.selectTranslate('util-page-meta.default.image', undefined, PageMetaService_1.TRANSLOCO_SCOPE),
        ])
            .pipe(first(), untilDestroyed(this))
            .subscribe(async ([title, description, author, keywords, type, image]) => {
            this.defaultMetadata = { title, description, author, keywords, type, image };
            if (this.initiallySet) {
                return;
            }
            await this.updateMetadata(this.defaultMetadata, true, true);
        });
        this.linkTagService.add({ rel: 'alternate', hreflang: 'x-default', href: this.urlHelper.enforceAbsoluteUrl('') });
        for (const lang of this.transloco.getAvailableLangs()) {
            this.linkTagService.add({
                rel: 'alternate',
                hreflang: lang.toString(),
                href: this.urlHelper.enforceAbsoluteUrl(`/${lang}`),
            });
        }
    }
    async updateMetadata(metadata, index = true, noTracking = false) {
        this.initiallySet = true;
        const pageMetadata = {
            ...this.defaultMetadata,
            ...metadata,
            title: metadata.title
                ? `${metadata.title} ${PageMetaService_1.SUFFIX_SEPARATOR} ${this.defaultMetadata.title}`
                : this.defaultMetadata.title,
        };
        if (pageMetadata.canonical) {
            this.linkTagService.updateCanonicalTag(this.urlHelper.enforceAbsoluteUrl(pageMetadata.canonical));
        }
        else {
            this.linkTagService.removeCanonicalTag();
        }
        let metaTags = this.generateMetaDefinitions(pageMetadata);
        metaTags = [
            ...metaTags,
            { property: 'og:url', content: `${this.urlHelper.enforceAbsoluteUrl()}` },
            { name: 'robots', content: index ? 'index, follow' : 'noindex' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' },
            { 'http-equiv': 'Content-Type', content: 'text/html; charset=utf-8' },
        ];
        for (const metaTag of metaTags) {
            if (!metaTag.content) {
                this.metaTagService.removeTag(metaTag.name ? `name="${metaTag.name}"` : `property="${metaTag.property}"`);
                continue;
            }
            this.metaTagService.updateTag(metaTag);
        }
        this.titleService.setTitle(pageMetadata.title);
        if (metadata.jsonLd) {
            let jsonLd = metadata.jsonLd;
            if (!Array.isArray(jsonLd)) {
                jsonLd = [jsonLd];
            }
            jsonLd = jsonLd.map((jld) => this.jsonLdService.getObject(jld['@type'], jld, jld['@context']));
            this.jsonLdService.set(jsonLd);
        }
        else {
            this.jsonLdService.reset();
        }
        if (!noTracking) {
            await this.trackingFactory.trackPage({
                title: pageMetadata.title,
                url: this.urlHelper.enforceAbsoluteUrl(),
            });
        }
    }
    generateMetaDefinitions(metadata) {
        return [
            { name: 'title', content: metadata.title },
            { property: 'og:title', content: metadata.title },
            { name: 'description', content: metadata.description.replace(/<.*?>/g, ' ') },
            { property: 'og:description', content: metadata.description.replace(/<.*?>/g, ' ') },
            { name: 'author', content: metadata.author },
            { property: 'og:author', content: metadata.author },
            { name: 'keywords', content: metadata.keywords },
            { property: 'og:type', content: metadata.type },
            {
                property: 'og:image',
                content: this.urlHelper.enforceAbsoluteUrl(metadata.image),
            },
        ];
    }
};
PageMetaService.SUFFIX_SEPARATOR = '-';
PageMetaService.TRANSLOCO_SCOPE = 'shared';
PageMetaService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PageMetaService, deps: [{ token: TRANSLOCO_SCOPE }, { token: i1$3.Meta }, { token: LinkTagService }, { token: i1$3.Title }, { token: JsonLdService }, { token: TrackingFactory }, { token: i1$1.TranslocoService }, { token: UrlHelperService }], target: i0.ɵɵFactoryTarget.Injectable });
PageMetaService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PageMetaService, providedIn: 'root' });
PageMetaService = PageMetaService_1 = __decorate([
    UntilDestroy()
], PageMetaService);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PageMetaService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [TRANSLOCO_SCOPE]
                }] }, { type: i1$3.Meta }, { type: LinkTagService }, { type: i1$3.Title }, { type: JsonLdService }, { type: TrackingFactory }, { type: i1$1.TranslocoService }, { type: UrlHelperService }]; } });

/**
 * Class decorator for adding page meta
 * This class should primarily being added to Features which will have their own route.
 * By default, the page is set to "index, follow". This can be disabled.
 * By default, the page meta call is also sent to tracking. This can be disabled.
 */
function PageMeta(options) {
    return (constructor) => {
        /**
         * Subject to manage unsubscription
         */
        let destroyed = new Subject();
        const originalPageEnterEvent = constructor.prototype.ngOnInit;
        const originalPageExitEvent = constructor.prototype.ngOnDestroy;
        constructor.prototype.ngOnInit = function (...args) {
            destroyed = new Subject();
            const pageMetaService = InjectorClass.inject(PageMetaService);
            const errorService = InjectorClass.inject(ErrorService);
            options
                .provide(this)
                .pipe(takeUntil(destroyed), catchError((error) => of(errorService.add({ label: 'PageMeta.ngOnInit()', ...error, error: error }))))
                .subscribe((meta) => pageMetaService.updateMetadata(meta, options.index, options.noTracking));
            originalPageEnterEvent && typeof originalPageEnterEvent === 'function' && originalPageEnterEvent.apply(this, args);
        };
        constructor.prototype.ngOnDestroy = function (...args) {
            destroyed.next();
            destroyed.complete();
            originalPageExitEvent && typeof originalPageExitEvent === 'function' && originalPageExitEvent.apply(this, args);
        };
    };
}

class PageMetaModule {
}
PageMetaModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PageMetaModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
PageMetaModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: PageMetaModule, imports: [CommonModule, TranslocoModule] });
PageMetaModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PageMetaModule, imports: [CommonModule, TranslocoModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PageMetaModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, TranslocoModule],
                }]
        }] });

let ProjectStore = class ProjectStore extends EntityStore {
    constructor() {
        super();
    }
};
ProjectStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProjectStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ProjectStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProjectStore, providedIn: 'root' });
ProjectStore = __decorate([
    StoreConfig({ name: 'projects', idKey: 'lngProjectID' })
], ProjectStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProjectStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

let ProjectQuery = class ProjectQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
};
ProjectQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProjectQuery, deps: [{ token: ProjectStore }], target: i0.ɵɵFactoryTarget.Injectable });
ProjectQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProjectQuery, providedIn: 'root' });
ProjectQuery = __decorate([
    QueryConfig({ sortBy: 'sProjectName', sortByOrder: Order.ASC })
], ProjectQuery);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProjectQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: ProjectStore }]; } });

function createProject({ lngProjectID = 0, lngCustomerID = 0, sCity = '', sCompany1 = '', sCompany2 = '', sCountry = '', sCountryCode = '', sProjectName = '', sStreet = '', sZipCode = '', shtStatus = 1, Status = 1, dtEntryDate = new Date(), }) {
    return {
        lngProjectID,
        lngCustomerID,
        sCity,
        sCompany1,
        sCompany2,
        sCountry,
        sCountryCode,
        sProjectName,
        sStreet,
        sZipCode,
        shtStatus,
        Status,
        dtEntryDate,
    };
}

class ProjectService extends NgEntityService {
    constructor(store) {
        super(store);
    }
}
ProjectService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProjectService, deps: [{ token: ProjectStore }], target: i0.ɵɵFactoryTarget.Injectable });
ProjectService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProjectService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProjectService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: ProjectStore }]; } });

class ProjectModule {
}
ProjectModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProjectModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ProjectModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: ProjectModule, imports: [CommonModule] });
ProjectModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProjectModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ProjectModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }] });

let SalesCreditNoteStore = class SalesCreditNoteStore extends EntityStore {
    constructor() {
        super();
    }
};
SalesCreditNoteStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesCreditNoteStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SalesCreditNoteStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesCreditNoteStore, providedIn: 'root' });
SalesCreditNoteStore = __decorate([
    StoreConfig({ name: 'sales-credit-notes', idKey: 'lngOrderID', resettable: true })
], SalesCreditNoteStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesCreditNoteStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class SalesCreditNoteQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
}
SalesCreditNoteQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesCreditNoteQuery, deps: [{ token: SalesCreditNoteStore }], target: i0.ɵɵFactoryTarget.Injectable });
SalesCreditNoteQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesCreditNoteQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesCreditNoteQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: SalesCreditNoteStore }]; } });

let SalesCreditNoteItemStore = class SalesCreditNoteItemStore extends EntityStore {
    constructor() {
        super();
    }
};
SalesCreditNoteItemStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesCreditNoteItemStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SalesCreditNoteItemStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesCreditNoteItemStore, providedIn: 'root' });
SalesCreditNoteItemStore = __decorate([
    StoreConfig({ name: 'sales-credit-note-item' })
], SalesCreditNoteItemStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesCreditNoteItemStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class SalesCreditNoteItemQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
    selectByCreditNoteId(id) {
        return this.selectAll({
            filterBy: ({ lngOrderID }) => lngOrderID === id,
        });
    }
    hasItemsByCreditNoteId(id) {
        return !!this.getAll({
            filterBy: ({ lngOrderID }) => lngOrderID === id,
        }).length;
    }
}
SalesCreditNoteItemQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesCreditNoteItemQuery, deps: [{ token: SalesCreditNoteItemStore }], target: i0.ɵɵFactoryTarget.Injectable });
SalesCreditNoteItemQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesCreditNoteItemQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesCreditNoteItemQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: SalesCreditNoteItemStore }]; } });

/** Creates a document module configuration injection token */
const SALES_CREDIT_NOTE_CONFIG = new InjectionToken('SalesCreditNoteConfig');

/**
 * A service that manages offer entities
 */
class SalesCreditNoteService {
    constructor(http, store, config) {
        this.http = http;
        this.store = store;
        this.config = config;
    }
    getByFilters(filters) {
        this.store.setLoading(true);
        let params = new HttpParams({ encoder: new CustomHttpParameterCodec() });
        filters.forEach((filter) => {
            params = params.append(filter.id, filter.value);
        });
        return this.http.get(this.config.apiUrl + '/sales-credit-notes', { params }).pipe(filter((res) => res.data), tap((res) => {
            this.store.upsertMany(res.data);
        }), finalize(() => this.store.setLoading(false)));
    }
    get(urlOrParams) {
        return queryPaginated(this.http, this.config.apiUrl + '/sales-credit-notes', urlOrParams).pipe(tap((response) => this.store.upsertMany(response.data)));
    }
    getById(lngOrderId) {
        return this.http
            .get(this.config.apiUrl + '/sales-credit-notes/' + lngOrderId)
            .pipe(tap((salesOrder) => this.store.upsert(salesOrder.lngOrderID, salesOrder)));
    }
}
SalesCreditNoteService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesCreditNoteService, deps: [{ token: i1.HttpClient }, { token: SalesCreditNoteStore }, { token: SALES_CREDIT_NOTE_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
SalesCreditNoteService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesCreditNoteService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesCreditNoteService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: SalesCreditNoteStore }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [SALES_CREDIT_NOTE_CONFIG]
                }] }]; } });

class SalesCreditNoteItemService {
    constructor(http, store, config) {
        this.http = http;
        this.store = store;
        this.config = config;
    }
    get(lngOrderID) {
        return of(undefined).pipe(tap(() => this.store.setLoading(true)), switchMap(() => this.http.get(this.config.apiUrl + '/sales-credit-notes/' + lngOrderID + '/items/')), map((res) => {
            return res.data.map((item) => {
                return { ...item, id: `${item.lngOrderID}-${item.shtFixedItemID}` };
            });
        }), tap((entities) => {
            this.store.upsertMany(entities);
        }), finalize(() => this.store.setLoading(false)));
    }
}
SalesCreditNoteItemService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesCreditNoteItemService, deps: [{ token: i1.HttpClient }, { token: SalesCreditNoteItemStore }, { token: SALES_CREDIT_NOTE_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
SalesCreditNoteItemService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesCreditNoteItemService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesCreditNoteItemService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: SalesCreditNoteItemStore }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [SALES_CREDIT_NOTE_CONFIG]
                }] }]; } });

class SalesCreditNoteModule {
    static forRoot(config) {
        return {
            ngModule: SalesCreditNoteModule,
            providers: [
                {
                    provide: SALES_CREDIT_NOTE_CONFIG,
                    useValue: config,
                },
            ],
        };
    }
}
SalesCreditNoteModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesCreditNoteModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SalesCreditNoteModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: SalesCreditNoteModule, imports: [CommonModule] });
SalesCreditNoteModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesCreditNoteModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesCreditNoteModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }] });

const SALES_CREDIT_NOTE_PAGINATOR = new InjectionToken('SalesCreditNotePaginator', {
    providedIn: 'root',
    factory: () => {
        return new Paginator(inject(SalesCreditNoteQuery)).withControls().withRange();
    },
});

class SalesCreditNoteFilterService extends AkitaFiltersPlugin {
    constructor(offerQuery) {
        super(offerQuery, { filtersStoreName: 'sales-credit-note-filter' });
        this.offerQuery = offerQuery;
        const fromDate = new Date();
        fromDate.setMonth(fromDate.getMonth() - 3);
        this.setFilter({
            id: 'dtEntryDate[gte]',
            value: fromDate.valueOf(),
            predicate: (entity) => {
                return entity.dtEntryDate >= fromDate.valueOf();
            },
        });
        this.setFilter({
            id: 'intPageIndex',
            value: 1,
            predicate: (_) => true,
        });
        this.setFilter({
            id: 'intPageSize',
            value: 10,
            predicate: (_) => true,
        });
    }
    /**
     * Sets orderBy
     */
    setOrderBy(by, order) {
        this.setSortBy({
            sortBy: by,
            sortByOrder: order === '+' ? Order.ASC : Order.DESC,
        });
    }
}
SalesCreditNoteFilterService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesCreditNoteFilterService, deps: [{ token: SalesCreditNoteQuery }], target: i0.ɵɵFactoryTarget.Injectable });
SalesCreditNoteFilterService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesCreditNoteFilterService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesCreditNoteFilterService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: SalesCreditNoteQuery }]; } });

let SalesDocumentStore = class SalesDocumentStore extends EntityStore {
    constructor() {
        super();
    }
};
SalesDocumentStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesDocumentStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SalesDocumentStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesDocumentStore, providedIn: 'root' });
SalesDocumentStore = __decorate([
    StoreConfig({ name: 'sales-document', idKey: 'lngDocumentID', resettable: true })
], SalesDocumentStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesDocumentStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

let SalesDocumentQuery = class SalesDocumentQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
    selectDocumentsByOrderId(lngOrderIDP) {
        return this.selectAll({
            filterBy: ({ lngOrderID }) => lngOrderID === lngOrderIDP,
        });
    }
    selectDocumentsByOrderIdAndOrderType(lngOrderIDP, sOrderTypeP) {
        return this.selectAll({
            filterBy: ({ lngOrderID, sOrderType }) => lngOrderID === lngOrderIDP && sOrderType === sOrderTypeP,
        });
    }
};
SalesDocumentQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesDocumentQuery, deps: [{ token: SalesDocumentStore }], target: i0.ɵɵFactoryTarget.Injectable });
SalesDocumentQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesDocumentQuery, providedIn: 'root' });
SalesDocumentQuery = __decorate([
    QueryConfig({ sortBy: 'dtPrintDate', sortByOrder: Order.DESC })
], SalesDocumentQuery);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesDocumentQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: SalesDocumentStore }]; } });

/** Creates a document module configuration injection token */
const SALES_DOCUMENT_CONFIG = new InjectionToken('SalesDocumentConfig');

class SalesDocumentService {
    constructor(salesDocumentQuery, store, http, salesDocumentConfig) {
        this.salesDocumentQuery = salesDocumentQuery;
        this.store = store;
        this.http = http;
        this.salesDocumentConfig = salesDocumentConfig;
    }
    // TODO Fix any type
    download(document) {
        if (this.salesDocumentConfig.useArchive) {
            return this.downloadFromArchive(document);
        }
        else {
            return this.reprintDocument(document);
        }
    }
    getById(documentId) {
        return this.http
            .get(this.salesDocumentConfig.apiUrl + '/sales-documents?lngOrderID=' + documentId)
            .pipe(tap((res) => this.store.upsertMany(res.data)));
    }
    getByIdAndOrderType(orderId, orderType) {
        return this.http
            .get(this.salesDocumentConfig.apiUrl + '/sales-documents?lngOrderID=' + orderId + '&sOrderType=' + orderType)
            .pipe(tap((res) => this.store.upsertMany(res.data)));
    }
    // TODO Fix any type
    reprintDocument(salesDocument) {
        const acceptHeaders = new HttpHeaders();
        acceptHeaders.append('Accept', '*/*');
        acceptHeaders.append('Accept-Encoding', 'gzip, deflate, br');
        acceptHeaders.append('Connection', 'keep-alive');
        const encodedDocumentType = encodeURIComponent(salesDocument.sDocumentType);
        return this.http
            .get(`${this.salesDocumentConfig.apiUrl}/sales-documents?sDocumentType=${encodedDocumentType}&lngDocumentID=${salesDocument.lngDocumentID}`, {
            headers: acceptHeaders,
            responseType: 'blob',
        })
            .pipe(take(1), tap((res) => {
            this.saveFileAs(res, `${salesDocument.sDocumentTypeName}_${salesDocument.lngDocumentID.toString()}.pdf`);
        }));
    }
    // TODO Fix any type
    downloadFromArchive(salesDocument) {
        const acceptHeaders = new HttpHeaders();
        acceptHeaders.append('Accept', '*/*');
        acceptHeaders.append('Accept-Encoding', 'gzip, deflate, br');
        acceptHeaders.append('Connection', 'keep-alive');
        const encodedOrderType = encodeURIComponent(salesDocument.sOrderType);
        const encodedDocumentType = encodeURIComponent(salesDocument.sDocumentType);
        return this.http
            .get(`${this.salesDocumentConfig.apiUrl}/archive-documents?sProgram=SalesDocument&lngReferenceID=${salesDocument.lngOrderID}&sOrderType=${encodedOrderType}&sDocumentType=${encodedDocumentType}`, {
            headers: acceptHeaders,
            responseType: 'blob',
        })
            .pipe(take(1), tap((res) => {
            this.saveFileAs(res, `${salesDocument.sDocumentTypeName}_${salesDocument.lngDocumentID.toString()}.pdf`);
        }));
    }
    saveFileAs(fileBlob, fileName) {
        const file = new Blob([fileBlob], { type: 'application/pdf' });
        saveAs(file, fileName);
    }
}
SalesDocumentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesDocumentService, deps: [{ token: SalesDocumentQuery }, { token: SalesDocumentStore }, { token: i1.HttpClient }, { token: SALES_DOCUMENT_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
SalesDocumentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesDocumentService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesDocumentService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: SalesDocumentQuery }, { type: SalesDocumentStore }, { type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [SALES_DOCUMENT_CONFIG]
                }] }]; } });

class SalesDocumentFilterService extends AkitaFiltersPlugin {
    constructor(store, salesDocumentQuery, http, salesDocumentConfig) {
        super(salesDocumentQuery);
        this.store = store;
        this.salesDocumentQuery = salesDocumentQuery;
        this.http = http;
        this.salesDocumentConfig = salesDocumentConfig;
        this.defaultFilters = {
            'dtDatePrinted[gte]': new Date().setMonth(new Date().getMonth() - 3).valueOf(),
            'dtDatePrinted[lte]': new Date().setDate(new Date().getDate() + 1).valueOf(),
        };
        this.withServer(this.getOnChangeFilter());
    }
    getOnChangeFilter() {
        return (filtersNormalized) => {
            return this.http
                .get(`${this.salesDocumentConfig.apiUrl}/sales-documents`, {
                params: Object.assign(this.defaultFilters, filtersNormalized),
            })
                .pipe(map((res) => {
                return res.data;
            }));
        };
    }
}
SalesDocumentFilterService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesDocumentFilterService, deps: [{ token: SalesDocumentStore }, { token: SalesDocumentQuery }, { token: i1.HttpClient }, { token: SALES_DOCUMENT_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
SalesDocumentFilterService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesDocumentFilterService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesDocumentFilterService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: SalesDocumentStore }, { type: SalesDocumentQuery }, { type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [SALES_DOCUMENT_CONFIG]
                }] }]; } });

class SalesDocumentModule {
    static forRoot(config) {
        return {
            ngModule: SalesDocumentModule,
            providers: [
                {
                    provide: SALES_DOCUMENT_CONFIG,
                    useValue: config,
                },
            ],
        };
    }
}
SalesDocumentModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesDocumentModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SalesDocumentModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: SalesDocumentModule, imports: [CommonModule] });
SalesDocumentModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesDocumentModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesDocumentModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }] });

let SalesOrderStore = class SalesOrderStore extends EntityStore {
    constructor() {
        super();
    }
};
SalesOrderStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOrderStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SalesOrderStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOrderStore, providedIn: 'root' });
SalesOrderStore = __decorate([
    StoreConfig({ name: 'sales-orders', idKey: 'lngOrderID', resettable: true })
], SalesOrderStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOrderStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class SalesOrderQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
}
SalesOrderQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOrderQuery, deps: [{ token: SalesOrderStore }], target: i0.ɵɵFactoryTarget.Injectable });
SalesOrderQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOrderQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOrderQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: SalesOrderStore }]; } });

let SalesOrderItemStore = class SalesOrderItemStore extends EntityStore {
    constructor() {
        super();
    }
};
SalesOrderItemStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOrderItemStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SalesOrderItemStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOrderItemStore, providedIn: 'root' });
SalesOrderItemStore = __decorate([
    StoreConfig({ name: 'sales-order-item' })
], SalesOrderItemStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOrderItemStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class SalesOrderItemQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
    selectByOrderId(id) {
        return this.selectAll({
            filterBy: ({ lngOrderID }) => lngOrderID === id,
        });
    }
    hasItemsByOrderId(id) {
        return !!this.getAll({
            filterBy: ({ lngOrderID }) => lngOrderID === id,
        }).length;
    }
}
SalesOrderItemQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOrderItemQuery, deps: [{ token: SalesOrderItemStore }], target: i0.ɵɵFactoryTarget.Injectable });
SalesOrderItemQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOrderItemQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOrderItemQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: SalesOrderItemStore }]; } });

/** Creates a document module configuration injection token */
const SALES_ORDER_CONFIG = new InjectionToken('SalesOrderConfig');

/**
 * A service that manages order entities
 */
class SalesOrderService {
    constructor(http, store, config) {
        this.http = http;
        this.store = store;
        this.config = config;
    }
    getByFilters(filters) {
        this.store.setLoading(true);
        let params = new HttpParams({ encoder: new CustomHttpParameterCodec() });
        filters.forEach((filter) => {
            params = params.append(filter.id, filter.value);
        });
        return this.http.get(this.config.apiUrl + '/sales-orders', { params }).pipe(filter((res) => res.data), tap((res) => {
            this.store.upsertMany(res.data);
        }), finalize(() => this.store.setLoading(false)));
    }
    get(urlOrParams) {
        return queryPaginated(this.http, this.config.apiUrl + '/sales-orders', urlOrParams).pipe(tap((response) => this.store.upsertMany(response.data)));
    }
    getById(lngOrderId) {
        return this.http
            .get(this.config.apiUrl + '/sales-orders/' + lngOrderId)
            .pipe(tap((salesOrder) => this.store.upsert(salesOrder.lngOrderID, salesOrder)));
    }
}
SalesOrderService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOrderService, deps: [{ token: i1.HttpClient }, { token: SalesOrderStore }, { token: SALES_ORDER_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
SalesOrderService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOrderService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOrderService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: SalesOrderStore }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [SALES_ORDER_CONFIG]
                }] }]; } });

class SalesOrderItemService {
    constructor(http, store, config) {
        this.http = http;
        this.store = store;
        this.config = config;
    }
    get(lngOrderID) {
        return of(undefined).pipe(tap(() => this.store.setLoading(true)), switchMap(() => this.http.get(this.config.apiUrl + '/sales-orders/' + lngOrderID + '/items/')), map((res) => {
            return res.data.map((item) => {
                return { ...item, id: `${item.lngOrderID}-${item.shtFixedItemID}` };
            });
        }), tap((entities) => {
            this.store.upsertMany(entities);
        }), finalize(() => this.store.setLoading(false)));
    }
}
SalesOrderItemService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOrderItemService, deps: [{ token: i1.HttpClient }, { token: SalesOrderItemStore }, { token: SALES_ORDER_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
SalesOrderItemService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOrderItemService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOrderItemService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: SalesOrderItemStore }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [SALES_ORDER_CONFIG]
                }] }]; } });

class SalesOrderModule {
    static forRoot(config) {
        return {
            ngModule: SalesOrderModule,
            providers: [
                {
                    provide: SALES_ORDER_CONFIG,
                    useValue: config,
                },
            ],
        };
    }
}
SalesOrderModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOrderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SalesOrderModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: SalesOrderModule, imports: [CommonModule] });
SalesOrderModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOrderModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOrderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }] });

const SALES_ORDER_PAGINATOR = new InjectionToken('SalesOrderPaginator', {
    providedIn: 'root',
    factory: () => {
        return new Paginator(inject(SalesOrderQuery)).withControls().withRange();
    },
});

class SalesOrderFilterService extends AkitaFiltersPlugin {
    constructor(offerQuery) {
        super(offerQuery, { filtersStoreName: 'sales-offer-filter' });
        this.offerQuery = offerQuery;
        const fromDate = new Date();
        fromDate.setMonth(fromDate.getMonth() - 3);
        this.setFilter({
            id: 'dtEntryDate[gte]',
            value: fromDate.valueOf(),
            predicate: (entity) => {
                return entity.dtEntryDate >= fromDate.valueOf();
            },
        });
        this.setFilter({
            id: 'intPageIndex',
            value: 1,
            predicate: (_) => true,
        });
        this.setFilter({
            id: 'intPageSize',
            value: 10,
            predicate: (_) => true,
        });
    }
    /**
     * Sets orderBy
     */
    setOrderBy(by, order) {
        this.setSortBy({
            sortBy: by,
            sortByOrder: order === '+' ? Order.ASC : Order.DESC,
        });
    }
}
SalesOrderFilterService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOrderFilterService, deps: [{ token: SalesOrderQuery }], target: i0.ɵɵFactoryTarget.Injectable });
SalesOrderFilterService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOrderFilterService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOrderFilterService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: SalesOrderQuery }]; } });

let SalesOfferStore = class SalesOfferStore extends EntityStore {
    constructor() {
        super();
    }
};
SalesOfferStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOfferStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SalesOfferStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOfferStore, providedIn: 'root' });
SalesOfferStore = __decorate([
    StoreConfig({ name: 'sales-offers', idKey: 'lngOrderID', resettable: true })
], SalesOfferStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOfferStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class SalesOfferQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
}
SalesOfferQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOfferQuery, deps: [{ token: SalesOfferStore }], target: i0.ɵɵFactoryTarget.Injectable });
SalesOfferQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOfferQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOfferQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: SalesOfferStore }]; } });

let SalesOfferItemStore = class SalesOfferItemStore extends EntityStore {
    constructor() {
        super();
    }
};
SalesOfferItemStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOfferItemStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SalesOfferItemStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOfferItemStore, providedIn: 'root' });
SalesOfferItemStore = __decorate([
    StoreConfig({ name: 'sales-offer-item' })
], SalesOfferItemStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOfferItemStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class SalesOfferItemQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
    selectByOfferId(id) {
        return this.selectAll({
            filterBy: ({ lngOrderID }) => lngOrderID === id,
        });
    }
    hasItemsByOfferId(id) {
        return !!this.getAll({
            filterBy: ({ lngOrderID }) => lngOrderID === id,
        }).length;
    }
}
SalesOfferItemQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOfferItemQuery, deps: [{ token: SalesOfferItemStore }], target: i0.ɵɵFactoryTarget.Injectable });
SalesOfferItemQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOfferItemQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOfferItemQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: SalesOfferItemStore }]; } });

/** Creates a document module configuration injection token */
const SALES_OFFER_CONFIG = new InjectionToken('SalesOfferConfig');

/**
 * A service that manages offer entities
 */
class SalesOfferService {
    constructor(http, store, config) {
        this.http = http;
        this.store = store;
        this.config = config;
    }
    getByFilters(filters) {
        this.store.setLoading(true);
        let params = new HttpParams({ encoder: new CustomHttpParameterCodec() });
        filters.forEach((filter) => {
            params = params.append(filter.id, filter.value);
        });
        return this.http.get(this.config.apiUrl + '/sales-offers', { params }).pipe(filter((res) => res.data), tap((res) => {
            this.store.upsertMany(res.data);
        }), finalize(() => this.store.setLoading(false)));
    }
    get(urlOrParams) {
        return queryPaginated(this.http, this.config.apiUrl + '/sales-offers', urlOrParams).pipe(tap((response) => this.store.upsertMany(response.data)));
    }
    getById(lngOrderId) {
        return this.http
            .get(this.config.apiUrl + '/sales-offers/' + lngOrderId)
            .pipe(tap((salesOrder) => this.store.upsert(salesOrder.lngOrderID, salesOrder)));
    }
}
SalesOfferService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOfferService, deps: [{ token: i1.HttpClient }, { token: SalesOfferStore }, { token: SALES_OFFER_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
SalesOfferService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOfferService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOfferService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: SalesOfferStore }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [SALES_OFFER_CONFIG]
                }] }]; } });

class SalesOfferItemService {
    constructor(http, store, config) {
        this.http = http;
        this.store = store;
        this.config = config;
    }
    get(lngOrderID) {
        return of(undefined).pipe(tap(() => this.store.setLoading(true)), switchMap(() => this.http.get(this.config.apiUrl + '/sales-offers/' + lngOrderID + '/items/')), map((res) => {
            return res.data.map((item) => {
                return { ...item, id: `${item.lngOrderID}-${item.shtFixedItemID}` };
            });
        }), tap((entities) => {
            this.store.upsertMany(entities);
        }), finalize(() => this.store.setLoading(false)));
    }
}
SalesOfferItemService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOfferItemService, deps: [{ token: i1.HttpClient }, { token: SalesOfferItemStore }, { token: SALES_OFFER_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
SalesOfferItemService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOfferItemService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOfferItemService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: SalesOfferItemStore }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [SALES_OFFER_CONFIG]
                }] }]; } });

class SalesOfferModule {
    static forRoot(config) {
        return {
            ngModule: SalesOfferModule,
            providers: [
                {
                    provide: SALES_OFFER_CONFIG,
                    useValue: config,
                },
            ],
        };
    }
}
SalesOfferModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOfferModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SalesOfferModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: SalesOfferModule, imports: [CommonModule] });
SalesOfferModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOfferModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOfferModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }] });

const SALES_OFFER_PAGINATOR = new InjectionToken('SalesOfferPaginator', {
    providedIn: 'root',
    factory: () => {
        return new Paginator(inject(SalesOfferQuery)).withControls().withRange();
    },
});

class SalesOfferFilterService extends AkitaFiltersPlugin {
    constructor(offerQuery) {
        super(offerQuery, { filtersStoreName: 'sales-offer-filter' });
        this.offerQuery = offerQuery;
        const fromDate = new Date();
        fromDate.setMonth(fromDate.getMonth() - 3);
        this.setFilter({
            id: 'dtEntryDate[gte]',
            value: fromDate.valueOf(),
            predicate: (entity) => {
                return entity.dtEntryDate >= fromDate.valueOf();
            },
        });
        this.setFilter({
            id: 'intPageIndex',
            value: 1,
            predicate: (_) => true,
        });
        this.setFilter({
            id: 'intPageSize',
            value: 10,
            predicate: (_) => true,
        });
    }
    /**
     * Sets orderBy
     */
    setOrderBy(by, order) {
        this.setSortBy({
            sortBy: by,
            sortByOrder: order === '+' ? Order.ASC : Order.DESC,
        });
    }
}
SalesOfferFilterService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOfferFilterService, deps: [{ token: SalesOfferQuery }], target: i0.ɵɵFactoryTarget.Injectable });
SalesOfferFilterService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOfferFilterService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesOfferFilterService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: SalesOfferQuery }]; } });

function createShippingAddress(lngCustomerID = 0, lngAddressID = 0, shtAddressType = 2, lngContactID = 0, sContact = '', lngRouteBaseID = 1, sCompany1 = '', sCompany2 = '', sCompany3 = '', sZipBox = '', sPostBox = '', sStreet = '', sZipCode = '', sCity = '', sCountry = '', sCountryCode = '', sMemo = '') {
    return {
        lngCustomerID,
        lngAddressID,
        shtAddressType,
        lngContactID,
        sContact,
        lngRouteBaseID,
        sCompany1,
        sCompany2,
        sCompany3,
        sZipBox,
        sPostBox,
        sStreet,
        sZipCode,
        sCity,
        sCountry,
        sCountryCode,
        sMemo,
    };
}

let ShippingAddressStore = class ShippingAddressStore extends EntityStore {
    constructor() {
        super();
    }
};
ShippingAddressStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ShippingAddressStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ShippingAddressStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ShippingAddressStore, providedIn: 'root' });
ShippingAddressStore = __decorate([
    StoreConfig({ name: 'shipping-addresses', idKey: 'lngAddressID', resettable: true })
], ShippingAddressStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ShippingAddressStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

let ShippingAddressQuery = class ShippingAddressQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
};
ShippingAddressQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ShippingAddressQuery, deps: [{ token: ShippingAddressStore }], target: i0.ɵɵFactoryTarget.Injectable });
ShippingAddressQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ShippingAddressQuery, providedIn: 'root' });
ShippingAddressQuery = __decorate([
    QueryConfig({ sortBy: 'sCompany1', sortByOrder: Order.ASC })
], ShippingAddressQuery);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ShippingAddressQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: ShippingAddressStore }]; } });

class ShippingAddressService extends NgEntityService {
    constructor(store) {
        super(store);
    }
    getSimple() {
        return this.getHttp()
            .get(this.getConfig().baseUrl + '/shipping-addresses')
            .pipe(tap((response) => {
            this.store.set(response.data);
        }));
    }
}
ShippingAddressService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ShippingAddressService, deps: [{ token: ShippingAddressStore }], target: i0.ɵɵFactoryTarget.Injectable });
ShippingAddressService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ShippingAddressService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ShippingAddressService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: ShippingAddressStore }]; } });

class ShippingAddressModule {
}
ShippingAddressModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ShippingAddressModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ShippingAddressModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: ShippingAddressModule, imports: [CommonModule] });
ShippingAddressModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ShippingAddressModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ShippingAddressModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }] });

let SteelFigureImageStore = class SteelFigureImageStore extends EntityStore {
    constructor() {
        super();
    }
};
SteelFigureImageStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelFigureImageStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SteelFigureImageStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelFigureImageStore, providedIn: 'root' });
SteelFigureImageStore = __decorate([
    StoreConfig({ name: 'steel-figure-image', idKey: 'sFigureID' })
], SteelFigureImageStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelFigureImageStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class SteelFigureImageQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
}
SteelFigureImageQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelFigureImageQuery, deps: [{ token: SteelFigureImageStore }], target: i0.ɵɵFactoryTarget.Injectable });
SteelFigureImageQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelFigureImageQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelFigureImageQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: SteelFigureImageStore }]; } });

let SteelConfigStore = class SteelConfigStore extends EntityStore {
    constructor() {
        super();
    }
};
SteelConfigStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelConfigStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SteelConfigStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelConfigStore, providedIn: 'root' });
SteelConfigStore = __decorate([
    StoreConfig({ name: 'steel-configuration', idKey: 'sArticleID' })
], SteelConfigStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelConfigStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class SteelConfigService {
    constructor(store, httpClient) {
        this.store = store;
        this.httpClient = httpClient;
    }
    createConfiguration(article) {
        const configurationParam = {
            sArticleID: article.sArticleID.toString(),
            decBO3Factor1: 0,
            decBO3Factor2: 0,
            decBO3Factor3: 0,
            decBO3QuantityInput: 1,
            sBO3QuantityUnitInput: article.oUnitColl[0]?.sQuantityUnitInput,
        };
        return this.httpClient.post('/p48apihost/steel-configuration', configurationParam);
    }
    updateConfiguration(steelConfig) {
        return this.httpClient.put('/p48apihost/steel-configuration', steelConfig);
    }
}
SteelConfigService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelConfigService, deps: [{ token: SteelConfigStore }, { token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
SteelConfigService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelConfigService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelConfigService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: SteelConfigStore }, { type: i1.HttpClient }]; } });

class SteelConfigurationQuery extends QueryEntity {
    constructor(store, steelService) {
        super(store);
        this.store = store;
        this.steelService = steelService;
    }
}
SteelConfigurationQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelConfigurationQuery, deps: [{ token: SteelConfigStore }, { token: SteelConfigService }], target: i0.ɵɵFactoryTarget.Injectable });
SteelConfigurationQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelConfigurationQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelConfigurationQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: SteelConfigStore }, { type: SteelConfigService }]; } });

class SteelCartService extends CartService {
    constructor(transloco, translocoLocale, cartHttpService, cartLocalService, cartGuestService, authService, cartHeaderStore, cartItemStore, cartItemQuery, cartHeaderQuery, http, userService, paramQuery) {
        super(transloco, translocoLocale, cartHttpService, cartLocalService, cartGuestService, authService, cartHeaderStore, cartItemStore, cartItemQuery, cartHeaderQuery, http, userService, paramQuery);
        this.transloco = transloco;
        this.translocoLocale = translocoLocale;
        this.cartHttpService = cartHttpService;
        this.cartLocalService = cartLocalService;
        this.cartGuestService = cartGuestService;
        this.authService = authService;
        this.cartHeaderStore = cartHeaderStore;
        this.cartItemStore = cartItemStore;
        this.cartItemQuery = cartItemQuery;
        this.cartHeaderQuery = cartHeaderQuery;
        this.http = http;
        this.userService = userService;
        this.paramQuery = paramQuery;
    }
}
SteelCartService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelCartService, deps: [{ token: i1$1.TranslocoService }, { token: i2$2.TranslocoLocaleService }, { token: CartHttpService }, { token: CartLocalService }, { token: CartGuestService }, { token: AuthService }, { token: CartHeaderStore }, { token: CartItemStore }, { token: CartItemQuery }, { token: CartHeaderQuery }, { token: i1.HttpClient }, { token: UserService }, { token: ParameterQuery }], target: i0.ɵɵFactoryTarget.Injectable });
SteelCartService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelCartService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelCartService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1$1.TranslocoService }, { type: i2$2.TranslocoLocaleService }, { type: CartHttpService }, { type: CartLocalService }, { type: CartGuestService }, { type: AuthService }, { type: CartHeaderStore }, { type: CartItemStore }, { type: CartItemQuery }, { type: CartHeaderQuery }, { type: i1.HttpClient }, { type: UserService }, { type: ParameterQuery }]; } });

/**
 * A service to manage steel article entities
 */
class SteelArticleService extends ArticleService {
    constructor(store, elasticSearch, catalogConfig) {
        super(store, elasticSearch, catalogConfig);
    }
}
SteelArticleService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelArticleService, deps: [{ token: ArticleStore }, { token: ElasticSearchService }, { token: CATALOG_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
SteelArticleService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelArticleService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelArticleService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: ArticleStore }, { type: ElasticSearchService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CATALOG_CONFIG]
                }] }]; } });

class SteelFigureImageService {
    constructor(steelFigureImageStore, http) {
        this.steelFigureImageStore = steelFigureImageStore;
        this.http = http;
    }
    get(sFigureID) {
        const encodedFigureImageID = encodeURIComponent(sFigureID);
        return this.http.get(`/p48apihost/steel-figure-image/${encodedFigureImageID}`, {}).pipe(tap((oFigureImage) => {
            this.steelFigureImageStore.upsert(sFigureID, oFigureImage);
        }));
    }
    getImagesByFigureList(figureList) {
        const imageObservables = figureList.map((figure) => this.get(figure.sFigureID));
        return forkJoin(imageObservables).pipe(map((images) => this.mapImageToFigure(images, figureList)));
    }
    mapImageToFigure(images, figureList) {
        return figureList.map((figure) => {
            return {
                ...figure,
                binTakePositionImage: images.find((image) => image.sFigureID === figure.sFigureID)?.binTakePositionImage,
            };
        });
    }
}
SteelFigureImageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelFigureImageService, deps: [{ token: SteelFigureImageStore }, { token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
SteelFigureImageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelFigureImageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelFigureImageService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: SteelFigureImageStore }, { type: i1.HttpClient }]; } });

/**
 * A factory function that creates SteelFavoriteItem
 */
function createSteelFavoriteItem({ gListID = '', decQuantity = 1, oArticle = undefined, shtItemID = 0, fakeId = '', sB03QuantityUnitInput = '', }) {
    return {
        ...createFavoriteItem({ gListID, decQuantity, oArticle, shtItemID, fakeId }),
        sB03QuantityUnitInput,
    };
}

class SteelModule {
}
SteelModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SteelModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: SteelModule, imports: [CommonModule] });
SteelModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelModule, providers: [
        { provide: ArticleService, useClass: SteelArticleService },
        {
            provide: CartService,
            useClass: SteelCartService,
        },
    ], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SteelModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    providers: [
                        { provide: ArticleService, useClass: SteelArticleService },
                        {
                            provide: CartService,
                            useClass: SteelCartService,
                        },
                    ],
                }]
        }] });

/**
 * A store to save article entities
 */
let CmsUiDynamicContentStore = class CmsUiDynamicContentStore extends EntityStore {
    /**
     * Creates an instance of article store.
     */
    constructor() {
        super({
            cmsMainMenu: undefined,
            homePage: undefined,
            errorPage: undefined,
            footer: undefined,
            blogPosts: undefined,
            blogSearch: undefined,
            pageSearch: undefined,
        });
    }
};
CmsUiDynamicContentStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CmsUiDynamicContentStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CmsUiDynamicContentStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CmsUiDynamicContentStore, providedIn: 'root' });
CmsUiDynamicContentStore = __decorate([
    StoreConfig({ name: 'cms-pages', resettable: true })
], CmsUiDynamicContentStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CmsUiDynamicContentStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

/**
 * Query object to query article entities
 */
class CmsUiDynamicContentQuery extends QueryEntity {
    /**
     * Creates an instance of article query.
     * @param store the article store
     */
    constructor(store) {
        super(store);
        this.store = store;
        this.cmsMainMenu$ = this.select('cmsMainMenu').pipe(filter((data) => !!data));
        this.homePage$ = this.select('homePage').pipe(filter((data) => !!data));
        this.errorPage$ = this.select('errorPage').pipe(filter((data) => !!data));
        this.footer$ = this.select('footer').pipe(filter((data) => !!data));
        this.blogPosts$ = this.select('blogPosts').pipe(filter((data) => !!data));
        this.blogSearch$ = this.select('blogSearch').pipe(filter((data) => !!data));
        this.pageSearch$ = this.select('pageSearch').pipe(filter((data) => !!data));
    }
    page(id) {
        return this.select(`/cms/pages/${id}`).pipe(filter((data) => !!data));
    }
    blog(id) {
        return this.select(`/cms/posts/${id}`).pipe(filter((data) => !!data));
    }
}
CmsUiDynamicContentQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CmsUiDynamicContentQuery, deps: [{ token: CmsUiDynamicContentStore }], target: i0.ɵɵFactoryTarget.Injectable });
CmsUiDynamicContentQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CmsUiDynamicContentQuery, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CmsUiDynamicContentQuery, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: CmsUiDynamicContentStore }]; } });

/** Creates a document module configuration injection token */
const STRAPI_CMS_CONFIG = new InjectionToken('StrapiCmsConfig');

/**
 * A service to manage article entities
 */
class CmsUiDynamicContentService {
    /**
     * Creates an instance of article service.
     */
    constructor(store, http, config) {
        this.store = store;
        this.http = http;
        this.config = config;
    }
    getCmsMainMenu() {
        return this.http
            .get(this.config.apiUrl + '/main-menu')
            .pipe(tap((response) => this.store.update({ cmsMainMenu: response })));
    }
    getHomePage() {
        return this.http.get(this.config.apiUrl + '/home').pipe(tap((response) => {
            this.store.update({ homePage: response });
        }));
    }
    getErrorPage() {
        return this.http.get(this.config.apiUrl + '/error').pipe(tap((response) => {
            this.store.update({ errorPage: response });
        }));
    }
    getCMSPage(id) {
        const selector = `/cms/pages/${id}`;
        return this.http.get(selector).pipe(tap((response) => {
            this.store.update({ [selector]: response });
        }));
    }
    searchCMSPages(term) {
        const params = new HttpParams({ encoder: new CustomHttpParameterCodec() }).set('_q', term);
        return this.http
            .get(this.config.apiUrl + '/pages', { params })
            .pipe(tap((response) => this.store.update({ pageSearch: response })));
    }
    getBlogPosts() {
        return this.http
            .get(this.config.apiUrl + '/posts')
            .pipe(tap((response) => this.store.update({ blogPosts: response })));
    }
    searchBlogPosts(term) {
        const params = new HttpParams({ encoder: new CustomHttpParameterCodec() }).set('_q', term);
        return this.http
            .get(this.config.apiUrl + '/posts', { params })
            .pipe(tap((response) => this.store.update({ blogSearch: response })));
    }
    resetSearchStore() {
        this.store.update({ blogSearch: [] });
        this.store.update({ pageSearch: [] });
    }
    getBlogPost(id) {
        const selector = `${this.config.apiUrl}/posts/${id}`;
        return this.http.get(selector).pipe(tap((response) => {
            this.store.update({ [selector]: response });
        }));
    }
    getFooter() {
        const selector = `${this.config.apiUrl}/footer`;
        return this.http.get(selector).pipe(tap((response) => {
            this.store.update({ footer: response });
        }));
    }
}
CmsUiDynamicContentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CmsUiDynamicContentService, deps: [{ token: CmsUiDynamicContentStore }, { token: i1.HttpClient }, { token: STRAPI_CMS_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
CmsUiDynamicContentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CmsUiDynamicContentService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CmsUiDynamicContentService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: CmsUiDynamicContentStore }, { type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [STRAPI_CMS_CONFIG]
                }] }]; } });

/**
 * A service to help with reoccurring tasks
 */
class CmsHelperService {
    flattenMenu(menu, level = 1) {
        const selector = `level_${level}`;
        const subMenu = menu[selector];
        if (!subMenu) {
            return [];
        }
        const currentLevelItems = subMenu;
        let nextLevelItems = [];
        for (const nextLevelMenu of subMenu) {
            nextLevelItems = [...nextLevelItems, ...this.flattenMenu(nextLevelMenu, level + 1)];
        }
        return [...currentLevelItems, ...nextLevelItems];
    }
}
CmsHelperService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CmsHelperService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CmsHelperService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CmsHelperService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CmsHelperService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class BlogBreadcrumbResolver extends BreadcrumbsResolver {
    constructor(cmsQuery, urlHelper) {
        super();
        this.cmsQuery = cmsQuery;
        this.urlHelper = urlHelper;
    }
    resolve() {
        return this.cmsQuery.blogPosts$.pipe(first(), map((blogPosts) => this.traverse(blogPosts)));
    }
    traverse(blogPosts) {
        if (!blogPosts) {
            return [];
        }
        return blogPosts
            .filter((blogPost) => this.pathMatches(blogPost))
            .map((blogPost) => this.blogPostToBreadcrumb(blogPost));
    }
    blogPostToBreadcrumb(item) {
        return { label: '', translate: false, url: '', path: this.urlHelper.localizeUrl(item.slug), text: item.title };
    }
    pathMatches(item) {
        return this.urlHelper.getPath().substring(3) === item.slug;
    }
}
BlogBreadcrumbResolver.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BlogBreadcrumbResolver, deps: [{ token: CmsUiDynamicContentQuery }, { token: UrlHelperService }], target: i0.ɵɵFactoryTarget.Injectable });
BlogBreadcrumbResolver.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BlogBreadcrumbResolver, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BlogBreadcrumbResolver, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: CmsUiDynamicContentQuery }, { type: UrlHelperService }]; } });

class PageBreadcrumbResolver extends BreadcrumbsResolver {
    constructor(cmsQuery, urlHelper) {
        super();
        this.cmsQuery = cmsQuery;
        this.urlHelper = urlHelper;
    }
    resolve() {
        return this.cmsQuery.cmsMainMenu$.pipe(first(), map((menu) => this.traverse(menu)));
    }
    traverse(menu, level = 1) {
        const selector = `level_${level}`;
        let matches = [];
        if (!menu[selector]) {
            return matches;
        }
        const currentLevelItems = menu[selector];
        for (const item of currentLevelItems) {
            if (this.pathMatches(item)) {
                return [this.menuItemToBreadcrumb(item)];
            }
            const nextLevelMatches = this.traverse(item, level + 1);
            if (nextLevelMatches.length) {
                matches = [...matches, this.menuItemToBreadcrumb(item), ...nextLevelMatches];
            }
        }
        return matches;
    }
    menuItemToBreadcrumb(item) {
        return { label: '', translate: false, url: '', path: this.urlHelper.localizeUrl(item.slug), text: item.title };
    }
    pathMatches(item) {
        return this.urlHelper.getPath().substring(3) === item.slug;
    }
}
PageBreadcrumbResolver.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PageBreadcrumbResolver, deps: [{ token: CmsUiDynamicContentQuery }, { token: UrlHelperService }], target: i0.ɵɵFactoryTarget.Injectable });
PageBreadcrumbResolver.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PageBreadcrumbResolver, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PageBreadcrumbResolver, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: CmsUiDynamicContentQuery }, { type: UrlHelperService }]; } });

/**
 * Intercepts all cms requests and appends the current locale
 */
class LanguageInterceptor {
    constructor(translocoService) {
        this.translocoService = translocoService;
    }
    intercept(req, next) {
        // Only intercept cms urls
        if (!req.url.match(/^\/cms\//i)) {
            return next.handle(req);
        }
        if (!!req.headers.get('ignoreLocale')) {
            return next.handle(req);
        }
        return next.handle(req.clone({ params: req.params.append('_locale', this.translocoService.getActiveLang()) }));
    }
}
LanguageInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LanguageInterceptor, deps: [{ token: i1$1.TranslocoService }], target: i0.ɵɵFactoryTarget.Injectable });
LanguageInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LanguageInterceptor, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LanguageInterceptor, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1$1.TranslocoService }]; } });

class PagePathPipe {
    constructor(urlHelper, cmsQuery, cmsHelper) {
        this.urlHelper = urlHelper;
        this.cmsQuery = cmsQuery;
        this.cmsHelper = cmsHelper;
    }
    transform(page) {
        return this.cmsQuery.cmsMainMenu$.pipe(map((menu) => this.cmsHelper.flattenMenu(menu)), map((flatMenu) => flatMenu.find((item) => item.page?.id === page.id)), filter((item) => !!item), map((item) => this.urlHelper.localizeUrl(item.slug)));
    }
}
PagePathPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PagePathPipe, deps: [{ token: UrlHelperService }, { token: CmsUiDynamicContentQuery }, { token: CmsHelperService }], target: i0.ɵɵFactoryTarget.Pipe });
PagePathPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: PagePathPipe, name: "pagePath" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PagePathPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'pagePath',
                }]
        }], ctorParameters: function () { return [{ type: UrlHelperService }, { type: CmsUiDynamicContentQuery }, { type: CmsHelperService }]; } });

class StrapiCmsModule {
    static forRoot(config) {
        return {
            ngModule: StrapiCmsModule,
            providers: [
                {
                    provide: STRAPI_CMS_CONFIG,
                    useValue: config,
                },
            ],
        };
    }
}
StrapiCmsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: StrapiCmsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
StrapiCmsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: StrapiCmsModule, declarations: [PagePathPipe], imports: [CommonModule], exports: [PagePathPipe] });
StrapiCmsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: StrapiCmsModule, providers: [{ provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true }], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: StrapiCmsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    providers: [{ provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true }],
                    declarations: [PagePathPipe],
                    exports: [PagePathPipe],
                }]
        }] });

let TicketStore = class TicketStore extends EntityStore {
    constructor() {
        super();
    }
};
TicketStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
TicketStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketStore, providedIn: 'root' });
TicketStore = __decorate([
    StoreConfig({ name: 'tickets', idKey: 'lngTicketID', resettable: true })
], TicketStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

let TicketQuery = class TicketQuery extends QueryEntity {
    /** Creates a new instance of ticket query */
    constructor(store) {
        super(store);
        this.store = store;
    }
    /**
     * Selects open tickets
     */
    selectOpenTickets() {
        return this.selectAll({
            filterBy: (entity) => entity.lngStatus === 1,
            limitTo: 5,
        });
    }
    /**
     * Selects all tickets and filters them by status
     */
    selectTicketsByStatus(status) {
        return this.selectAll({
            filterBy: (entity) => entity.lngStatus === status,
        });
    }
    /**
     * Selects all tickets and filters them by status, contact, responsible
     */
    selectTicketsFiltered(status = 0, contactID = 0, responsible = '') {
        let tickets;
        if (status !== 0) {
            tickets = this.selectAll({
                filterBy: (entity) => entity.lngStatus === status,
            });
        }
        else {
            tickets = this.selectAll();
        }
        if (contactID !== 0) {
            tickets = tickets.pipe(map((items) => items.filter((item) => item.lngContactID === contactID)));
        }
        if (responsible !== '') {
            tickets = tickets.pipe(map((items) => items.filter((item) => item.sB22Responsible === responsible)));
        }
        return tickets;
    }
};
TicketQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketQuery, deps: [{ token: TicketStore }], target: i0.ɵɵFactoryTarget.Injectable });
TicketQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketQuery, providedIn: 'root' });
TicketQuery = __decorate([
    QueryConfig({ sortBy: 'dtAlterationDate', sortByOrder: Order.DESC })
], TicketQuery);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: TicketStore }]; } });

let TicketTypeStore = class TicketTypeStore extends EntityStore {
    constructor() {
        super();
    }
};
TicketTypeStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketTypeStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
TicketTypeStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketTypeStore, providedIn: 'root' });
TicketTypeStore = __decorate([
    StoreConfig({ name: 'ticket-types', idKey: 'lngTypeID' })
], TicketTypeStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketTypeStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

let TicketTypeQuery = class TicketTypeQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
};
TicketTypeQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketTypeQuery, deps: [{ token: TicketTypeStore }], target: i0.ɵɵFactoryTarget.Injectable });
TicketTypeQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketTypeQuery, providedIn: 'root' });
TicketTypeQuery = __decorate([
    QueryConfig({
        sortBy: 'id',
        sortByOrder: Order.ASC,
    })
], TicketTypeQuery);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketTypeQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: TicketTypeStore }]; } });

let TicketMessageStore = class TicketMessageStore extends EntityStore {
    constructor() {
        super();
    }
};
TicketMessageStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketMessageStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
TicketMessageStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketMessageStore, providedIn: 'root' });
TicketMessageStore = __decorate([
    StoreConfig({ name: 'ticket-messages', idKey: 'id' })
], TicketMessageStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketMessageStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

let TicketMessageQuery = class TicketMessageQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
    selectByTicketId(ticketId) {
        return this.selectAll({
            filterBy: (ticket) => ticket.lngTicketId === ticketId,
        });
    }
};
TicketMessageQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketMessageQuery, deps: [{ token: TicketMessageStore }], target: i0.ɵɵFactoryTarget.Injectable });
TicketMessageQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketMessageQuery, providedIn: 'root' });
TicketMessageQuery = __decorate([
    QueryConfig({
        sortBy: 'dtEntryDate',
        sortByOrder: Order.ASC, // Order.DESC
    })
], TicketMessageQuery);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketMessageQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: TicketMessageStore }]; } });

let TicketCategoryStore = class TicketCategoryStore extends EntityStore {
    constructor() {
        super();
    }
};
TicketCategoryStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketCategoryStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
TicketCategoryStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketCategoryStore, providedIn: 'root' });
TicketCategoryStore = __decorate([
    StoreConfig({ name: 'ticket-categories', idKey: 'lngCategoryID' })
], TicketCategoryStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketCategoryStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

let TicketCategoryQuery = class TicketCategoryQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
};
TicketCategoryQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketCategoryQuery, deps: [{ token: TicketCategoryStore }], target: i0.ɵɵFactoryTarget.Injectable });
TicketCategoryQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketCategoryQuery, providedIn: 'root' });
TicketCategoryQuery = __decorate([
    QueryConfig({ sortBy: 'id', sortByOrder: Order.ASC })
], TicketCategoryQuery);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketCategoryQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: TicketCategoryStore }]; } });

let TicketPriorityStore = class TicketPriorityStore extends EntityStore {
    constructor() {
        super();
    }
};
TicketPriorityStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketPriorityStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
TicketPriorityStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketPriorityStore, providedIn: 'root' });
TicketPriorityStore = __decorate([
    StoreConfig({ name: 'ticket-priorities', idKey: 'lngPriorityID' })
], TicketPriorityStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketPriorityStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

let TicketPriorityQuery = class TicketPriorityQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
};
TicketPriorityQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketPriorityQuery, deps: [{ token: TicketPriorityStore }], target: i0.ɵɵFactoryTarget.Injectable });
TicketPriorityQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketPriorityQuery, providedIn: 'root' });
TicketPriorityQuery = __decorate([
    QueryConfig({
        sortBy: 'id',
        sortByOrder: Order.ASC,
    })
], TicketPriorityQuery);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketPriorityQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: TicketPriorityStore }]; } });

let TicketStatisticStore = class TicketStatisticStore extends EntityStore {
    constructor() {
        super();
    }
};
TicketStatisticStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketStatisticStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
TicketStatisticStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketStatisticStore, providedIn: 'root' });
TicketStatisticStore = __decorate([
    StoreConfig({ name: 'ticket-statistic', idKey: 'sYear' })
], TicketStatisticStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketStatisticStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

let TicketStatisticQuery = class TicketStatisticQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
};
TicketStatisticQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketStatisticQuery, deps: [{ token: TicketStatisticStore }], target: i0.ɵɵFactoryTarget.Injectable });
TicketStatisticQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketStatisticQuery, providedIn: 'root' });
TicketStatisticQuery = __decorate([
    QueryConfig({
        sortBy: 'id',
        sortByOrder: Order.ASC,
    })
], TicketStatisticQuery);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketStatisticQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: TicketStatisticStore }]; } });

/** Creates a new ticket */
function createTicket({ sB22Description = '', lngStatus = 1, lngCategory = 0, lngCallType = 0, sEntryUser = '', dtEntryDate = new Date().getTime(), dtAlterationDate = new Date().getTime(), sAlterationUser = '', sB22Responsible = '', lngPriority = 1, lngContactID = 0, sB22Reference = '', }) {
    return {
        sB22Description,
        lngStatus,
        lngCategory,
        lngCallType,
        sEntryUser,
        dtEntryDate,
        dtAlterationDate,
        sAlterationUser,
        sB22Responsible,
        lngPriority,
        lngContactID,
        sB22Reference,
    };
}

function createTicketMessage({ sTextType = '', sText = '', sClerkID = '', dtEntryDate = new Date().getTime(), lngTicketId = 0, sAlterationUser = '', }) {
    return {
        sTextType,
        sText,
        sClerkID,
        dtEntryDate,
        lngTicketId,
        sAlterationUser,
    };
}

class TicketService extends NgEntityService {
    constructor(store, ticketQuery) {
        super(store);
        this.store = store;
        this.ticketQuery = ticketQuery;
    }
}
TicketService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketService, deps: [{ token: TicketStore }, { token: TicketQuery }], target: i0.ɵɵFactoryTarget.Injectable });
TicketService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: TicketStore }, { type: TicketQuery }]; } });

class TicketStatisticService {
    constructor(store, http) {
        this.store = store;
        this.http = http;
    }
    get() {
        return this.http.get('/p48apihost/ticket-stats').pipe(tap((stats) => {
            this.store.set(stats);
        }));
    }
}
TicketStatisticService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketStatisticService, deps: [{ token: TicketStatisticStore }, { token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
TicketStatisticService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketStatisticService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketStatisticService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: TicketStatisticStore }, { type: i1.HttpClient }]; } });

class TicketFilterService extends AkitaFiltersPlugin {
    constructor(store, ticketQuery, ticketService) {
        super(ticketQuery);
        this.store = store;
        this.ticketQuery = ticketQuery;
        this.ticketService = ticketService;
        this._total$ = new BehaviorSubject(0);
        this.defaultFilters = {
            'dtEntryDate[gte]': new Date().setMonth(new Date().getMonth() - 3).valueOf(),
            'dtEntryDate[lte]': new Date().setDate(new Date().getDate() + 1).valueOf(),
            intMaxRows: 99999,
        };
        this.withServer(this.getOnChangeFilter());
    }
    get total$() {
        return this._total$.asObservable();
    }
    /**
     * this functions is called if the filters from akita-filter-plugin are changed
     * be aware that you have to set the server = true in the filter definition to trigger this function
     * as it only makes sence to load tickets with a date filter it is not possible to remove those
     */
    getOnChangeFilter() {
        return (filtersNormalized) => {
            return this.ticketService.get({
                params: Object.assign(this.defaultFilters, filtersNormalized),
                mapResponseFn: (res) => {
                    this._total$.next(Number(res.body.total));
                    if (res.body.data) {
                        return res.body.data;
                    }
                    return res.body;
                },
                observe: 'response', // <----- this
            });
        };
    }
    /**
     * Sets orderBy
     * @param by order by key
     * @param order order direction ('+' or '-')
     */
    setOrderBy(by, order) {
        this.setSortBy({
            sortBy: by,
            sortByOrder: order === '+' ? Order.ASC : Order.DESC,
        });
    }
    selectAllByFilters(options) {
        return super.selectAllByFilters(options);
    }
}
TicketFilterService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketFilterService, deps: [{ token: TicketStore }, { token: TicketQuery }, { token: TicketService }], target: i0.ɵɵFactoryTarget.Injectable });
TicketFilterService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketFilterService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketFilterService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: TicketStore }, { type: TicketQuery }, { type: TicketService }]; } });

class TicketPriorityService {
    constructor(store, http) {
        this.store = store;
        this.http = http;
    }
    get() {
        return this.http.get('/p48apihost/ticket-priorities').pipe(tap((priorities) => this.store.upsertMany(priorities)));
    }
}
TicketPriorityService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketPriorityService, deps: [{ token: TicketPriorityStore }, { token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
TicketPriorityService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketPriorityService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketPriorityService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: TicketPriorityStore }, { type: i1.HttpClient }]; } });

class TicketMessageService extends NgEntityService {
    constructor(store) {
        super(store);
        this.store = store;
    }
    get(ticketId) {
        return this.getHttp()
            .get(this.getConfig().baseUrl + '/tickets/' + ticketId + '/messages/')
            .pipe(map((res) => res.data), tap((entities) => {
            this.store.set(entities);
        }));
    }
    // TODO refactor url should be set in this method -> override add method (send is confusing)
    send(url, entity) {
        const fullUrl = this.getConfig().baseUrl + url;
        return super.add(entity, { url: fullUrl });
    }
}
TicketMessageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketMessageService, deps: [{ token: TicketMessageStore }], target: i0.ɵɵFactoryTarget.Injectable });
TicketMessageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketMessageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketMessageService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: TicketMessageStore }]; } });

class TicketTypeService extends NgEntityService {
    constructor(store) {
        super(store);
        this.store = store;
    }
}
TicketTypeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketTypeService, deps: [{ token: TicketTypeStore }], target: i0.ɵɵFactoryTarget.Injectable });
TicketTypeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketTypeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketTypeService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: TicketTypeStore }]; } });

class TicketCategoryService extends NgEntityService {
    constructor(store) {
        super(store);
        this.store = store;
    }
}
TicketCategoryService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketCategoryService, deps: [{ token: TicketCategoryStore }], target: i0.ɵɵFactoryTarget.Injectable });
TicketCategoryService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketCategoryService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketCategoryService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: TicketCategoryStore }]; } });

/**
 * A pipe that transforms a category id in a category name.
 */
class CategoryNamePipe {
    constructor(ticketCategoryQuery) {
        this.ticketCategoryQuery = ticketCategoryQuery;
    }
    /**
     * Transforms a category id in a category name
     */
    transform(value) {
        return this.ticketCategoryQuery.selectAll().pipe(map((categories) => {
            let name;
            if (value) {
                name = categories.find((category) => category.lngCategoryID === value)?.sName;
            }
            return name ? name : '';
        }));
    }
}
CategoryNamePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CategoryNamePipe, deps: [{ token: TicketCategoryQuery }], target: i0.ɵɵFactoryTarget.Pipe });
CategoryNamePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: CategoryNamePipe, name: "categoryName" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CategoryNamePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'categoryName' }]
        }], ctorParameters: function () { return [{ type: TicketCategoryQuery }]; } });

/**
 * A pipe that transforms a category id in a category name.
 */
class TypeNamePipe {
    constructor(ticketTypeQuery) {
        this.ticketTypeQuery = ticketTypeQuery;
    }
    /**
     * Transforms a type id in a type name
     */
    transform(value) {
        return this.ticketTypeQuery.selectAll().pipe(map((ticketTypes) => {
            let name;
            if (value) {
                name = ticketTypes.find((type) => type.lngTypeID === value)?.sName;
            }
            return name ? name : '';
        }));
    }
}
TypeNamePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TypeNamePipe, deps: [{ token: TicketTypeQuery }], target: i0.ɵɵFactoryTarget.Pipe });
TypeNamePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: TypeNamePipe, name: "typeName" });
TypeNamePipe.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TypeNamePipe });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TypeNamePipe, decorators: [{
            type: Injectable
        }, {
            type: Pipe,
            args: [{
                    name: 'typeName',
                }]
        }], ctorParameters: function () { return [{ type: TicketTypeQuery }]; } });

class TicketModule {
}
TicketModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
TicketModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: TicketModule, declarations: [CategoryNamePipe, TypeNamePipe], imports: [CommonModule], exports: [CategoryNamePipe, TypeNamePipe] });
TicketModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: TicketModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [CategoryNamePipe, TypeNamePipe],
                    exports: [CategoryNamePipe, TypeNamePipe],
                }]
        }] });

let SalesRepCustomerStore = class SalesRepCustomerStore extends EntityStore {
    constructor() {
        super();
    }
};
SalesRepCustomerStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesRepCustomerStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SalesRepCustomerStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesRepCustomerStore, providedIn: 'root' });
SalesRepCustomerStore = __decorate([
    StoreConfig({ name: 'sales-rep-customers', idKey: 'lngCustomerID' })
], SalesRepCustomerStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesRepCustomerStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

let SalesRepCustomerQuery = class SalesRepCustomerQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
};
SalesRepCustomerQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesRepCustomerQuery, deps: [{ token: SalesRepCustomerStore }], target: i0.ɵɵFactoryTarget.Injectable });
SalesRepCustomerQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesRepCustomerQuery, providedIn: 'root' });
SalesRepCustomerQuery = __decorate([
    QueryConfig({ sortBy: 'sCompany1', sortByOrder: Order.ASC })
], SalesRepCustomerQuery);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesRepCustomerQuery, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: SalesRepCustomerStore }]; } });

class SalesRepService {
    constructor(store, http, userService, authService) {
        this.store = store;
        this.http = http;
        this.userService = userService;
        this.authService = authService;
    }
    getSalesRepCustomers() {
        return this.http.get(`p48apihost/sales-rep/customers`).pipe(tap((customers) => this.store.upsertMany(customers)));
    }
    changeSalesRepCustomer(lngCustomerID) {
        return this.http.put('p48apihost/sales-rep/' + lngCustomerID, {}).pipe(filter((data) => !!data), switchTap((user) => this.authService.updateAuthUser(user)), tap((user) => this.authService.changeAuthUser(user)));
    }
    resetSalesRepCustomer() {
        return this.http.put('p48apihost/sales-rep/reset', {}).pipe(filter((data) => !!data), switchTap((user) => this.authService.updateAuthUser(user)), tap((user) => this.authService.changeAuthUser(user)));
    }
}
SalesRepService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesRepService, deps: [{ token: SalesRepCustomerStore }, { token: i1.HttpClient }, { token: UserService }, { token: AuthService }], target: i0.ɵɵFactoryTarget.Injectable });
SalesRepService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesRepService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesRepService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: SalesRepCustomerStore }, { type: i1.HttpClient }, { type: UserService }, { type: AuthService }]; } });

class SalesRepModule {
}
SalesRepModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesRepModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SalesRepModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: SalesRepModule, imports: [CommonModule] });
SalesRepModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesRepModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: SalesRepModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }] });

const SCANNER_CONFIG = new InjectionToken('ScannerConfig');

class ScannerModule {
    static forRoot(config) {
        return {
            ngModule: ScannerModule,
            providers: [
                config.adapter,
                {
                    provide: SCANNER_CONFIG,
                    useValue: config,
                },
            ],
        };
    }
}
ScannerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ScannerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ScannerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: ScannerModule, imports: [CommonModule] });
ScannerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ScannerModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ScannerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }] });

class ScannerAdapterAbstract {
}
ScannerAdapterAbstract.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ScannerAdapterAbstract, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ScannerAdapterAbstract.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ScannerAdapterAbstract });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ScannerAdapterAbstract, decorators: [{
            type: Injectable
        }] });

const BACKLOG_CONFIG = new InjectionToken('BacklogConfig');

class BacklogModule {
    static forRoot(config) {
        return {
            ngModule: BacklogModule,
            providers: [
                {
                    provide: BACKLOG_CONFIG,
                    useValue: config,
                },
            ],
        };
    }
}
BacklogModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BacklogModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
BacklogModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: BacklogModule, imports: [CommonModule] });
BacklogModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BacklogModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BacklogModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }] });

class BacklogService {
    constructor(http, backlogConfig) {
        this.http = http;
        this.backlogConfig = backlogConfig;
    }
    getAllBacklogItems(urlOrParams) {
        return queryPaginated(this.http, this.backlogConfig.apiUrl + '/backlog', urlOrParams).pipe(filter((res) => res?.data));
    }
}
BacklogService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BacklogService, deps: [{ token: i1.HttpClient }, { token: BACKLOG_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
BacklogService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BacklogService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: BacklogService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [BACKLOG_CONFIG]
                }] }]; } });

const CRUD_CONFIG = new InjectionToken('CrudConfig');

class CrudService {
    constructor(http, crudConfig) {
        this.http = http;
        this.crudConfig = crudConfig;
    }
    getAll(path, params) {
        const httpParams = new HttpParams({ fromObject: params });
        return this.http.get(this.crudConfig.apiUrl + path, { params: httpParams }).pipe(map((x) => x.data));
    }
    getAllPaginated(path, params) {
        const httpParams = new HttpParams({ fromObject: params });
        return this.http.get(this.crudConfig.apiUrl + path, { params: httpParams });
    }
    getById(path, id) {
        return this.http.get(this.crudConfig.apiUrl + path + '/' + id);
    }
    create(path, item) {
        return this.http.post(this.crudConfig.apiUrl + path, item);
    }
    update(path, item, id) {
        return this.http.put(this.crudConfig.apiUrl + path + '/' + id, item);
    }
    delete(path, id) {
        return this.http.delete(this.crudConfig.apiUrl + path + '/' + id);
    }
}
CrudService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CrudService, deps: [{ token: i1.HttpClient }, { token: CRUD_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
CrudService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CrudService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CrudService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CRUD_CONFIG]
                }] }]; } });

class CrudModule {
    static forRoot(config) {
        return {
            ngModule: CrudModule,
            providers: [
                {
                    provide: CRUD_CONFIG,
                    useValue: config,
                },
            ],
        };
    }
}
CrudModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CrudModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CrudModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: CrudModule, imports: [CommonModule] });
CrudModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CrudModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: CrudModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }] });

const FACTORY_ATTESTATION_CONFIG = new InjectionToken('FactoryAttestationConfig');

class FactoryAttestationService {
    constructor(http, factoryAttestationConfig) {
        this.http = http;
        this.factoryAttestationConfig = factoryAttestationConfig;
    }
    searchFactoryAttestations(searchText) {
        return this.http.get(this.factoryAttestationConfig.apiUrl + '/factory-attestation', { params: { searchtext: searchText } });
    }
    downloadFactoryAttestationDocument(oFactoryAttestation) {
        return this.http
            .post(this.factoryAttestationConfig.apiUrl + '/factory-attestation', {
            sReference: oFactoryAttestation.sReference,
            sReferenceDetail: oFactoryAttestation.sReferenceDetail,
        }, { responseType: 'blob', observe: 'response' })
            .pipe(take(1), tap((res) => {
            const contentType = res.headers.get('content-type') || '';
            this.saveFileAs(res.body, `${oFactoryAttestation.sReference}_${oFactoryAttestation.sReferenceDetail}.${getExtension(contentType)}`, contentType);
        }));
    }
    saveFileAs(file, fileName, type) {
        const blob = new Blob([file], { type });
        saveAs(blob, fileName);
    }
}
FactoryAttestationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FactoryAttestationService, deps: [{ token: i1.HttpClient }, { token: FACTORY_ATTESTATION_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
FactoryAttestationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FactoryAttestationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FactoryAttestationService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [FACTORY_ATTESTATION_CONFIG]
                }] }]; } });

class FactoryAttestationModule {
    static forRoot(config) {
        return {
            ngModule: FactoryAttestationModule,
            providers: [
                {
                    provide: FACTORY_ATTESTATION_CONFIG,
                    useValue: config,
                },
            ],
        };
    }
}
FactoryAttestationModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FactoryAttestationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FactoryAttestationModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: FactoryAttestationModule, imports: [CommonModule, TranslocoModule] });
FactoryAttestationModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FactoryAttestationModule, imports: [CommonModule, TranslocoModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: FactoryAttestationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, TranslocoModule],
                }]
        }] });

class PriceListModule {
}
PriceListModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PriceListModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
PriceListModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: PriceListModule, imports: [CommonModule] });
PriceListModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PriceListModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PriceListModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }] });

class PriceListService {
    constructor(http) {
        this.http = http;
    }
    post(ecommGroups) {
        return this.http.post('p48apihost/pricelist', { ecommgroups: ecommGroups });
    }
}
PriceListService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PriceListService, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
PriceListService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PriceListService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PriceListService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }]; } });

/**
 * Generated bundle index. Do not edit.
 */

export { A11yModule, ACCESS_ID, ARCHIVE_DOCUMENT_CONFIG, AUTH_CONFIG, AccessService, AnonymousGuard, AppHookModule, ArchiveDocumentModule, ArchiveDocumentQuery, ArchiveDocumentService, ArchiveDocumentStore, ArticleAccessoriesQuery, ArticleAccessoriesService, ArticleAccessoriesStore, ArticleFacetedSearchQuery, ArticleFacetedSearchService, ArticleFacetedSearchStore, ArticleFactory, ArticleQuery, ArticleService, ArticleStore, AuthHookModule, AuthHookTriggerService, AuthHttpService, AuthModule, AuthService, AuthStateEnum, AuthStorageService, AuthorizedGuard, BACKLOG_CONFIG, BacklogModule, BacklogService, BaseInterceptor, BearerTokenAuthInterceptor, BlogBreadcrumbResolver, BreadcrumbsResolver, BreadcrumbsService, BreakpointService, CART_CONFIG, CATALOG_CONFIG, CLOUDINARY_CONFIG, CORE_CONFIG, CRUD_CONFIG, CRUD_METHOD, CartGuestService, CartHeaderLocalQuery, CartHeaderLocalStore, CartHeaderQuery, CartHeaderStore, CartHttpService, CartItemLocalQuery, CartItemLocalStore, CartItemQuery, CartItemStore, CartLocalService, CartModule, CartService, CatalogModule, CatalogQuery, CatalogService, CatalogStore, Catch, CategoryFacetedSearchQuery, CategoryFacetedSearchService, CategoryFacetedSearchStore, CategoryNamePipe, CategoryPageViewFilterService, CategoryQuery, CategoryService, CategoryStore, CloudinaryImagePathPipe, CloudinaryModule, CmsHelperService, CmsUiDynamicContentQuery, CmsUiDynamicContentService, CmsUiDynamicContentStore, CompareService, ConsentService, ContactModule, ContactQuery, ContactService, ContactStore, CoreModule, CountryModule, CountryQuery, CountryService, CountryStore, CrudModule, CrudService, CustomHttpParameterCodec, CustomerModule, CustomerQuery, CustomerService, CustomerStore, DOWNLOAD_CONFIG, DateHelperService, DebounceClickDirective, DebounceClickModule, DownloadModule, ELASTICSEARCH_CONFIG, ERROR_CONFIG, ElasticSearchService, ElasticsearchModule, ErrorHandlerService, ErrorHookModule, ErrorHookTriggerService, ErrorModule, ErrorService, FACTORY_ATTESTATION_CONFIG, FAVORITE_CONFIG, FacetedSearchResultQuery, FacetedSearchResultService, FacetedSearchResultStore, FacetedSpecificFeatureResultService, FactoryAttestationModule, FactoryAttestationService, FavoriteHttpService, FavoriteItemLocalQuery, FavoriteItemLocalStore, FavoriteItemQuery, FavoriteItemStore, FavoriteListQuery, FavoriteListStore, FavoriteLocalService, FavoriteModule, FavoriteService, FileTreeQuery, FileTreeService, FileTreeStore, FilterQuery, FilterService, FilterStore, FocusListDirective, FocusableDirective, GOOGLE_TAG_MANAGER_CONFIG, GoogleTagManagerAdapter, GoogleTagManagerAdapterModule, GoogleTagManagerHelper, GoogleTagManagerLoaderService, HasAccessGuard, HasAccessPipe, Hook, HookHandlerAbstract, HookHandlerFactoryService, HookPriority, HookReturnType, ImagePathHelperService, InjectorClass, LanguageHookModule, LanguageHookTriggerService, LanguageInterceptor, LanguageModule, LanguageNamePipePipe, LanguageQuery, LanguageService, LanguageStore, LaunchedHookTriggerService, LinkTagService, LocalSearchStringQuery, LocalSearchStringService, LocalSearchStringStore, LocalizeUrlPathAsyncPipe, LocalizeUrlPathModule, LocalizeUrlPathPipe, LocalstorageService, LogService, MemoryStorageService, PageBreadcrumbResolver, PageMeta, PageMetaModule, PageMetaService, PagePathPipe, PageViewFilterAbstract, ParameterModule, ParameterQuery, ParameterService, ParameterStore, PriceListModule, PriceListService, PriceQuery, PriceService, PriceStore, ProductPageViewFilterService, ProductQuery, ProductService, ProductStore, ProjectModule, ProjectQuery, ProjectService, ProjectStore, RedirectLanguageGuard, RequestDebounceQuery, RequestDebounceService, RequestDebounceStore, RoleNamePipePipe, RoleQuery, RoleService, RoleStore, SALES_CREDIT_NOTE_CONFIG, SALES_CREDIT_NOTE_PAGINATOR, SALES_DOCUMENT_CONFIG, SALES_OFFER_CONFIG, SALES_OFFER_PAGINATOR, SALES_ORDER_CONFIG, SALES_ORDER_PAGINATOR, SCANNER_CONFIG, SENTRY_CONFIG, STRAPI_CMS_CONFIG, SalesCreditNoteFilterService, SalesCreditNoteItemQuery, SalesCreditNoteItemService, SalesCreditNoteItemStore, SalesCreditNoteModule, SalesCreditNoteQuery, SalesCreditNoteService, SalesCreditNoteStore, SalesDocumentFilterService, SalesDocumentModule, SalesDocumentQuery, SalesDocumentService, SalesDocumentStore, SalesOfferFilterService, SalesOfferItemQuery, SalesOfferItemService, SalesOfferItemStore, SalesOfferModule, SalesOfferQuery, SalesOfferService, SalesOfferStore, SalesOrderFilterService, SalesOrderItemQuery, SalesOrderItemService, SalesOrderItemStore, SalesOrderModule, SalesOrderQuery, SalesOrderService, SalesOrderStore, SalesRepCustomerQuery, SalesRepCustomerStore, SalesRepModule, SalesRepService, ScannerAdapterAbstract, ScannerModule, SearchPageViewFilterService, SearchQuery, SearchService, SearchStore, SentryModule, SentryService, ShippingAddressModule, ShippingAddressQuery, ShippingAddressService, ShippingAddressStore, ShippingConditionPipe, ShippingTypeQuery, ShippingTypeService, ShippingTypeStore, SimpleStorageService, SteelArticleService, SteelCartService, SteelConfigService, SteelConfigStore, SteelConfigurationQuery, SteelFigureImageQuery, SteelFigureImageService, SteelFigureImageStore, SteelModule, StockAvailabilityQuery, StockAvailabilityService, StockAvailabilityStore, StorageService, StrapiCmsModule, StringHelper, TicketCategoryQuery, TicketCategoryService, TicketCategoryStore, TicketFilterService, TicketMessageQuery, TicketMessageService, TicketMessageStore, TicketModule, TicketPriorityQuery, TicketPriorityService, TicketPriorityStore, TicketQuery, TicketService, TicketStatisticQuery, TicketStatisticService, TicketStatisticStore, TicketStore, TicketTypeQuery, TicketTypeService, TicketTypeStore, TrackClass, TrackMethod, TrackPageView, TrackingFactory, TypeNamePipe, UrlEncodingModule, UrlEncodingPipe, UrlHelperService, UrlPathHelperService, UserFilterService, UserModule, UserQuery, UserService, UserStore, articleFiltersToSearchQuery, buildArticleAccessoriesQuery, buildArticleFacetedMultiSearchQuery, buildCatalgQueryByGroupId, buildCatalogByLevelQuery, buildCatalogQuery, buildCategoryFacetedMultiSearchQuery, buildCategorySearchQuery, buildFacetedBrandMultiSearchQuery, buildFacetedMultiSearchQuery, buildMultiSearchQuery, buildSearchQuery, categoryFiltersToSearchQuery, caughtError, createArchiveDocument, createCartHeader, createCartItem, createFavoriteItem, createFavoriteList, createLocalSearchString, createProject, createShippingAddress, createSteelFavoriteItem, createTicket, createTicketMessage, createUser, defaultCatalogConfig, defaultCoreConfig, defaultElasticsearchConfig, defaultErrorConfig, defaultPageViewConfig, filtersToSearchQuery, init_remote_params, parseHits, queryPaginated, switchTap, tapOnce };
